import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ClipboardList, Inbox, Wrench, CheckCircle, Search, Pencil, Trash2,
  Users, Plus, Coins, Camera, Image, Smartphone, Printer, Gift, Truck, BookOpen,
  BarChart3, Lightbulb, CircleDot, ScrollText, Mail, MapPin, ArrowLeft, Send,
  ChevronDown, Store, Package, AlertTriangle, Banknote, CreditCard, Building2, Wallet, Settings, Check, Bluetooth, X, MoreVertical, ArrowLeftRight, RotateCcw
} from 'lucide-react';
import LogModal from '../components/LogModal';
import PriceInput from '../components/PriceInput';
import ZoomablePhoto from '../components/ZoomablePhoto';
import RepairsMobileHome from './RepairsMobileHome';
import logService from '../services/logService';
import { API_URL, API_HOST } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint } from '../utils/printHelper';
import { buildCustomerReceiptHtml, buildRepairReceiptHtml, buildRepairPaymentReceiptHtml } from '../utils/repairReceiptHtml';
import { computeRepairPricing, parseRepairPricing, itemPricingToForm, formatTL, discountLabel, itemPriceLabel } from '../utils/repairPricing';
import {
  getBluetoothPrinterInfo,
  printRawToBluetooth,
  buildRepairCustomerReceiptBytes,
  buildRepairInternalReceiptBytes,
  buildTestReceiptBytes,
  pairBluetoothPrinter,
  clearBluetoothPrinter,
  setBluetoothPrinterEnabled,
  isBluetoothSupported,
  getBluetoothCols,
  setBluetoothCols,
} from '../utils/bluetoothPrinter';
import {
  startBTHostHeartbeat,
  stopBTHostHeartbeat,
  startBTHostJobPolling,
  stopBTHostJobPolling,
  printBTWithFallback,
} from '../utils/bluetoothRelay';
import { printSystemReceipt } from '../utils/systemPrintRelay';
import { normalizePhoneDigits, formatPhoneDisplay, isPhoneComplete } from '../utils/phoneFormat';
import './Repairs.css';

const IMAGE_HOST = API_HOST;

// Tamirat kaydında ödenen toplam tutar: varsa paid_amount + teslimde alınan ödeme (payment_amount).
// repairs tablosunda paid_amount kolonu yok; teslim ödemesi payment_amount'ta tutuluyor.
const getRepairPaidAmount = (repair) =>
  Number(repair.paid_amount || 0)
  + (repair.status === 'delivered' ? Number(repair.payment_amount || 0) : 0);

// "Alınan Ücret": müşteriden tahsil edilen toplam = kapora + teslimde alınan ödeme.
const getRepairCollectedAmount = (repair) =>
  Number(repair.deposit_amount || 0) + getRepairPaidAmount(repair);

// mobileApp: iOS uygulamasının WebView'ı (/mobile/tamirat, MobileTamirat.js) — ana görünüm telefona
// göre RepairsMobileHome ile çizilir; modallar, filtreler ve tüm hesaplar web ile ortaktır.
const Repairs = ({ user, selectedStore, mobileApp = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromRetailSales = location.state?.from === 'retail-sales';

  // Mağaza türünü belirle (store_id 1 = altın, diğerleri = gümüş)
  const currentStoreId = user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
  const isGoldStore = currentStoreId === 1;
  const storeMetalType = isGoldStore ? 'gold' : 'silver';

  const [repairs, setRepairs] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Admin uygulamasında (mobileApp) varsayılan tüm mağazalar; web'de kullanıcının mağazası
  const [filterStore, setFilterStore] = useState(mobileApp ? 'all' : 'current'); // 'all', 'gold', 'osmangazi', 'yesilsu', 'current'
  const [filterRepairPoint, setFilterRepairPoint] = useState('all'); // 'all' veya repair_point_id
  const [showModal, setShowModal] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRepairDetails, setSelectedRepairDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [customerDebtBalance, setCustomerDebtBalance] = useState(0);
  const [customerDebtMap, setCustomerDebtMap] = useState({});
  const [expandedItemsRepairId, setExpandedItemsRepairId] = useState(null);
  // Tamirat formunda 3'ten fazla fotoğrafı olan ürünün tüm fotoğraflarını aç/kapa
  const [expandedPhotoItem, setExpandedPhotoItem] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const customerDropdownRef = useRef(null);

  // Terminal fotoğraf çekimi için state'ler
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [waitingForPhoto, setWaitingForPhoto] = useState(false);
  const [photoRequestId, setPhotoRequestId] = useState(null);
  const [terminalPhotoItemIndex, setTerminalPhotoItemIndex] = useState(0); // Hangi ürüne fotoğraf eklenecek
  const photoCheckIntervalRef = useRef(null);

  // Müşteri seçimi için
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [repairCustomerTab, setRepairCustomerTab] = useState('search'); // 'search' | 'new'

  // Log modal için
  const [showLogModal, setShowLogModal] = useState(false);

  // Sütun ayarları
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const defaultRepairColumns = {
    no: true, source: false, customer: true, store: true, items: true,
    price: true, deposit: true, balance: true, estimatedDelivery: false,
    deadline: true, staff: false, customerCalled: false, messageLeft: false,
    createdAt: true, status: true, actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('repairsVisibleColumns');
    if (saved) return { ...defaultRepairColumns, ...JSON.parse(saved) };
    return defaultRepairColumns;
  });
  const allRepairColumns = [
    { key: 'no', label: 'No' },
    { key: 'source', label: 'Kaynak' },
    { key: 'customer', label: 'Müşteri' },
    { key: 'store', label: 'Mağaza' },
    { key: 'items', label: 'Ürünler' },
    { key: 'price', label: 'Toplam Ücret' },
    { key: 'deposit', label: 'Alınan Ücret' },
    { key: 'balance', label: 'Kalan Bakiye' },
    { key: 'estimatedDelivery', label: 'Tahmini Teslim' },
    { key: 'deadline', label: 'En Geç Teslim' },
    { key: 'staff', label: 'Kasiyer' },
    { key: 'customerCalled', label: 'Arandı' },
    { key: 'messageLeft', label: 'Mesaj' },
    { key: 'createdAt', label: 'Kayıt Tarihi' },
    { key: 'status', label: 'Durum' },
    { key: 'actions', label: 'İşlemler' }
  ];
  const toggleColumn = (key) => {
    const updated = { ...visibleColumns, [key]: !visibleColumns[key] };
    setVisibleColumns(updated);
    localStorage.setItem('repairsVisibleColumns', JSON.stringify(updated));
  };
  const resetColumns = () => {
    setVisibleColumns(defaultRepairColumns);
    localStorage.setItem('repairsVisibleColumns', JSON.stringify(defaultRepairColumns));
  };

  // Tablo sıralama
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };
  const sortIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';
  const repairStatusOrder = {
    received: 0, sent_to_workshop: 1, in_progress: 2, completed: 3,
    returning_to_store: 4, ready_for_pickup: 5, checked: 6, delivered: 7
  };
  const getRepairSortValue = (repair, key, estimatedDeliveryField = 'estimated_delivery') => {
    switch (key) {
      case 'price':
        return Number(repair.estimated_price || 0);
      case 'deposit':
        return getRepairCollectedAmount(repair);
      case 'balance': {
        if (repair.price_type === 'free' || repair.price_type === 'pending') return 0;
        const total = Number(repair.estimated_price || 0);
        const deposit = Number(repair.deposit_amount || 0);
        const paid = getRepairPaidAmount(repair);
        return total - deposit - paid;
      }
      case 'deadline':
        return repair.deadline ? new Date(repair.deadline).getTime() : 0;
      case 'estimatedDelivery':
        return repair[estimatedDeliveryField] ? new Date(repair[estimatedDeliveryField]).getTime() : 0;
      case 'createdAt':
        return repair.created_at ? new Date(repair.created_at).getTime() : 0;
      case 'status':
        return repairStatusOrder[repair.status] ?? 99;
      default:
        return 0;
    }
  };
  const sortRepairs = (arr, estimatedDeliveryField = 'estimated_delivery') => {
    if (!sortConfig.key) return arr;
    return [...arr].sort((a, b) => {
      const av = getRepairSortValue(a, sortConfig.key, estimatedDeliveryField);
      const bv = getRepairSortValue(b, sortConfig.key, estimatedDeliveryField);
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Yardım modal için
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Bluetooth yazıcı ayarları modal
  const [showBTPrinterModal, setShowBTPrinterModal] = useState(false);
  const [btPrinterInfo, setBtPrinterInfo] = useState(() => getBluetoothPrinterInfo());
  const [btPairing, setBtPairing] = useState(false);
  const [btTesting, setBtTesting] = useState(false);
  const [btCols, setBtCols] = useState(() => getBluetoothCols());

  // Mağaza bilgisi (BT fiş başlığı için) — component seviyesinde, hem manuel hem auto için kullanılır
  const buildStoreInfoForId = (storeId) => {
    if (storeId === 1) {
      return {
        name: 'AKİK GOLD JEWELLERY',
        address: 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D Şehitkamil/Gaziantep',
        phone: 'Tel: 0342 230 00 64',
        web: 'www.akikjewellery.com',
      };
    }
    if (storeId === 3) {
      return {
        name: 'AKİK GÜMÜŞ',
        branch: '(YEŞİLSU ŞUBESİ)',
        address: 'Bey Mah. Atatürk Blv. 43/A Şahinbey/Gaziantep',
        phone: 'Tel: 0342 230 00 64',
      };
    }
    return {
      name: 'AKİK GÜMÜŞ',
      branch: '(OSMANGAZİ ŞUBESİ)',
      address: 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F Şehitkamil/Gaziantep',
      phone: 'Tel: 0342 230 00 64',
    };
  };

  // Sistem yazıcı sıralı akış: yeni tamirat sonrası önce Müşteri sonra Tamirat fişi
  // arka arkaya otomatik yazdırılır (devam butonu yok).
  const runSystemPrintSequence = async (repair) => {
    if (!repair) return;
    const storeId = repair.store_id || currentStoreId;
    let customerHtml = null;
    let internalHtml = null;
    try {
      customerHtml = await printReceipt(repair, true);
      internalHtml = printRepairReceipt(repair, true);
    } catch (e) {
      console.error('Fiş HTML üretim hatası:', e);
      toast.error('Fiş hazırlanamadı: ' + (e?.message || ''));
      return;
    }
    if (!customerHtml || !internalHtml) {
      toast.error('Fiş HTML üretilemedi');
      return;
    }

    const sendOne = async (html, label, role) => {
      try {
        const result = await printSystemReceipt(html, storeId, role);
        if (result.mode === 'local') toast.success(`${label} yazıcıya gönderildi`);
        else if (result.pending) toast.info(`${label} kuyruğa alındı (${result.targetName})`);
        else toast.success(`${label} ${result.targetName} cihazından yazdırıldı`);
      } catch (e) {
        toast.error(`${label} yazdırma hatası: ` + (e?.message || ''));
      }
    };

    await sendOne(customerHtml, 'Müşteri Fişi', 'repair');
    await sendOne(internalHtml, 'Tamirat Fişi', 'repair');
  };

  // BT yazıcı röle: bu cihazda BT yazıcı paired+enabled ise host olarak heartbeat + job polling başlat
  useEffect(() => {
    if (btPrinterInfo.enabled && btPrinterInfo.name && currentStoreId) {
      startBTHostHeartbeat(currentStoreId);
      startBTHostJobPolling();
    } else {
      stopBTHostHeartbeat();
      stopBTHostJobPolling();
    }
    return () => {
      stopBTHostHeartbeat();
      stopBTHostJobPolling();
    };
  }, [btPrinterInfo.enabled, btPrinterInfo.name, currentStoreId]);

  // Not: sistem yazıcı host heartbeat'i App.js'te tüm uygulama için global başlatılıyor.

  // Tamirattan ürün teslim alma için
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [selectedForDeliver, setSelectedForDeliver] = useState([]);
  const [deliverBarcodeInput, setDeliverBarcodeInput] = useState('');
  const [deliverStaffName, setDeliverStaffName] = useState(''); // Toplu teslim alan kasiyer
  const [showBulkDeliverStaffModal, setShowBulkDeliverStaffModal] = useState(false); // Toplu teslimde kasiyer seçim modalı
  const deliverBarcodeInputRef = useRef(null);

  // Müşteri formu — telefon eşleşmesi ve seçim kaynağı
  const [phoneMatchCustomers, setPhoneMatchCustomers] = useState([]);
  const [customerSelectedFromMatch, setCustomerSelectedFromMatch] = useState(false);

  // Header'daki İşlemler dropdown menüsü
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef(null);
  const menuItemStyle = {
    display: 'flex', alignItems: 'center', gap: '10px',
    width: '100%', padding: '10px 14px', background: '#fff',
    border: 'none', borderBottom: '1px solid #f1f5f9',
    color: '#1e293b', fontSize: '0.85rem', fontWeight: 500,
    cursor: 'pointer', textAlign: 'left'
  };
  const menuBadgeStyle = {
    background: '#ef4444', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
    minWidth: '20px', height: '20px', borderRadius: '10px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 6px'
  };

  // Toplu tamirat çıkışı (received → sent_to_workshop) için
  const [showBulkDispatchModal, setShowBulkDispatchModal] = useState(false);
  const [bulkDispatchSelected, setBulkDispatchSelected] = useState([]);
  const [bulkDispatchBarcodeInput, setBulkDispatchBarcodeInput] = useState('');
  const [bulkDispatchPointId, setBulkDispatchPointId] = useState('');
  const [bulkDispatching, setBulkDispatching] = useState(false);
  const bulkDispatchBarcodeInputRef = useRef(null);

  // Admin manuel "Atölyeye Gönderildi/Tamirata Başlandı" yaparken tamirat noktası seçtirme
  const [statusPointModal, setStatusPointModal] = useState(null); // { repair, newStatus } | null
  // Kontrolde beğenilmeyen tamiratı tekrar atölyeye gönderme: { repair } | null
  const [reworkModal, setReworkModal] = useState(null);
  const [reworkPointId, setReworkPointId] = useState('');
  const [reworkReason, setReworkReason] = useState('');
  const [reworkSaving, setReworkSaving] = useState(false);
  const [statusPointId, setStatusPointId] = useState('');

  // Tamirattan teslim alma kasiyer seçimi
  const [showReceiveStaffModal, setShowReceiveStaffModal] = useState(false);
  const [receiveStaffAction, setReceiveStaffAction] = useState(null); // { repair, hasAppAccess }
  const [receiveStaffName, setReceiveStaffName] = useState('');

  // Kontrol edildi kasiyer seçimi
  const [showCheckStaffModal, setShowCheckStaffModal] = useState(false);
  const [checkStaffName, setCheckStaffName] = useState('');
  const [checkRepair, setCheckRepair] = useState(null);

  // Müşteriye teslim doğrulama için
  const [showDeliverConfirmModal, setShowDeliverConfirmModal] = useState(false);
  const [deliverConfirmCode, setDeliverConfirmCode] = useState('');
  const [deliverConfirmError, setDeliverConfirmError] = useState('');
  // Kod olmadan teslim - DB'den yüklenir, tüm cihazlarda ortak
  const [repairOverrideEnabled, setRepairOverrideEnabled] = useState(false);

  // Tamirat ödeme modalı için
  const [showRepairPaymentModal, setShowRepairPaymentModal] = useState(false);
  const [repairPaymentMethod, setRepairPaymentMethod] = useState('');
  const [repairPaymentAmount, setRepairPaymentAmount] = useState('');
  const [repairPayments, setRepairPayments] = useState([]);
  const [depositPaymentSaving, setDepositPaymentSaving] = useState(false);
  const depositModalInitialIdsRef = useRef([]);
  const [repairPaymentRepair, setRepairPaymentRepair] = useState(null);
  const [repairPaymentIsOnSite, setRepairPaymentIsOnSite] = useState(false);
  const [repairBankAccounts, setRepairBankAccounts] = useState([]);
  const [selectedRepairBankAccount, setSelectedRepairBankAccount] = useState(null);
  // 'delivery' = teslim ödemesi, 'deposit' = kapora alma
  const [repairPaymentMode, setRepairPaymentMode] = useState('delivery');

  // Teslim edilenler tablosu açık/kapalı

  // Atölye Çıkış Paneli için
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [workshopDispatchRepair, setWorkshopDispatchRepair] = useState(null);
  const [showWorkshopDispatchModal, setShowWorkshopDispatchModal] = useState(false);
  const [stores, setStores] = useState([]);
  const [repairNotDone, setRepairNotDone] = useState(false);
  // Atölye çıkışında zorunlu not
  const [workshopDispatchNote, setWorkshopDispatchNote] = useState('');

  // Tamirat Noktaları için
  const [repairPoints, setRepairPoints] = useState([]);
  const [workshopUsers, setWorkshopUsers] = useState([]);
  const [showRepairPointModal, setShowRepairPointModal] = useState(false); // Tamirata gönder seçim modalı
  const [showRepairPointManageModal, setShowRepairPointManageModal] = useState(false); // Yönetim modalı
  const [repairPointForm, setRepairPointForm] = useState({ name: '', contact_info: '', has_app_access: false, user_id: '' });
  const [editingRepairPoint, setEditingRepairPoint] = useState(null);
  const [sendingToRepairPoint, setSendingToRepairPoint] = useState(null); // Hangi tamirat gönderiliyor

  // Şube Transferi için
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferringRepair, setTransferringRepair] = useState(null); // Hangi tamirat transfer ediliyor
  const [showTransferStaffModal, setShowTransferStaffModal] = useState(false); // Transferi kim yaptı seçim modalı
  const [transferTargetStore, setTransferTargetStore] = useState(null); // Seçilen hedef şube { id, name }
  const [transferStaffName, setTransferStaffName] = useState('');

  // Modal kapanış animasyonu için state
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showDeliverConfirmModal, () => setShowDeliverConfirmModal(false)],
    [showHelpModal, () => setShowHelpModal(false)],
    [showBTPrinterModal, () => setShowBTPrinterModal(false)],
    [!!viewingPhoto, () => setViewingPhoto(null)],
    [showDetailModal, () => setShowDetailModal(false)],
    [showTerminalModal, () => setShowTerminalModal(false)],
    [showRepairPaymentModal, () => setShowRepairPaymentModal(false)],
    [showRepairPointModal, () => setShowRepairPointModal(false)],
    [showRepairPointManageModal, () => setShowRepairPointManageModal(false)],
    [showTransferModal, () => setShowTransferModal(false)],
    [showTransferStaffModal, () => setShowTransferStaffModal(false)],
    [showBulkDeliverStaffModal, () => setShowBulkDeliverStaffModal(false)],
    [showDeliverModal, () => setShowDeliverModal(false)],
    [showBulkDispatchModal, () => setShowBulkDispatchModal(false)],
    [showWorkshopDispatchModal, () => setShowWorkshopDispatchModal(false)],
    [showWorkshopModal, () => setShowWorkshopModal(false)],
    [showModal, () => setShowModal(false)],
    [showLogModal, () => {}],
  ]);

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      // onCancel eskiden config'e kopyalanmıyordu; bu yüzden "Kapat (1 Adet)" gibi cancel
      // butonuna bağlanan işlemler (ör. tamirat tahsilat fişini 1 adet yazdır) hiç çalışmıyordu.
      onCancel: config.onCancel || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Modal kapanış handler'ı (callback opsiyonel)
  const handleCloseModal = (modalName, setShowModal, callback) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
      if (callback) callback();
    }, 250);
  };

  // Fotoğrafları parse et
  const parsePhotos = (photos) => {
    if (!photos) return [];
    try {
      return typeof photos === 'string' ? JSON.parse(photos) : photos;
    } catch (e) {
      return [];
    }
  };

  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    items: [{
      item_type: storeMetalType,
      item_description: '',
      problem_description: '',
      photos: [],
      price_type: 'normal',
      price: '',
      discount_type: 'amount',
      discount_value: ''
    }],
    estimated_price: '',
    price_type: 'normal',
    pricing_mode: 'receipt',
    discount_type: 'amount',
    discount_value: '',
    deposit_amount: '',
    deposit_payment_method: '',
    deposit_payment_details: [],
    delivery_payment_details: [],
    estimated_delivery: '',
    deadline: '',
    notes: '',
    staff_name: ''
  });

  useEffect(() => {
    fetchRepairs();
    fetchCustomers();
    fetchStores();
    fetchStaff();
    fetchRepairPoints();
    fetchRepairOverrideEnabled();
  }, []);

  // Müşteri arama inputu değişince API'ye debounce'lu istek at (ilk 100'ün dışındaki müşterileri de bul)
  useEffect(() => {
    if (!customerSearch.trim()) return;
    const timer = setTimeout(() => {
      fetchCustomers(customerSearch.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // iPad/iOS: bu sayfa ilk açılışta hafif zoom'lu açılmasın diye viewport ölçeğini 1.0'a kilitle.
  // Sadece bu sayfaya özel; sayfadan çıkınca orijinal viewport'a geri dönülür (diğer sayfalar etkilenmez).
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    const original = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1');
    return () => {
      meta.setAttribute('content', original || 'width=device-width, initial-scale=1');
    };
  }, []);

  // Tamirat teslim override (kod olmadan teslim) ayarını DB'den yükle
  const fetchRepairOverrideEnabled = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/repair-deliver-override`);
      const data = await response.json();
      setRepairOverrideEnabled(!!data.enabled && !!data.hasPassword);
    } catch (error) {
      console.error('Tamirat teslim override ayarı yüklenemedi:', error);
    }
  };

  // Admin mağaza filtresi değiştiğinde personel listesini güncelle
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const storeIdMap = { gold: 1, osmangazi: 2, yesilsu: 3, current: currentStoreId, all: null };
    fetchStaff(storeIdMap[filterStore] || null);
  }, [filterStore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRepairs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/repairs`);
      const data = await response.json();
      setRepairs(data);
      fetchCustomerDebtsSummary();
    } catch (error) {
      console.error('Tamirat yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  // Borcu olan tüm müşterileri tek seferde getir (satır bazlı borç uyarısı ikonu için)
  const fetchCustomerDebtsSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/customers/debts/summary`);
      const data = await response.json();
      const map = {};
      (Array.isArray(data) ? data : []).forEach(row => {
        const tlDebt = parseFloat(row.total_tl_debt) || 0;
        if (tlDebt > 0.01) {
          map[row.customer_id] = tlDebt;
        }
      });
      setCustomerDebtMap(map);
    } catch (error) {
      console.error('Müşteri bakiye özeti yüklenemedi:', error);
    }
  };

  const fetchCustomers = async (search = '') => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`${API_URL}/customers?page=1&limit=100${searchParam}`);
      const data = await response.json();
      setCustomers(data.customers || data);
    } catch (error) {
      console.error('Müşteri yükleme hatası:', error);
    }
  };

  // Mağazaları getir
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setStores(data);
      }
    } catch (error) {
      console.error('Mağazalar yüklenemedi:', error);
    }
  };

  const fetchStaff = async (storeId) => {
    try {
      const sid = storeId || currentStoreId;
      const storeParam = sid ? `?store_id=${sid}` : '';
      const response = await fetch(`${API_URL}/sales-staff${storeParam}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setStaffList(data);
      }
    } catch (error) {
      console.error('Personel listesi yüklenemedi:', error);
    }
  };

  // Tamirat noktalarını getir
  const fetchRepairPoints = async () => {
    try {
      const response = await fetch(`${API_URL}/repair-points`);
      const data = await response.json();
      setRepairPoints(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Tamirat noktaları yüklenemedi:', error);
    }
  };

  // Atölye kullanıcılarını getir
  const fetchWorkshopUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/repair-points/workshop-users`);
      const data = await response.json();
      setWorkshopUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Atölye kullanıcıları yüklenemedi:', error);
    }
  };

  // Tamirat noktası kaydet (ekle/güncelle)
  const saveRepairPoint = async () => {
    if (!repairPointForm.name.trim()) {
      toast.error('Tamirat noktası adı zorunludur!');
      return;
    }
    if (repairPointForm.has_app_access && !repairPointForm.user_id) {
      toast.error('Uygulama erişimi için atölye kullanıcısı seçmelisiniz!');
      return;
    }
    try {
      const url = editingRepairPoint
        ? `${API_URL}/repair-points/${editingRepairPoint.id}`
        : `${API_URL}/repair-points`;
      const method = editingRepairPoint ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: repairPointForm.name.trim(),
          contact_info: repairPointForm.contact_info.trim(),
          has_app_access: repairPointForm.has_app_access,
          user_id: repairPointForm.has_app_access ? repairPointForm.user_id : null,
          store_id: currentStoreId
        })
      });
      const data = await response.json();
      if (data.success || data.id) {
        toast.success(editingRepairPoint ? 'Tamirat noktası güncellendi!' : 'Tamirat noktası eklendi!');
        setRepairPointForm({ name: '', contact_info: '', has_app_access: false, user_id: '' });
        setEditingRepairPoint(null);
        fetchRepairPoints();
      } else {
        toast.error(data.error || 'Hata oluştu!');
      }
    } catch (error) {
      toast.error('Sunucu hatası!');
    }
  };

  // Tamirat noktası sil
  const deleteRepairPoint = async (id) => {
    try {
      const response = await fetch(`${API_URL}/repair-points/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        toast.success('Tamirat noktası silindi!');
        fetchRepairPoints();
      }
    } catch (error) {
      toast.error('Silme hatası!');
    }
  };

  // Tamirata gönder (tamirat noktası seçili)
  const sendToRepairPoint = async (repairPointId) => {
    if (!sendingToRepairPoint) return;
    try {
      const response = await fetch(`${API_URL}/repairs/${sendingToRepairPoint.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'sent_to_workshop',
          repair_point_id: repairPointId
        })
      });
      const data = await response.json();
      if (data.success) {
        const point = repairPoints.find(rp => rp.id === repairPointId);
        toast.success(`Ürün "${point?.name}" tamirat noktasına gönderildi!`);
        setShowRepairPointModal(false);
        setSendingToRepairPoint(null);
        if (selectedRepairDetails) {
          setSelectedRepairDetails(prev => ({
            ...prev,
            repair: {
              ...prev.repair,
              status: 'sent_to_workshop',
              repair_point_id: repairPointId,
              repair_point_name: point?.name,
              repair_point_has_app_access: point?.has_app_access
            }
          }));
        }
        fetchRepairs();
      } else {
        toast.error(data.error || 'Hata oluştu!');
      }
    } catch (error) {
      toast.error('Sunucu hatası!');
    }
  };

  // Tamiratı başka bir şubeye transfer et (müşteri farklı şubeden teslim almak isterse)
  const transferRepairToStore = async (targetStoreId, staffName) => {
    if (!transferringRepair) return;
    try {
      const response = await fetch(`${API_URL}/repairs/${transferringRepair.id}/transfer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store_id: targetStoreId, staff_name: staffName || null })
      });
      const data = await response.json();
      if (data.success) {
        const store = stores.find(s => s.id === targetStoreId);
        toast.success(`Tamirat "${store?.name || ''}" şubesine transfer edildi, teslim alınmayı bekliyor!`);
        setShowTransferStaffModal(false);
        setTransferringRepair(null);
        setTransferTargetStore(null);
        setTransferStaffName('');
        if (selectedRepairDetails) {
          setSelectedRepairDetails(prev => ({
            ...prev,
            repair: { ...prev.repair, transferred_to_store_id: targetStoreId, transferred_store_name: store?.name || data.transferred_store_name, status: 'returning_to_store' }
          }));
        }
        fetchRepairs();
      } else {
        toast.error(data.error || 'Hata oluştu!');
      }
    } catch (error) {
      toast.error('Sunucu hatası!');
    }
  };

  // Mağaza adını getir
  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Bilinmeyen Mağaza';
  };

  // Terminal listesini getir (mağazaya bağlı)
  const fetchTerminals = async () => {
    setLoadingTerminals(true);
    try {
      // Kullanıcının mağaza ID'sini al (localStorage'dan)
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const storeId = userData.store_id || 1;

      const response = await fetch(`${API_URL}/users/terminals/${storeId}`);
      const data = await response.json();
      setTerminals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Terminal yükleme hatası:', error);
      setTerminals([]);
    } finally {
      setLoadingTerminals(false);
    }
  };

  // Terminal seçim modalını aç
  const openTerminalModal = (itemIndex = 0) => {
    setTerminalPhotoItemIndex(itemIndex);
    fetchTerminals();
    setShowTerminalModal(true);
    setSelectedTerminal(null);
  };

  // Terminale fotoğraf çekim isteği gönder
  const sendPhotoRequest = async (terminal) => {
    try {
      const response = await fetch(`${API_URL}/terminal-photo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          terminal_user_id: terminal.id,
          request_type: 'repair_photo',
          description: formData.item_description || 'Tamirat ürünü'
        })
      });

      const data = await response.json();
      if (data.success) {
        setPhotoRequestId(data.request_id);
        setSelectedTerminal(terminal);
        setShowTerminalModal(false);
        setWaitingForPhoto(true);
        toast.info(`${terminal.name} terminaline fotoğraf isteği gönderildi!`);

        // Fotoğraf geldi mi kontrol et (her 2 saniyede bir)
        startPhotoCheck(data.request_id);
      } else {
        toast.error(data.message || 'İstek gönderilemedi!');
      }
    } catch (error) {
      console.error('Fotoğraf isteği hatası:', error);
      toast.error('İstek gönderilirken hata oluştu!');
    }
  };

  // Fotoğraf kontrolünü başlat
  const startPhotoCheck = (requestId) => {
    // Önceki interval'ı temizle
    if (photoCheckIntervalRef.current) {
      clearInterval(photoCheckIntervalRef.current);
    }

    photoCheckIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/terminal-photo-requests/${requestId}`);
        const data = await response.json();

        if (data.status === 'completed' && data.photo_url) {
          // Fotoğraf geldi!
          clearInterval(photoCheckIntervalRef.current);
          setWaitingForPhoto(false);
          setPhotoRequestId(null);

          // Fotoğrafı doğru ürüne ekle
          setFormData(prev => ({
            ...prev,
            items: prev.items.map((item, i) =>
              i === terminalPhotoItemIndex
                ? { ...item, photos: [...(item.photos || []), data.photo_url] }
                : item
            )
          }));

          toast.success('Fotoğraf terminalden alındı!');
        } else if (data.status === 'rejected') {
          // Reddedildi
          clearInterval(photoCheckIntervalRef.current);
          setWaitingForPhoto(false);
          setPhotoRequestId(null);
          toast.warning('Terminal fotoğraf isteğini reddetti.');
        } else if (data.status === 'expired') {
          // Süresi doldu
          clearInterval(photoCheckIntervalRef.current);
          setWaitingForPhoto(false);
          setPhotoRequestId(null);
          toast.warning('Fotoğraf isteğinin süresi doldu.');
        }
      } catch (error) {
        console.error('Fotoğraf kontrol hatası:', error);
      }
    }, 2000);

    // 2 dakika sonra timeout
    setTimeout(() => {
      if (photoCheckIntervalRef.current) {
        clearInterval(photoCheckIntervalRef.current);
        if (waitingForPhoto) {
          setWaitingForPhoto(false);
          setPhotoRequestId(null);
          toast.warning('Fotoğraf isteği zaman aşımına uğradı.');
        }
      }
    }, 120000);
  };

  // Fotoğraf beklemeyi iptal et
  const cancelPhotoWait = async () => {
    if (photoCheckIntervalRef.current) {
      clearInterval(photoCheckIntervalRef.current);
    }

    if (photoRequestId) {
      try {
        await fetch(`${API_URL}/terminal-photo-requests/${photoRequestId}/cancel`, {
          method: 'PUT'
        });
      } catch (error) {
        console.error('İptal hatası:', error);
      }
    }

    setWaitingForPhoto(false);
    setPhotoRequestId(null);
    setSelectedTerminal(null);
    toast.info('Fotoğraf isteği iptal edildi.');
  };

  // Component unmount olduğunda interval'ı temizle
  useEffect(() => {
    return () => {
      if (photoCheckIntervalRef.current) {
        clearInterval(photoCheckIntervalRef.current);
      }
    };
  }, []);

  // Telefon numarasıyla mevcut müşteri ara — formdaki telefon input'una bağlanır
  const searchCustomerByPhone = async (phone) => {
    const cleanPhone = (phone || '').replace(/\D/g, '');
    if (cleanPhone.length < 9) {
      setPhoneMatchCustomers([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/customers?search=${cleanPhone}`);
      const data = await response.json();
      const list = data.customers || data || [];
      if (Array.isArray(list)) {
        const matches = list.filter(c => {
          if (!c.phone || !c.phone.replace(/\D/g, '').includes(cleanPhone)) return false;
          // Mağaza türüne göre filtrele: altın mağazasında sadece altın, gümüş mağazasında sadece gümüş müşterileri
          const customerStoreType = (c.store_type || '').toLowerCase();
          if (isGoldStore) return customerStoreType === 'gold' || customerStoreType === 'altın';
          return customerStoreType === 'silver' || customerStoreType === 'gümüş';
        });
        setPhoneMatchCustomers(matches.slice(0, 5));
      }
    } catch (error) {
      setPhoneMatchCustomers([]);
    }
  };

  // Form üzerinden yeni müşteri oluştur
  const createNewCustomer = async () => {
    if (!formData.customer_name) {
      toast.warning('Müşteri adı gerekli!');
      return;
    }
    if (formData.customer_id) {
      toast.info('Müşteri zaten seçili!');
      return;
    }
    try {
      // Önce aynı telefonla mevcut müşteri var mı bak; varsa onu seç, yenisini açma.
      if (formData.customer_phone && !isPhoneComplete(formData.customer_phone)) {
        toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
        return;
      }
      const cleanPhone = (formData.customer_phone || '').replace(/\D/g, '');
      if (cleanPhone.length >= 9) {
        try {
          const searchRes = await fetch(`${API_URL}/customers?search=${cleanPhone}`);
          const searchData = await searchRes.json();
          const list = searchData.customers || searchData || [];
          if (Array.isArray(list)) {
            const match = list.find(c => {
              if (!c.phone) return false;
              const cPhone = c.phone.replace(/\D/g, '');
              if (!cPhone.includes(cleanPhone) && !cleanPhone.includes(cPhone)) return false;
              const st = (c.store_type || '').toLowerCase();
              if (isGoldStore) return st === 'gold' || st === 'altın';
              return st === 'silver' || st === 'gümüş';
            });
            if (match) {
              setFormData(prev => ({ ...prev, customer_id: match.id, customer_name: match.name, customer_phone: match.phone || prev.customer_phone }));
              setPhoneMatchCustomers([]);
              setCustomerSelectedFromMatch(true);
              toast.info(`Mevcut müşteri seçildi: ${match.name}`);
              return;
            }
          }
        } catch (e) { /* aramada hata olursa yeni kayıt akışına devam */ }
      }

      const response = await fetch(`${API_URL}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.customer_name,
          phone: formData.customer_phone || '',
          store_type: isGoldStore ? 'Altın' : 'Gümüş',
          kvkk_consent: true
        })
      });
      const data = await response.json();
      if (data.success && data.customer) {
        setFormData(prev => ({ ...prev, customer_id: data.customer.id }));
        setPhoneMatchCustomers([]);
        setCustomerSelectedFromMatch(false);
        toast.success('Müşteri kaydedildi!');
        fetchCustomers();
      } else {
        toast.error(data.message || data.error || 'Müşteri oluşturulamadı!');
      }
    } catch (error) {
      console.error('Müşteri oluşturma hatası:', error);
      toast.error('Müşteri oluşturulamadı!');
    }
  };

  // Müşteri arama (mağaza türüne göre filtreleme)
  const filteredCustomers = customers.filter(c => {
    // Mağaza türüne göre müşteri filtrele
    const customerStoreType = (c.store_type || '').toLowerCase();
    // Altın mağazasında sadece altın müşterileri (gold veya altın)
    if (isGoldStore && customerStoreType !== 'gold' && customerStoreType !== 'altın') return false;
    // Gümüş mağazasında sadece gümüş müşterileri (silver veya gümüş)
    if (!isGoldStore && customerStoreType !== 'silver' && customerStoreType !== 'gümüş') return false;

    // İsim veya telefon ile ara (Türkçe İ/ı için toLocaleLowerCase('tr') kullan)
    return c.name?.toLocaleLowerCase('tr').includes(customerSearch.toLocaleLowerCase('tr')) ||
           c.phone?.includes(customerSearch);
  });

  // Tamirat detaylarını getir
  const fetchRepairDetails = async (repairId) => {
    try {
      setDetailLoading(true);
      setCustomerDebtBalance(0);
      const response = await fetch(`${API_URL}/repairs/${repairId}/details`);
      const data = await response.json();
      if (data.success) {
        setSelectedRepairDetails(data);
        setShowDetailModal(true);

        const customerId = data.repair?.customer_id;
        if (customerId) {
          try {
            const debtRes = await fetch(`${API_URL}/customers/${customerId}/debts`);
            const debtData = await debtRes.json();
            const tlBalance = debtData?.tl_balance ?? debtData?.balance ?? 0;
            setCustomerDebtBalance(tlBalance > 0.01 ? tlBalance : 0);
          } catch (debtError) {
            console.error('Müşteri bakiye kontrolü hatası:', debtError);
          }
        }
      }
    } catch (error) {
      console.error('Detay yükleme hatası:', error);
      toast.error('Detaylar yüklenemedi!');
    } finally {
      setDetailLoading(false);
    }
  };

  // Tamirat detay modalındaki "Tahsilat yapınız" uyarısına tıklanınca
  // Perakende Satış sayfasına gidip o müşterinin tahsilat penceresini otomatik aç
  const goToCollectionForRepairCustomer = () => {
    const repair = selectedRepairDetails?.repair;
    if (!repair?.customer_id) return;
    navigate('/retail-sales', {
      state: {
        from: 'repairs',
        openCollectionCustomerId: repair.customer_id,
        openCollectionCustomerName: repair.customer_name,
        openCollectionCustomerPhone: repair.customer_phone,
      }
    });
  };

  // RetailSales "Fişe Git" ile gelindiğinde ilgili tamiratın detay modalını otomatik aç
  useEffect(() => {
    const openRepairId = location.state?.openRepairId;
    const openCreateRepairModal = location.state?.openCreateRepairModal;
    if (openRepairId) {
      fetchRepairDetails(openRepairId);
      // Aynı kayıt geri/yenile'de tekrar açılmasın diye openRepairId'yi temizle ('from' korunur)
      navigate(location.pathname, { replace: true, state: { from: location.state?.from } });
    } else if (openCreateRepairModal) {
      // RetailSales "Tamirat" > "Tamirat Oluştur" ile gelindiğinde yeni kayıt modalını otomatik aç
      const prefillCustomer = location.state?.prefillCustomer;
      resetForm();
      if (prefillCustomer) {
        setFormData(prev => ({
          ...prev,
          customer_id: prefillCustomer.id,
          customer_name: prefillCustomer.name,
          customer_phone: prefillCustomer.phone || ''
        }));
      }
      setShowModal(true);
      navigate(location.pathname, { replace: true, state: { from: location.state?.from } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Durum adını Türkçe olarak getir
  const getStatusLabel = (status) => {
    const statusMap = {
      'received': 'Teslim Alındı',
      'sent_to_workshop': 'Atölyeye Gönderildi',
      'at_workshop': 'Atölyede',
      'in_progress': 'Tamirata Başlandı',
      'completed': 'Tamirat Tamamlandı',
      'returning_to_store': 'Mağazaya Yolda',
      'ready_for_pickup': 'Tamirattan Geldi',
      'checked': 'Kontrol Edildi',
      'delivered': 'Teslim Edildi'
    };
    return statusMap[status] || status;
  };

  // Durum için ikon getir
  const getStatusIcon = (status) => {
    const iconMap = {
      'received': <Inbox size={16} />,
      'sent_to_workshop': <Truck size={16} />,
      'at_workshop': <Package size={16} />,
      'in_progress': <Wrench size={16} />,
      'completed': <CheckCircle size={16} />,
      'returning_to_store': <Send size={16} />,
      'ready_for_pickup': <Gift size={16} />,
      'checked': <CheckCircle size={16} />,
      'delivered': <Gift size={16} />
    };
    return iconMap[status] || <ClipboardList size={16} />;
  };

  // Fiş yazdır (ya da returnHtml=true ile sadece HTML döndür)
  const printReceipt = async (repairParam, returnHtml = false) => {
    const repair = repairParam || selectedRepairDetails?.repair;
    if (!repair) return;
    const repairCode = repair.repair_code || String(repair.id).padStart(6, '0'); // 6 basamaklı fiş numarası

    // QR kodu al
    let qrCodeData = null;
    try {
      const qrResponse = await fetch(`${API_URL}/public/repair-qr/${repairCode}`);
      const qrResult = await qrResponse.json();
      if (qrResult.success) {
        qrCodeData = qrResult.qrCode;
      }
    } catch (e) {
      console.error('QR kod alınamadı:', e);
    }
    const html = buildCustomerReceiptHtml(repair, { storeId: currentStoreId, qrCodeData });
    if (returnHtml) return html;
    handlePrint(html, 'receipt');
  };

  // Tamirat Fişi Yazdır (iç kullanım için) — returnHtml=true sadece HTML döndürür
  const printRepairReceipt = (repairParam, returnHtml = false) => {
    const repair = repairParam || selectedRepairDetails?.repair;
    if (!repair) return;
    const html = buildRepairReceiptHtml(repair, { storeId: currentStoreId });
    if (returnHtml) return html;
    handlePrint(html, 'receipt');
  };

  // Tahsilat / Ödeme Bilgi Fişi Yazdır — o ana kadar alınan tutar ve kalan tutarı gösterir.
  // Kapora/teslim ödemesi kaydedilmeden önce (repairParam ile canlı önizleme) veya detay
  // ekranından herhangi bir zamanda kayıtlı tutarlarla çağrılabilir.
  const printRepairPaymentReceipt = (repairParam, returnHtml = false) => {
    const repair = repairParam || selectedRepairDetails?.repair;
    if (!repair) return;
    const html = buildRepairPaymentReceiptHtml(repair, { storeId: currentStoreId });
    if (returnHtml) return html;
    handlePrint(html, 'receipt');
  };

  // Otomatik "kaç adet yazdırılsın?" akışı için: manuel "Tamirat Tahsilat Fişi" butonuyla
  // aynı kanaldan (printSystemReceipt / tamirat yazıcı rölesi) fiş gönderir.
  const sendRepairPaymentReceipt = async (repairParam) => {
    const repair = repairParam || selectedRepairDetails?.repair;
    if (!repair) return;
    const storeId = repair.transferred_to_store_id || repair.store_id || currentStoreId;
    let html = null;
    try {
      html = printRepairPaymentReceipt(repair, true);
    } catch (e) {
      toast.error('Fiş hazırlanamadı: ' + (e?.message || ''));
      return;
    }
    if (!html) { toast.error('Fiş HTML üretilemedi'); return; }
    try {
      const result = await printSystemReceipt(html, storeId, 'repair');
      if (result.mode === 'local') toast.success('Tamirat Tahsilat Fişi yazıcıya gönderildi');
      else if (result.pending) toast.info(`Tamirat Tahsilat Fişi kuyruğa alındı (${result.targetName})`);
      else toast.success(`Tamirat Tahsilat Fişi ${result.targetName} cihazından yazdırıldı`);
    } catch (e) {
      toast.error('Tamirat Tahsilat Fişi yazdırma hatası: ' + (e?.message || ''));
    }
  };

  const handleSubmit = async () => {

    if (!formData.staff_name) {
      toast.warning('Kasiyer seçimi zorunludur!');
      return;
    }

    // En az bir ürün açıklaması olmalı
    const hasValidItem = formData.items.some(item => item.item_description?.trim());
    if (!hasValidItem) {
      toast.warning('En az bir ürün açıklaması gerekli!');
      return;
    }

    // Açıklaması girilen her ürün için en az 1 fotoğraf zorunlu
    const itemMissingPhoto = formData.items.find(item => item.item_description?.trim() && (!item.photos || item.photos.length === 0));
    if (itemMissingPhoto) {
      toast.warning(`"${itemMissingPhoto.item_description}" için en az 1 fotoğraf eklemelisiniz!`);
      return;
    }

    // Yeni kayıt açarken ad soyad + telefon zorunlu
    if (!editingRepair) {
      if (!formData.customer_phone?.trim()) {
        toast.warning('Telefon numarası zorunludur!');
        return;
      }
      if (!isPhoneComplete(formData.customer_phone)) {
        toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
        return;
      }
      // Telefona kayıtlı müşteri seçilmişse (customer_id var) isim otomatik düşmese de uyarı verme
      if (!formData.customer_id && !formData.customer_name?.trim()) {
        toast.warning('Ad soyad zorunludur!');
        return;
      }
    }

    // Ücret: ya tutar girilmeli, ya "Hesaplanacak" ya da "Ücretsiz" seçilmeli
    if (formData.pricing_mode === 'item') {
      const unpriced = formData.items.find(item => item.item_description?.trim() && (item.price_type || 'normal') === 'normal' && !(Number(item.price) > 0));
      if (unpriced) {
        toast.warning(`"${unpriced.item_description}" için ücret girin veya "Ücretsiz" / "Hesaplanacak" seçin!`);
        return;
      }
    } else if (formData.price_type === 'normal' && !(Number(formData.estimated_price) > 0)) {
      toast.warning('Ücret girin veya "Hesaplanacak" / "Ücretsiz" seçeneğini işaretleyin!');
      return;
    }

    const submitPricing = getFormPricing();

    // İndirim/fiyat değişikliğinden sonra alınan ücret yeni toplamı geçemez
    const alreadyPaid = [...(formData.deposit_payment_details || []), ...(formData.delivery_payment_details || [])]
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (alreadyPaid > 0.01 && submitPricing.price_type !== 'pending' && alreadyPaid - submitPricing.total > 0.01) {
      toast.warning(`Alınan ücret (${formatTL(alreadyPaid)}) tamirat toplamını (${formatTL(submitPricing.total)}) geçemez! Önce alınan ücreti düzenleyin.`);
      return;
    }

    try {
      // customer_id boşsa ama isim doluysa: önce telefonla eşleşen mevcut müşteriyi bul,
      // yoksa otomatik müşteri kaydı oluştur. (Duplikat kayıt oluşturmayı engeller.)
      let customerIdToUse = formData.customer_id;
      // Telefona kayıtlı mevcut cari bulunursa fişte de o carinin adı/telefonu kullanılsın
      // (kullanıcının elle yazdığı isim değil). Eşleşme yoksa yazılan isim geçerli.
      let customerNameToUse = formData.customer_name;
      let customerPhoneToUse = formData.customer_phone;
      if (!customerIdToUse && formData.customer_name?.trim()) {
        // 1) Telefonla mevcut müşteri ara (aynı mağaza tipi)
        const cleanPhone = (formData.customer_phone || '').replace(/\D/g, '');
        if (cleanPhone.length >= 9) {
          try {
            const searchRes = await fetch(`${API_URL}/customers?search=${cleanPhone}`);
            const searchData = await searchRes.json();
            const list = searchData.customers || searchData || [];
            if (Array.isArray(list)) {
              const match = list.find(c => {
                if (!c.phone) return false;
                const cPhone = c.phone.replace(/\D/g, '');
                if (!cPhone.includes(cleanPhone) && !cleanPhone.includes(cPhone)) return false;
                const st = (c.store_type || '').toLowerCase();
                if (isGoldStore) return st === 'gold' || st === 'altın';
                return st === 'silver' || st === 'gümüş';
              });
              if (match) {
                customerIdToUse = match.id;
                // Eşleşen carinin adı/telefonu esas alınır; elle yazılan isim göz ardı edilir
                customerNameToUse = match.name;
                customerPhoneToUse = match.phone || customerPhoneToUse;
              }
            }
          } catch (searchErr) {
            console.error('Müşteri telefon arama hatası:', searchErr);
          }
        }
        // 2) Eşleşme yoksa yeni müşteri oluştur
        if (!customerIdToUse) {
          try {
            const createRes = await fetch(`${API_URL}/customers`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: formData.customer_name,
                phone: formData.customer_phone || '',
                store_type: isGoldStore ? 'Altın' : 'Gümüş',
                kvkk_consent: true
              })
            });
            const createData = await createRes.json();
            if (createData.success && createData.customer) {
              customerIdToUse = createData.customer.id;
            } else if (createData.duplicate && createData.existing_customer) {
              // Aynı telefon+mağaza tipinde zaten cari var - mükerrer açmak yerine onu kullan
              customerIdToUse = createData.existing_customer.id;
            }
          } catch (createErr) {
            console.error('Otomatik müşteri oluşturma hatası:', createErr);
          }
        }
      }

      const url = editingRepair
        ? `${API_URL}/repairs/${editingRepair.id}`
        : `${API_URL}/repairs`;

      const method = editingRepair ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          items: submitPricing.items,
          price_type: submitPricing.price_type,
          estimated_price: submitPricing.price_type === 'normal' ? submitPricing.total : 0,
          pricing: buildPricingMeta(submitPricing),
          customer_id: customerIdToUse || null,
          customer_name: customerNameToUse,
          customer_phone: customerPhoneToUse,
          store_id: currentStoreId // Hangi mağazadan geldiğini kaydet
        })
      });

      const data = await response.json();
      if (data.success) {
        const action = editingRepair ? 'update' : 'create';
        const repairCode = data.repair_code || editingRepair?.repair_code || '-';
        const desc = editingRepair
          ? `Tamirat bilgileri güncellendi - ${formData.customer_name}`
          : `Yeni tamirat kaydı oluşturuldu - ${formData.customer_name}`;
        logService.log('repairs', action, desc, {
          entity_type: 'repair',
          entity_id: data.id || editingRepair?.id,
          entity_code: repairCode,
          details: {
            musteri: formData.customer_name,
            urun_sayisi: formData.items.filter(i => i.item_description?.trim()).length,
            urunler: formData.items.map(i => i.item_description).filter(Boolean).join(', '),
            fis_no: repairCode
          },
          status: 'success'
        });
        setShowModal(false);
        resetForm();
        fetchRepairs();

        // Yeni kayıtsa otomatik BT Müşteri çıksın, kullanıcı "Devam Et" basınca veya 15 sn sonra BT Tamirat
        if (!editingRepair && data.id) {
          try {
            const detailRes = await fetch(`${API_URL}/repairs/${data.id}/details`);
            const detailData = await detailRes.json();
            if (detailData.success && detailData.repair) {
              await runSystemPrintSequence(detailData.repair);

              // Yeni kayıt kapora ile açıldıysa (ilk teslimatta olduğu gibi) Tamirat
              // Tahsilat Fişi de yazdırılmalı — Müşteri/Tamirat fişleri ödeme fişinin
              // yerine geçmiyor, kasa mutabakatı için ayrı tahsilat fişi gerekiyor.
              const newDepositAmount = Number(detailData.repair.deposit_amount) || 0;
              if (newDepositAmount > 0) {
                showConfirmation({
                  title: 'Tamirat Tahsilat Fişi Yazdır',
                  message: 'Tamirat tahsilat fişi kaç adet yazdırılsın?',
                  confirmText: 'Yazdır (2 Adet)',
                  cancelText: 'Kapat (1 Adet)',
                  type: 'info',
                  onConfirm: () => {
                    sendRepairPaymentReceipt(detailData.repair);
                    setTimeout(() => sendRepairPaymentReceipt(detailData.repair), 1500);
                  },
                  onCancel: () => {
                    sendRepairPaymentReceipt(detailData.repair);
                  }
                });
              }
            }
          } catch (e) {
            console.error('Otomatik fiş yazdırma hatası:', e);
          }
        }
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('repairs', editingRepair ? 'update' : 'create', 'Tamirat kayıt hatası', { status: 'error', details: { error: error.message } });
    }
  };

  const handleStatusChange = async (repair, newStatus, notes = null, repairPointId = null) => {
    try {
      const body = { status: newStatus };
      if (notes) body.notes = notes;
      if (repairPointId) body.repair_point_id = repairPointId;
      const response = await fetch(`${API_URL}/repairs/${repair.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (data.success) {
        const statusTurkish = {
          'received': 'Alındı',
          'sent_to_workshop': 'Ustaya Gönderildi',
          'at_workshop': 'Atölyede',
          'in_progress': 'İşlemde',
          'completed': 'Tamamlandı',
          'delivered': 'Teslim Edildi',
          'Alındı': 'Alındı',
          'Ustaya Gönderildi': 'Ustaya Gönderildi',
          'İşlemde': 'İşlemde',
          'Tamamlandı': 'Tamamlandı',
          'Teslim Edildi': 'Teslim Edildi'
        };
        const oldStatusName = statusTurkish[repair.status] || repair.status;
        const newStatusName = statusTurkish[newStatus] || newStatus;
        logService.log('repairs', 'repair_status_change', `Durum değiştirildi: ${oldStatusName} → ${newStatusName}`, {
          entity_type: 'repair',
          entity_id: repair.id,
          entity_code: repair.repair_code,
          details: {
            musteri: repair.customer_name,
            eski_durum: oldStatusName,
            yeni_durum: newStatusName,
            fis_no: repair.repair_code
          },
          status: 'success'
        });
        fetchRepairs();
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
    }
  };

  // Tamirat noktası seçim modalı onayı (admin manuel atölyeye/tamirata gönderme)
  const confirmStatusPoint = async () => {
    if (!statusPointId) {
      toast.warning('Lütfen tamirat noktası seçin!');
      return;
    }
    const { repair, newStatus } = statusPointModal;
    const point = repairPoints.find(rp => rp.id === Number(statusPointId));
    await handleStatusChange(repair, newStatus, 'Admin tarafından değiştirildi', Number(statusPointId));
    setSelectedRepairDetails(prev => prev ? ({ ...prev, repair: { ...prev.repair, status: newStatus, repair_point_id: Number(statusPointId), repair_point_name: point?.name, repair_point_has_app_access: point?.has_app_access } }) : prev);
    toast.success(`Durum güncellendi: ${getStatusLabel(newStatus)}${point?.name ? ` (${point.name})` : ''}`);
    setStatusPointModal(null);
    setStatusPointId('');
  };

  // Mağazaya dönen (Tamirattan Geldi / Kontrol Edildi) ama istendiği gibi olmayan tamiratı
  // sebebiyle birlikte tekrar atölyeye gönder. Sebep tamiratın kalıcı notuna eklenir (atölye görür).
  const openReworkModal = (repair) => {
    setReworkModal({ repair });
    setReworkPointId(repair?.repair_point_id ? String(repair.repair_point_id) : '');
    setReworkReason('');
  };

  const confirmRework = async () => {
    if (!reworkModal?.repair || reworkSaving) return;
    const reason = reworkReason.trim();
    if (!reworkPointId) {
      toast.warning('Lütfen tamirat noktası seçin!');
      return;
    }
    if (!reason) {
      toast.warning('Lütfen tekrar gönderme sebebini yazın!');
      return;
    }
    const repair = reworkModal.repair;
    const pointId = Number(reworkPointId);
    const point = repairPoints.find(rp => rp.id === pointId);
    setReworkSaving(true);
    try {
      const response = await fetch(`${API_URL}/repairs/${repair.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'sent_to_workshop',
          repair_point_id: pointId,
          notes: `Tekrar atölyeye gönderildi: ${reason}`,
          rework_note: reason
        })
      });
      const data = await response.json();
      if (!data.success) {
        toast.error(data.error || 'Hata oluştu!');
        return;
      }
      logService.log('repairs', 'repair_status_change', `Tekrar atölyeye gönderildi: ${getStatusLabel(repair.status)} → Atölyeye Gönderildi`, {
        entity_type: 'repair',
        entity_id: repair.id,
        entity_code: repair.repair_code,
        details: {
          musteri: repair.customer_name,
          fis_no: repair.repair_code,
          eski_durum: getStatusLabel(repair.status),
          yeni_durum: 'Atölyeye Gönderildi',
          tamirat_noktasi: point?.name,
          sebep: reason
        },
        status: 'success'
      });
      toast.success(`Ürün tekrar "${point?.name || 'atölye'}" noktasına gönderildi!`);
      const reworkLine = `[Tekrar Atölyeye]: ${reason}`;
      setSelectedRepairDetails(prev => prev ? ({
        ...prev,
        repair: {
          ...prev.repair,
          status: 'sent_to_workshop',
          repair_not_done: 0,
          notes: prev.repair?.notes ? `${prev.repair.notes.trim()}\n${reworkLine}` : reworkLine,
          repair_point_id: pointId,
          repair_point_name: point?.name,
          repair_point_has_app_access: point?.has_app_access
        }
      }) : prev);
      setReworkModal(null);
      setReworkReason('');
      setReworkPointId('');
      fetchRepairs();
    } catch (error) {
      toast.error('Sunucu hatası!');
    } finally {
      setReworkSaving(false);
    }
  };

  // Müşteriye teslim et - ödeme modalını aç
  const handleDeliverToCustomer = () => {
    if (!selectedRepairDetails?.repair) return;
    const repair = selectedRepairDetails.repair;

    // Fiyatı "Hesaplanacak" olan tamirat, ücret belirlenmeden teslim edilemez
    if (repair.price_type === 'pending') {
      toast.warning('Fiyatı "Hesaplanacak" olan tamirat teslim edilemez! Önce tamirat ücretini belirleyin (Düzenle).');
      return;
    }

    const wasOnSite = !selectedRepairDetails.statusHistory?.some(h => h.new_status === 'sent_to_workshop');

    const remainingAmount = repair.price_type === 'free' ? 0 : (Number(repair.estimated_price) || 0) - (Number(repair.deposit_amount) || 0);

    // Kalan tutar 0 veya negatifse direkt teslim akışına git (ücretsiz ise de direkt teslim)
    if (remainingAmount <= 0) {
      // Ödeme modalı bu yolda açılmadığından, önceki bir işlemden (ör. kapora) kalan
      // ödeme satırlarının teslime sızmaması için listeyi sıfırla ve teslimi boş ödemeyle yap.
      setRepairPayments([]);
      if (wasOnSite) {
        completeDelivery(repair, true, []);
      } else {
        setDeliverConfirmCode('');
        setDeliverConfirmError('');
        setShowDeliverConfirmModal(true);
      }
      return;
    }

    // Ödeme modalını aç
    setRepairPaymentMode('delivery');
    setRepairPaymentRepair(repair);
    setRepairPaymentIsOnSite(wasOnSite);
    setRepairPaymentMethod('');
    setRepairPaymentAmount('');
    setRepairPayments([]);
    setSelectedRepairBankAccount(null);
    fetchRepairBankAccounts();
    setShowRepairPaymentModal(true);
  };

  // Banka hesaplarını yükle
  const fetchRepairBankAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/bank-accounts?active_only=true`);
      const data = await response.json();
      if (data.success) setRepairBankAccounts(data.accounts || []);
    } catch (e) {}
  };

  // Ödeme ekle
  const addRepairPayment = (methodOverride, bankAccountOverride) => {
    const amount = parseFloat(repairPaymentAmount);
    if (!amount || amount <= 0) {
      toast.warning('Lütfen tutar girin!');
      return;
    }
    const method = methodOverride || repairPaymentMethod;
    if (!method) {
      toast.warning('Lütfen ödeme yöntemi seçin!');
      return;
    }
    const bankAccount = bankAccountOverride || selectedRepairBankAccount;
    if (method === 'Havale/EFT' && !bankAccount) {
      toast.warning('Lütfen banka hesabı seçin!');
      return;
    }

    // Alınan ücret toplam tamirat ücretini geçemez (hem teslim hem kapora modunda)
    let cappedAmount = amount;
    if (repairPaymentMode === 'deposit') {
      const total = getFormNetPrice();
      const remaining = Math.max(0, total - getRepairPaymentsTotal());
      if (remaining <= 0) {
        toast.warning('Alınan ücret toplam tamirat ücretine ulaştı!');
        return;
      }
      cappedAmount = Math.min(amount, remaining);
    } else {
      const remaining = getRepairRemainingAmount();
      cappedAmount = Math.min(amount, remaining);
    }
    if (cappedAmount < amount) {
      toast.info('Alınan ücret toplam tamirat ücretini geçemez, tutar sınırlandı.');
    }

    const payment = {
      id: Date.now(),
      method,
      amount: cappedAmount
    };
    if (repairPaymentMode === 'deposit') {
      // Teslim edilmiş tamiratta düzenleme ile eklenen ödeme, teslim ödemesinin düzeltmesidir
      payment.source = editingRepair?.status === 'delivered' ? 'delivery' : 'deposit';
    }
    if (method === 'Havale/EFT' && bankAccount) {
      payment.bankAccount = bankAccount;
    }

    setRepairPayments(prev => [...prev, payment]);
    setRepairPaymentAmount('');
    setRepairPaymentMethod(method);
    setSelectedRepairBankAccount(null);
  };

  // Ödeme sil
  const removeRepairPayment = (id) => {
    setRepairPayments(prev => prev.filter(p => p.id !== id));
  };

  // Toplam ödenen
  const getRepairPaymentsTotal = () => {
    return repairPayments.reduce((sum, p) => sum + p.amount, 0);
  };

  // Kalan tutar
  const getRepairRemainingAmount = () => {
    if (!repairPaymentRepair) return 0;
    if (repairPaymentRepair.price_type === 'free' || repairPaymentRepair.price_type === 'pending') return 0;
    const total = (Number(repairPaymentRepair.estimated_price) || 0) - (Number(repairPaymentRepair.deposit_amount) || 0);
    return Math.max(0, total - getRepairPaymentsTotal());
  };

  // Kapora ödeme modalını aç (yeni veya düzenleme modunda)
  const openDepositPaymentModal = () => {
    if (!(getFormNetPrice() > 0)) {
      toast.warning('Önce tamirat ücretini girin!');
      return;
    }
    setRepairPaymentMode('deposit');
    setRepairPaymentRepair(null);
    setRepairPaymentIsOnSite(false);
    setRepairPaymentMethod('');
    setRepairPaymentAmount('');
    // Mevcut ödemeleri modala yükle: teslimden önce alınanlar (deposit) + teslimde alınanlar
    // (delivery). Her satır kaynağını (source) taşır; kaydederken ait olduğu alana geri yazılır.
    const toRow = (source) => (p, idx) => ({
      id: p.id || (Date.now() + idx + (source === 'delivery' ? 1000 : 0)),
      method: p.method,
      amount: Number(p.amount) || 0,
      source,
      bankAccount: p.bank_account ? { id: p.bank_account_id, account_name: p.bank_account, account_type: p.bank_account_type || 'personal' } : undefined
    });
    const existing = [
      ...(formData.deposit_payment_details || []).map(toRow('deposit')),
      ...(formData.delivery_payment_details || []).map(toRow('delivery'))
    ];
    setRepairPayments(existing);
    depositModalInitialIdsRef.current = existing.map(p => p.id);
    setSelectedRepairBankAccount(null);
    fetchRepairBankAccounts();
    setShowRepairPaymentModal(true);
  };

  // Düzenleme modundaki (kayıtlı tamirat) ödeme satırlarını ANINDA kaydeder — sadece ödeme
  // alanları (payments_only). Eskiden ödeme ekleme/silme yalnızca forma yazılıyor ve Güncelle'ye
  // bağlıydı; fiş çıktıktan sonra modal X / İptal ile kapatılınca alınan ödeme kayboluyordu.
  const persistEditPayments = async (rows) => {
    const toDetail = (p) => ({
      method: p.method,
      amount: p.amount,
      ...(p.bankAccount ? { bank_account: p.bankAccount.account_name, bank_account_type: p.bankAccount.account_type } : {})
    });
    const methodOf = (arr) => arr.length === 1 ? arr[0].method : (arr.length > 1 ? 'Karışık' : null);
    const depositRows = rows.filter(p => p.source !== 'delivery');
    const deliveryRows = rows.filter(p => p.source === 'delivery');
    const result = {
      depositTotal: depositRows.reduce((sum, p) => sum + p.amount, 0),
      deliveryTotal: deliveryRows.reduce((sum, p) => sum + p.amount, 0),
      primaryMethod: methodOf(depositRows),
      deliveryPrimaryMethod: methodOf(deliveryRows),
      details: depositRows.map(toDetail),
      deliveryDetails: deliveryRows.map(toDetail)
    };

    if (editingRepair) {
      setDepositPaymentSaving(true);
      try {
        const response = await fetch(`${API_URL}/repairs/${editingRepair.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payments_only: true,
            deposit_amount: result.depositTotal,
            deposit_payment_method: result.primaryMethod,
            deposit_payment_details: result.details,
            delivery_payment_details: result.deliveryDetails
          })
        });
        const data = await response.json();
        if (!data.success) {
          toast.error(data.error || 'Ödeme kaydedilirken hata oluştu');
          return null;
        }
      } catch (error) {
        console.error('Tamirat ödeme kayıt hatası:', error);
        toast.error('Ödeme kaydedilirken hata oluştu');
        return null;
      } finally {
        setDepositPaymentSaving(false);
      }
      const grandTotal = result.depositTotal + result.deliveryTotal;
      logService.log('repairs', 'repair_payment', `Tamirat ödemesi ${grandTotal > 0 ? 'güncellendi' : 'silindi'} (düzenleme) - ${editingRepair.customer_name} (₺${grandTotal.toLocaleString('tr-TR')})`, {
        entity_type: 'repair',
        entity_id: editingRepair.id,
        entity_code: editingRepair.repair_code,
        details: {
          musteri: editingRepair.customer_name,
          fis_no: editingRepair.repair_code,
          odemeler: rows.map(p => ({ yontem: p.method, tutar: p.amount }))
        },
        status: 'success'
      });
      setEditingRepair(prev => prev ? ({
        ...prev,
        deposit_amount: result.depositTotal,
        deposit_payment_method: result.primaryMethod,
        deposit_payment_details: result.details,
        ...(prev.status === 'delivered' ? { payment_amount: result.deliveryTotal, payment_details: result.deliveryDetails, payment_method: result.deliveryPrimaryMethod } : {})
      }) : prev);
      fetchRepairs();
    }

    setFormData(prev => ({
      ...prev,
      deposit_amount: result.depositTotal > 0 ? result.depositTotal.toFixed(2) : '',
      deposit_payment_method: result.primaryMethod,
      deposit_payment_details: result.details,
      delivery_payment_details: result.deliveryDetails
    }));
    return result;
  };

  // Ücret alımını tamamla → formData'ya yaz (kayıtlı tamiratta anında kaydedilir)
  const completeDepositPayment = async () => {
    if (depositPaymentSaving) return;
    const rows = repairPayments;
    const total = rows.reduce((sum, p) => sum + p.amount, 0);
    const currentIds = new Set(rows.map(p => p.id));
    const removedCount = editingRepair
      ? depositModalInitialIdsRef.current.filter(id => !currentIds.has(id)).length
      : 0;
    if (total <= 0 && removedCount === 0) {
      toast.warning('Lütfen en az bir ödeme ekleyin!');
      return;
    }

    const doSave = async () => {
      const result = await persistEditPayments(rows);
      if (!result) return;
      setShowRepairPaymentModal(false);
      setRepairPaymentMode('delivery');
      // Ücret satırlarının sonraki bir teslime sızmaması için ödeme listesini temizle.
      setRepairPayments([]);
      if (!editingRepair) return;
      if (total <= 0) {
        toast.success('Alınan ücret silindi!');
        return;
      }
      toast.success('Ödeme kaydedildi!');

      // Mevcut (kayıtlı) bir tamirata ücret alındıysa Tamirat Tahsilat Fişi yazdırma sorusu.
      // Yeni tamirat oluştururken fiş kodu henüz yok; kayıt sonrası Müşteri/Tamirat fişleri
      // zaten otomatik yazdırılıyor.
      const receiptRepair = {
        ...editingRepair,
        customer_name: formData.customer_name || editingRepair.customer_name,
        customer_phone: formData.customer_phone || editingRepair.customer_phone,
        items: getFormPricing().items,
        pricing: buildPricingMeta(getFormPricing()),
        notes: formData.notes,
        staff_name: formData.staff_name || editingRepair.staff_name,
        estimated_price: getFormNetPrice(),
        deposit_amount: result.depositTotal,
        deposit_payment_details: result.details,
        deposit_payment_method: result.primaryMethod,
        payment_amount: result.deliveryTotal,
        payment_details: result.deliveryDetails,
        payment_method: result.deliveryPrimaryMethod
      };
      showConfirmation({
        title: 'Tamirat Tahsilat Fişi Yazdır',
        message: 'Tamirat tahsilat fişi kaç adet yazdırılsın?',
        confirmText: 'Yazdır (2 Adet)',
        cancelText: 'Kapat (1 Adet)',
        type: 'info',
        onConfirm: () => {
          sendRepairPaymentReceipt(receiptRepair);
          setTimeout(() => sendRepairPaymentReceipt(receiptRepair), 1500);
        },
        onCancel: () => {
          sendRepairPaymentReceipt(receiptRepair);
        }
      });
    };

    // Kayıtlı tamiratta mevcut ödeme satırı silindiyse önce onay al (silme anında kaydedilir).
    if (removedCount > 0) {
      showConfirmation({
        title: 'Ödeme Silinecek',
        message: total <= 0
          ? 'Alınan ücretin tamamı silinecek. Onaylıyor musunuz?'
          : `${removedCount} adet ödeme silinecek. Onaylıyor musunuz?`,
        confirmText: 'Evet, Sil',
        cancelText: 'Vazgeç',
        type: 'warning',
        onConfirm: doSave
      });
      return;
    }
    await doSave();
  };

  // Alınan ücreti temizle (kayıtlı tamiratta onay alınıp anında kaydedilir)
  const clearDepositPayment = () => {
    if (!editingRepair) {
      setFormData(prev => ({
        ...prev,
        deposit_amount: '',
        deposit_payment_method: '',
        deposit_payment_details: [],
        delivery_payment_details: []
      }));
      return;
    }
    showConfirmation({
      title: 'Ödeme Silinecek',
      message: 'Alınan ücretin tamamı silinecek. Onaylıyor musunuz?',
      confirmText: 'Evet, Sil',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        const result = await persistEditPayments([]);
        if (result) toast.success('Alınan ücret silindi!');
      }
    });
  };

  // Tamiratın herhangi bir aşamasında (teslimden önce) ödeme almak için modalı aç.
  // Sadece intake'te (Ücret Al) veya teslimde değil, kayıtlı bir tamirata istenildiği an
  // buradan tekrar tekrar ödeme eklenebilir. "Kapora" kavramı yok — doğrudan tamirat
  // ödemesi/ücreti olarak backend'e (deposit_amount/deposit_payment_details üzerine
  // EKLENEREK) yazılır, durum değişmez.
  const openAnytimeRepairPaymentModal = (repair) => {
    if (!repair) return;
    if (repair.price_type !== 'normal' || !(Number(repair.estimated_price) > 0)) {
      toast.warning('Bu tamirat için ücret tanımlanmamış!');
      return;
    }
    const remaining = Math.max(0, Number(repair.estimated_price) - Number(repair.deposit_amount || 0));
    if (remaining <= 0.01) {
      toast.info('Tamirat ücreti zaten tamamen ödenmiş.');
      return;
    }
    setRepairPaymentMode('topup');
    setRepairPaymentRepair(repair);
    setRepairPaymentIsOnSite(false);
    setRepairPaymentMethod('');
    setRepairPaymentAmount('');
    setRepairPayments([]);
    setSelectedRepairBankAccount(null);
    fetchRepairBankAccounts();
    setShowRepairPaymentModal(true);
  };

  // Anlık (teslimden bağımsız) ödemeyi kaydet — durumu değiştirmez, mevcut alınan ücretin
  // üzerine ekler. Backend her seferinde tek bir mutabakat kaydı üretir (idempotent değil,
  // her ödeme kendi kalemi olarak eklenir).
  const completeAnytimeRepairPayment = async () => {
    if (!repairPaymentRepair) return;
    const total = repairPayments.reduce((sum, p) => sum + p.amount, 0);
    if (total <= 0) {
      toast.warning('Lütfen en az bir ödeme ekleyin!');
      return;
    }
    const details = repairPayments.map(p => ({
      method: p.method,
      amount: p.amount,
      ...(p.bankAccount ? { bank_account: p.bankAccount.account_name, bank_account_type: p.bankAccount.account_type } : {})
    }));
    try {
      const response = await fetch(`${API_URL}/repairs/${repairPaymentRepair.id}/payment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments: details,
          staff_name: user?.name || user?.username || ''
        })
      });
      const data = await response.json();
      if (data.success) {
        logService.log('repairs', 'repair_payment', `Tamirat ödemesi alındı - ${repairPaymentRepair.customer_name} (₺${total.toLocaleString('tr-TR')})`, {
          entity_type: 'repair',
          entity_id: repairPaymentRepair.id,
          entity_code: repairPaymentRepair.repair_code,
          details: {
            musteri: repairPaymentRepair.customer_name,
            fis_no: repairPaymentRepair.repair_code,
            odemeler: repairPayments.map(p => ({ yontem: p.method, tutar: p.amount }))
          },
          status: 'success'
        });
        toast.success('Tamirat ödemesi alındı!');
        setShowRepairPaymentModal(false);
        setRepairPaymentMode('delivery');

        const updatedRepair = {
          ...repairPaymentRepair,
          deposit_amount: data.deposit_amount,
          deposit_payment_method: data.deposit_payment_method,
          deposit_payment_details: data.deposit_payment_details,
          deposit_taken_at: data.deposit_taken_at
        };
        setSelectedRepairDetails(prev => prev ? ({ ...prev, repair: { ...prev.repair, ...updatedRepair } }) : prev);
        fetchRepairs();

        // Tamirat Tahsilat Fişi yazdırma sorusu
        showConfirmation({
          title: 'Tamirat Tahsilat Fişi Yazdır',
          message: 'Tamirat tahsilat fişi kaç adet yazdırılsın?',
          confirmText: 'Yazdır (2 Adet)',
          cancelText: 'Kapat (1 Adet)',
          type: 'info',
          onConfirm: () => {
            sendRepairPaymentReceipt(updatedRepair);
            setTimeout(() => sendRepairPaymentReceipt(updatedRepair), 1500);
          },
          onCancel: () => {
            sendRepairPaymentReceipt(updatedRepair);
          }
        });
        setRepairPayments([]);
      } else {
        toast.error(data.error || 'Ödeme kaydedilirken hata oluştu');
      }
    } catch (error) {
      console.error('Tamirat ödeme hatası:', error);
      toast.error('Ödeme kaydedilirken hata oluştu');
    }
  };

  // Ödeme tamamlandıktan sonra teslim et
  const completeRepairPaymentAndDeliver = async () => {
    if (!repairPaymentRepair) return;
    const remaining = getRepairRemainingAmount();
    if (remaining > 0) {
      toast.warning('Kalan tutar ödenmeden teslim edilemez!');
      return;
    }

    if (repairPaymentIsOnSite) {
      // Yerinde tamirat - direkt teslim
      await completeDelivery(repairPaymentRepair, true);
      setShowRepairPaymentModal(false);
    } else {
      // Normal tamirat - şifre doğrulama
      setShowRepairPaymentModal(false);
      setDeliverConfirmCode('');
      setDeliverConfirmError('');
      setShowDeliverConfirmModal(true);
    }
  };

  // Teslim işlemini tamamla
  // paymentsArg: teslimde alınan ödeme satırları. Verilmezse repairPayments state'i kullanılır.
  // Ücretsiz/direkt teslimde boş dizi ([]) geçilerek önceki işlemden kalan satırların sızması önlenir.
  const completeDelivery = async (repair, isOnSite = false, paymentsArg = null) => {
    try {
      const payments = paymentsArg !== null ? paymentsArg : repairPayments;
      // Ödeme bilgilerini yapılandır
      const totalPayment = payments.reduce((sum, p) => sum + p.amount, 0);
      const primaryMethod = payments.length === 1 ? payments[0].method : (payments.length > 1 ? 'Karışık' : null);

      const response = await fetch(`${API_URL}/repairs/${repair.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'delivered',
          notes: payments.length > 0 ? `Ödeme: ${payments.map(p => `${p.method} ₺${p.amount.toLocaleString('tr-TR')}`).join(', ')}` : undefined,
          payment_method: primaryMethod,
          payment_amount: totalPayment,
          payment_details: payments.length > 0 ? payments.map(p => ({ method: p.method, amount: p.amount, ...(p.bankAccount ? { bank_account: p.bankAccount.account_name, bank_account_type: p.bankAccount.account_type } : {}) })) : null,
          staff_name: user?.name || user?.username || ''
        })
      });
      const data = await response.json();
      if (data.success) {
        logService.log('repairs', 'repair_delivered', `${isOnSite ? 'Yerinde tamirat - ' : ''}Ürün müşteriye teslim edildi - ${repair.customer_name}`, {
          entity_type: 'repair',
          entity_id: repair.id,
          entity_code: repair.repair_code,
          details: {
            musteri: repair.customer_name,
            urun: repair.item_description,
            fis_no: repair.repair_code,
            yerinde_tamirat: isOnSite,
            odemeler: payments.length > 0 ? payments.map(p => ({ yontem: p.method, tutar: p.amount })) : undefined
          },
          status: 'success'
        });
        toast.success('Ürün müşteriye teslim edildi!');
        setShowDetailModal(false);
        setRepairPayments([]);
        fetchRepairs();

        // Teslimde ödeme alındıysa Tamirat Tahsilat Fişi yazdırma sorusu
        if (totalPayment > 0) {
          const receiptRepair = {
            ...repair,
            payment_amount: totalPayment,
            payment_details: payments.map(p => ({ method: p.method, amount: p.amount, ...(p.bankAccount ? { bank_account: p.bankAccount.account_name } : {}) })),
            payment_method: primaryMethod
          };
          showConfirmation({
            title: 'Tamirat Tahsilat Fişi Yazdır',
            message: 'Tamirat tahsilat fişi kaç adet yazdırılsın?',
            confirmText: 'Yazdır (2 Adet)',
            cancelText: 'Kapat (1 Adet)',
            type: 'info',
            onConfirm: () => {
              sendRepairPaymentReceipt(receiptRepair);
              setTimeout(() => sendRepairPaymentReceipt(receiptRepair), 1500);
            },
            onCancel: () => {
              sendRepairPaymentReceipt(receiptRepair);
            }
          });
        }
      } else {
        toast.error(data.error || 'Teslim sırasında hata oluştu');
      }
    } catch (error) {
      console.error('Teslim hatası:', error);
      toast.error('Teslim sırasında hata oluştu');
    }
  };

  // Fiş kodu doğrulayıp teslim et
  const confirmDeliverToCustomer = async () => {
    if (!selectedRepairDetails?.repair) return;

    const repair = selectedRepairDetails.repair;
    const enteredCode = deliverConfirmCode.trim();

    // Önce fiş kodu kontrolü (yerel)
    const codeMatch = enteredCode === repair.repair_code;
    let overrideMatch = false;

    // Fiş kodu eşleşmiyorsa ve override aktifse şifreyi sunucudan doğrula
    if (!codeMatch && repairOverrideEnabled && enteredCode) {
      try {
        const verifyRes = await fetch(`${API_URL}/settings/repair-deliver-override/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: enteredCode })
        });
        const verifyData = await verifyRes.json();
        overrideMatch = !!verifyData.valid;
      } catch (error) {
        console.error('Override doğrulama hatası:', error);
      }
    }

    if (!codeMatch && !overrideMatch) {
      setDeliverConfirmError(repairOverrideEnabled ? 'Fiş kodu veya yönetici şifresi hatalı!' : 'Fiş kodu hatalı! Lütfen doğru kodu girin.');
      return;
    }

    await completeDelivery(repair, false);
    setShowDeliverConfirmModal(false);
  };

  // Atölye çıkış - mağazaya gönder
  const workshopDispatchToStore = async () => {
    if (!workshopDispatchRepair) return;
    if (!workshopDispatchNote.trim()) {
      toast.warning('Çıkış için not girmek zorunludur!');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/repairs/${workshopDispatchRepair.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'returning_to_store',
          repair_not_done: repairNotDone,
          workshop_note: workshopDispatchNote.trim()
        })
      });
      const data = await response.json();
      if (data.success) {
        const message = repairNotDone
          ? `Ürün (TAMİRAT YAPILMADI) ${getStoreName(workshopDispatchRepair.store_id)} mağazasına gönderildi!`
          : `Ürün ${getStoreName(workshopDispatchRepair.store_id)} mağazasına gönderildi!`;
        toast.success(message);
        setShowWorkshopDispatchModal(false);
        setWorkshopDispatchRepair(null);
        setRepairNotDone(false);
        setWorkshopDispatchNote('');
        fetchRepairs();
      } else {
        toast.error(data.error || 'Hata oluştu!');
      }
    } catch (error) {
      toast.error('Sunucu hatası!');
    }
  };

  // Tamamlanan tamiratlar (atölye paneli için)
  const completedWorkshopRepairs = repairs.filter(r => r.status === 'completed');

  // Kullanıcı yetkisi kontrolü (admin veya gümüş mağazaları - altın hariç)
  const canAccessWorkshopPanel = () => {
    if (user?.role === 'admin') return true;
    // Gümüş mağazaları (altın mağazası store_id=1 hariç herkes)
    if (!isGoldStore) return true;
    return false;
  };

  const handleDelete = async (repair) => {
    showConfirmation({
      title: 'Tamirat Kaydını Sil',
      message: 'Bu tamirat kaydını silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/repairs/${repair.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const data = await response.json();
          if (data.success) {
            logService.log('repairs', 'delete', `Tamirat kaydı silindi - ${repair.customer_name}`, {
              entity_type: 'repair',
              entity_id: repair.id,
              entity_code: repair.repair_code,
              details: {
                musteri: repair.customer_name,
                urun: repair.item_description,
                fis_no: repair.repair_code
              },
              status: 'success'
            });
            toast.success('Tamirat kaydı silindi');
            fetchRepairs();
          } else {
            toast.error(data.message || 'Tamirat silinemedi');
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Silme işlemi başarısız');
          logService.log('repairs', 'delete', 'Tamirat silme hatası', { status: 'error', details: { error: error.message } });
        }
      }
    });
  };

  // Formdaki fiyatlandırmanın hesaplanmış hali (net toplam, ürün payları, indirimler)
  const getFormPricing = (fd = formData) => computeRepairPricing({
    mode: fd.pricing_mode,
    items: fd.items,
    receiptPriceType: fd.price_type,
    receiptPrice: fd.estimated_price,
    discountType: fd.discount_type,
    discountValue: fd.discount_value
  });

  // Formdaki net tamirat ücreti (indirim sonrası) — ödeme alırken üst sınır
  const getFormNetPrice = () => {
    const pr = getFormPricing();
    return pr.price_type === 'normal' ? pr.total : 0;
  };

  // repairs.pricing kolonuna yazılacak özet
  const buildPricingMeta = (pr, fd = formData) => ({
    mode: fd.pricing_mode === 'item' ? 'item' : 'receipt',
    subtotal: pr.subtotal,
    item_discount_total: pr.item_discount_total,
    discount_type: pr.discount_amount > 0 ? (fd.discount_type || 'amount') : null,
    discount_value: pr.discount_amount > 0 ? Number(fd.discount_value) || 0 : null,
    discount_amount: pr.discount_amount,
    total: pr.total
  });

  const openEditModal = (repair) => {
    // Müşteriye teslim edilmiş tamiratı yalnızca admin düzenleyebilir
    if (repair.status === 'delivered' && user?.role !== 'admin') {
      toast.warning('Teslim edilmiş tamiratı yalnızca yönetici düzenleyebilir.');
      return;
    }
    setEditingRepair(repair);
    setCustomerSearch('');
    setPhoneMatchCustomers([]);
    setCustomerSelectedFromMatch(!!repair.customer_id);
    setShowCustomerDropdown(false);
    setRepairCustomerTab('new');

    // items alanını parse et (JSON string olabilir)
    let items = [];
    if (repair.items) {
      try {
        items = typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items;
      } catch (e) {
        items = [];
      }
    }

    // Eğer items boşsa, legacy alanlardan oluştur
    if (!items || items.length === 0) {
      let photos = [];
      if (repair.photos) {
        try {
          photos = typeof repair.photos === 'string' ? JSON.parse(repair.photos) : repair.photos;
        } catch (e) {
          photos = [];
        }
      }
      items = [{
        item_type: repair.item_type || 'gold',
        item_description: repair.item_description || '',
        problem_description: repair.problem_description || '',
        photos: photos
      }];
    }

    // Kapora ödeme detaylarını parse et (JSON string ya da array gelebilir)
    let depositPaymentDetails = [];
    if (repair.deposit_payment_details) {
      try {
        depositPaymentDetails = typeof repair.deposit_payment_details === 'string'
          ? JSON.parse(repair.deposit_payment_details)
          : repair.deposit_payment_details;
        if (!Array.isArray(depositPaymentDetails)) depositPaymentDetails = [];
      } catch (e) {
        depositPaymentDetails = [];
      }
    }

    // Teslimde alınan ödeme (payment_details) — teslim edilmiş tamiratlarda para genelde burada
    // durur. Düzenleme formu eskiden sadece deposit_* alanını gösterdiği için teslimde alınan
    // ücret formda "boş" görünüyor, düzeltme yapılamıyor ve üstüne ikinci ödeme eklenebiliyordu.
    let deliveryPaymentDetails = [];
    if (repair.status === 'delivered' && repair.payment_details) {
      try {
        deliveryPaymentDetails = typeof repair.payment_details === 'string'
          ? JSON.parse(repair.payment_details)
          : repair.payment_details;
        if (!Array.isArray(deliveryPaymentDetails)) deliveryPaymentDetails = [];
      } catch (e) {
        deliveryPaymentDetails = [];
      }
    }

    // Fiyatlandırma: pricing yoksa (eski kayıt) fiş bazlı + indirimsiz kabul edilir.
    // Fiş bazlıda formdaki ücret = indirim ÖNCESİ tutar (pricing.subtotal); estimated_price nettir.
    const pricing = parseRepairPricing(repair);
    const pricingMode = pricing?.mode === 'item' ? 'item' : 'receipt';
    const receiptGross = pricingMode === 'receipt' && Number(pricing?.subtotal) > 0 ? Number(pricing.subtotal) : Number(repair.estimated_price);

    setFormData({
      customer_id: repair.customer_id || '',
      customer_name: repair.customer_name || '',
      customer_phone: repair.customer_phone || '',
      items: items.map(itemPricingToForm),
      estimated_price: receiptGross > 0 ? receiptGross : '',
      price_type: pricingMode === 'receipt' && receiptGross > 0 ? 'normal' : (repair.price_type || 'normal'),
      pricing_mode: pricingMode,
      discount_type: pricing?.discount_type || 'amount',
      discount_value: Number(pricing?.discount_value) > 0 ? String(pricing.discount_value) : '',
      deposit_amount: Number(repair.deposit_amount) > 0 ? repair.deposit_amount : '',
      deposit_payment_method: repair.deposit_payment_method || '',
      deposit_payment_details: depositPaymentDetails,
      delivery_payment_details: deliveryPaymentDetails,
      estimated_delivery: repair.estimated_delivery ? repair.estimated_delivery.split('T')[0] : '',
      deadline: repair.deadline ? repair.deadline.split('T')[0] : '',
      notes: repair.notes || '',
      staff_name: repair.staff_name || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRepair(null);
    setCustomerSearch('');
    setPhoneMatchCustomers([]);
    setCustomerSelectedFromMatch(false);
    setShowCustomerDropdown(false);
    setRepairCustomerTab('search');
    setFormData({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      items: [{
        item_type: storeMetalType,
        item_description: '',
        problem_description: '',
        photos: [],
        price_type: 'normal',
        price: '',
        discount_type: 'amount',
        discount_value: ''
      }],
      estimated_price: '',
      price_type: 'normal',
      pricing_mode: 'receipt',
      discount_type: 'amount',
      discount_value: '',
      deposit_amount: '',
      deposit_payment_method: '',
      deposit_payment_details: [],
      delivery_payment_details: [],
      estimated_delivery: '',
      deadline: '',
      notes: '',
      staff_name: ''
    });
  };

  // Ürün ekleme
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item_type: storeMetalType,
        item_description: '',
        problem_description: '',
        photos: [],
        price_type: 'normal',
        price: '',
        discount_type: 'amount',
        discount_value: ''
      }]
    }));
  };

  // Ürün silme
  const removeItem = (index) => {
    if (formData.items.length <= 1) {
      toast.warning('En az bir ürün olmalı!');
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Ürün güncelleme
  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Fotoğraf yükleme (ürün bazlı)
  const handlePhotoUpload = async (e, itemIndex) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const formDataUpload = new FormData();

    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('photos', files[i]);
    }

    try {
      const response = await fetch(`${API_URL}/repairs/upload-photos`, {
        method: 'POST',
        body: formDataUpload
      });

      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          items: prev.items.map((item, i) =>
            i === itemIndex ? { ...item, photos: [...item.photos, ...data.photos] } : item
          )
        }));
      }
    } catch (error) {
      console.error('Fotoğraf yükleme hatası:', error);
      toast.error('Fotoğraf yüklenemedi!');
    } finally {
      setUploading(false);
      // Input'u temizle
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  // Fotoğraf silme (ürün bazlı)
  const removePhoto = (itemIndex, photoIndex) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === itemIndex ? { ...item, photos: item.photos.filter((_, pi) => pi !== photoIndex) } : item
      )
    }));
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const selectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone || ''
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'received': { label: 'Teslim Alındı', class: 'status-received' },
      'sent_to_workshop': { label: 'Atölyeye Gönderildi', class: 'status-workshop' },
      'at_workshop': { label: 'Atölyede', class: 'status-workshop' },
      'in_progress': { label: 'Tamirata Başlandı', class: 'status-progress' },
      'completed': { label: 'Tamirat Tamamlandı', class: 'status-completed' },
      'returning_to_store': { label: 'Mağazaya Yolda', class: 'status-returning' },
      'ready_for_pickup': { label: 'Tamirattan Geldi', class: 'status-ready' },
      'delivered': { label: 'Teslim Edildi', class: 'status-delivered' }
    };
    const s = statusMap[status] || statusMap['received'];
    return <span className={`status-badge ${s.class}`}>{s.label}</span>;
  };

  const getItemTypeBadge = (type) => {
    return type === 'gold'
      ? <span className="item-type-badge gold">Altın</span>
      : <span className="item-type-badge silver">Gümüş</span>;
  };

  // Teslim için hazır ürünler (returning_to_store durumunda olanlar) — şube transferi ile gelenler de dahil
  const readyForDeliverRepairs = repairs.filter(r => {
    const effectiveStoreId = r.transferred_to_store_id || r.store_id;
    return r.status === 'returning_to_store' && (effectiveStoreId === currentStoreId || !effectiveStoreId);
  });

  // Barkod ile ürün teslim alma
  const handleDeliverBarcodeSubmit = (e) => {
    e.preventDefault();
    const code = deliverBarcodeInput.trim();
    if (!code) return;

    const foundRepair = readyForDeliverRepairs.find(r => r.repair_code === code);

    if (foundRepair) {
      if (selectedForDeliver.includes(foundRepair.id)) {
        toast.info('Bu ürün zaten seçili!');
      } else {
        setSelectedForDeliver(prev => [...prev, foundRepair.id]);
        toast.success(`Ürün eklendi: ${foundRepair.customer_name}`);
      }
    } else {
      toast.error('Barkod bulunamadı veya bu mağazaya ait değil!');
    }

    setDeliverBarcodeInput('');
    deliverBarcodeInputRef.current?.focus();
  };

  // Checkbox ile seçim
  const toggleDeliverSelection = (repairId) => {
    setSelectedForDeliver(prev =>
      prev.includes(repairId)
        ? prev.filter(id => id !== repairId)
        : [...prev, repairId]
    );
  };

  // Tümünü seç/kaldır
  const toggleDeliverSelectAll = () => {
    if (selectedForDeliver.length === readyForDeliverRepairs.length) {
      setSelectedForDeliver([]);
    } else {
      setSelectedForDeliver(readyForDeliverRepairs.map(r => r.id));
    }
  };

  // Seçili ürünleri teslim et
  const deliverSelectedProducts = async () => {
    if (selectedForDeliver.length === 0) {
      toast.warning('Lütfen en az bir ürün seçin!');
      return;
    }
    if (!deliverStaffName) {
      toast.warning('Lütfen teslim alan kasiyeri seçin!');
      return;
    }

    try {
      let successCount = 0;
      for (const repairId of selectedForDeliver) {
        const response = await fetch(`${API_URL}/repairs/${repairId}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'ready_for_pickup', notes: `Toplu teslim alındı - Teslim alan: ${deliverStaffName}` })
        });
        const data = await response.json();
        if (data.success) successCount++;
      }

      toast.success(`${successCount} ürün teslim edildi! Teslim alan: ${deliverStaffName}`);
      setShowDeliverModal(false);
      setSelectedForDeliver([]);
      setDeliverStaffName('');
      fetchRepairs();
    } catch (error) {
      toast.error('Hata oluştu!');
    }
  };

  // Modal açıldığında barkod inputuna focus + kasiyer seçimini sıfırla
  useEffect(() => {
    if (showDeliverModal && deliverBarcodeInputRef.current) {
      setTimeout(() => deliverBarcodeInputRef.current?.focus(), 100);
      setDeliverStaffName('');
    }
  }, [showDeliverModal]);

  // ====== TOPLU TAMİRAT ÇIKIŞI ======
  // Tamirata henüz gönderilmemiş, bu mağazaya ait teslim alınmış ürünler
  const receivedRepairs = repairs.filter(r =>
    r.status === 'received' &&
    (r.store_id === currentStoreId || !r.store_id)
  );

  const handleBulkDispatchBarcodeSubmit = (e) => {
    e.preventDefault();
    const code = bulkDispatchBarcodeInput.trim();
    if (!code) return;
    const found = receivedRepairs.find(r => r.repair_code === code);
    if (found) {
      if (bulkDispatchSelected.includes(found.id)) {
        toast.info('Bu ürün zaten seçili!');
      } else {
        setBulkDispatchSelected(prev => [...prev, found.id]);
        toast.success(`Ürün eklendi: ${found.customer_name}`);
      }
    } else {
      toast.error('Barkod bulunamadı veya bu mağazaya ait gönderim bekleyen ürün değil!');
    }
    setBulkDispatchBarcodeInput('');
    bulkDispatchBarcodeInputRef.current?.focus();
  };

  const toggleBulkDispatchSelection = (repairId) => {
    setBulkDispatchSelected(prev =>
      prev.includes(repairId)
        ? prev.filter(id => id !== repairId)
        : [...prev, repairId]
    );
  };

  const toggleBulkDispatchSelectAll = () => {
    if (bulkDispatchSelected.length === receivedRepairs.length) {
      setBulkDispatchSelected([]);
    } else {
      setBulkDispatchSelected(receivedRepairs.map(r => r.id));
    }
  };

  const dispatchSelectedProducts = async () => {
    if (bulkDispatchSelected.length === 0) {
      toast.warning('Lütfen en az bir ürün seçin!');
      return;
    }
    if (!bulkDispatchPointId) {
      toast.warning('Lütfen tamirat noktası seçin!');
      return;
    }
    setBulkDispatching(true);
    try {
      let successCount = 0;
      for (const repairId of bulkDispatchSelected) {
        const response = await fetch(`${API_URL}/repairs/${repairId}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'sent_to_workshop', repair_point_id: Number(bulkDispatchPointId) })
        });
        const data = await response.json();
        if (data.success) successCount++;
      }
      const point = repairPoints.find(rp => rp.id === Number(bulkDispatchPointId));
      toast.success(`${successCount} ürün "${point?.name || 'tamirat noktası'}"na gönderildi!`);
      setShowBulkDispatchModal(false);
      setBulkDispatchSelected([]);
      setBulkDispatchPointId('');
      fetchRepairs();
    } catch (error) {
      toast.error('Hata oluştu!');
    } finally {
      setBulkDispatching(false);
    }
  };

  useEffect(() => {
    if (showBulkDispatchModal && bulkDispatchBarcodeInputRef.current) {
      setTimeout(() => bulkDispatchBarcodeInputRef.current?.focus(), 100);
    }
  }, [showBulkDispatchModal]);

  // İşlemler dropdown — dışarı tıklama ile kapanır
  useEffect(() => {
    if (!showActionsMenu) return;
    const handler = (e) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showActionsMenu]);

  // Mağaza eşleştirme helper — store_id bazlı (Osmangazi ve Yeşilsu ayrı mağazalar)
  const matchesStoreFilter = (repair) => {
    const isAdmin = user?.role === 'admin';
    // Admin değilse sadece kendi mağazasını görsün
    const activeFilter = isAdmin ? filterStore : 'current';

    if (activeFilter === 'all') return true;

    const filterStoreId = activeFilter === 'gold' ? 1
      : activeFilter === 'osmangazi' ? 2
      : activeFilter === 'yesilsu' ? 3
      : currentStoreId; // 'current'

    // Transfer edilmiş kayıtlar hedef mağazanınmış gibi gösterilir
    const effectiveStoreId = repair.transferred_to_store_id || repair.store_id;

    // Eski store_id'si olmayan kayıtlar sadece kullanıcının kendi mağazasında görünür
    if (!effectiveStoreId) {
      return filterStoreId === currentStoreId;
    }

    return effectiveStoreId === filterStoreId;
  };

  // Tamirat noktası eşleştirme helper
  const matchesRepairPointFilter = (repair) => {
    if (filterRepairPoint === 'all') return true;
    return Number(repair.repair_point_id) === Number(filterRepairPoint);
  };

  // Arama + mağaza + tamirat noktası filtresinden geçen tüm kayıtlar (durum filtresi hariç)
  const searchMatchedRepairs = repairs.filter(repair => {
    const term = searchTerm.toLocaleLowerCase('tr-TR');
    const matchesSearch =
      repair.customer_name?.toLocaleLowerCase('tr-TR').includes(term) ||
      repair.item_description?.toLocaleLowerCase('tr-TR').includes(term) ||
      repair.customer_phone?.includes(searchTerm);

    return matchesSearch && matchesStoreFilter(repair) && matchesRepairPointFilter(repair);
  });

  const isOverdueRepair = (repair) => !!repair.deadline && repair.status !== 'delivered' && new Date(repair.deadline) < new Date();

  const filteredRepairs = searchMatchedRepairs.filter(repair => {
    if (repair.status === 'delivered') return false;
    if (filterStatus === 'all') return true;
    if (filterStatus === 'overdue') return isOverdueRepair(repair);
    return repair.status === filterStatus;
  });

  // Teslim edilmiş ürünler için ayrı liste ("Teslim Edilenler" filtresi)
  const deliveredRepairs = searchMatchedRepairs.filter(repair => repair.status === 'delivered');

  // Durum filtresi butonlarındaki sayılar — o filtreye basınca listelenecek kayıt sayısı
  const statusCounts = searchMatchedRepairs.reduce((acc, repair) => {
    acc[repair.status] = (acc[repair.status] || 0) + 1;
    if (repair.status !== 'delivered') acc.all += 1;
    if (isOverdueRepair(repair)) acc.overdue += 1;
    return acc;
  }, { all: 0, overdue: 0 });

  // Tamirat formu açıkken fiyatlandırmanın canlı hesabı (ürün payları, indirimler, net toplam)
  const formPricing = showModal ? getFormPricing() : null;

  // İndirim girişi: ₺ / % seçimi + tutar
  const renderDiscountInput = (type, value, onType, onValue, width = '120px') => (
    <div style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', background: '#fff', width }}>
      <button
        type="button"
        onClick={() => onType(type === 'percent' ? 'amount' : 'percent')}
        title="İndirim tipini değiştir (tutar / yüzde)"
        style={{ border: 'none', borderRight: '1px solid #e2e8f0', background: '#f1f5f9', color: '#334155', fontWeight: 700, fontSize: '0.8rem', padding: '0 9px', cursor: 'pointer', flexShrink: 0 }}
      >{type === 'percent' ? '%' : '₺'}</button>
      <PriceInput
        hideText
        placeholder="0"
        value={value}
        onChange={onValue}
        style={{ border: 'none', padding: '5px 8px', fontSize: '0.85rem', width: '100%', outline: 'none', minWidth: 0 }}
      />
    </div>
  );

  const renderMobileHome = () => {
    const workshopVisible = canAccessWorkshopPanel();
    return (
      <RepairsMobileHome
        loading={loading}
        repairs={filterStatus === 'delivered' ? deliveredRepairs : filteredRepairs}
        statusCounts={statusCounts}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        isAdmin={user?.role === 'admin'}
        filterStore={filterStore} setFilterStore={setFilterStore}
        repairPoints={repairPoints}
        filterRepairPoint={filterRepairPoint} setFilterRepairPoint={setFilterRepairPoint}
        customerDebtMap={customerDebtMap}
        getStatusLabel={getStatusLabel}
        getStoreName={getStoreName}
        getPaidAmount={getRepairPaidAmount}
        getCollectedAmount={getRepairCollectedAmount}
        imageHost={IMAGE_HOST}
        onOpenRepair={fetchRepairDetails}
        onNewRepair={openNewModal}
        readyCount={readyForDeliverRepairs.length}
        workshopCount={workshopVisible ? completedWorkshopRepairs.length : null}
        receivedCount={receivedRepairs.length}
        onDeliver={() => { setShowDeliverModal(true); setSelectedForDeliver([]); }}
        onWorkshop={() => setShowWorkshopModal(true)}
        onBulkDispatch={() => {
          setBulkDispatchSelected([]);
          setBulkDispatchBarcodeInput('');
          setBulkDispatchPointId('');
          setShowBulkDispatchModal(true);
        }}
        onOpenLog={() => setShowLogModal(true)}
      />
    );
  };

  return (
    <div className={`repairs-container${mobileApp ? ' rp-mobile m-sheets' : ''}`}>
      {mobileApp ? renderMobileHome() : (<>
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate(fromRetailSales ? '/retail-sales' : '/')}>
            <ArrowLeft size={16} /> {fromRetailSales ? 'Perakende Satışa Dön' : 'Ana Sayfa'}
          </button>
          <h1>Tamirat Ürünleri</h1>
        </div>
        <div className="header-actions">
          {(() => {
            const workshopVisible = canAccessWorkshopPanel();
            const totalBadge = readyForDeliverRepairs.length + (workshopVisible ? completedWorkshopRepairs.length : 0) + receivedRepairs.length;
            return (
              <div className="actions-menu-wrapper" ref={actionsMenuRef} style={{ position: 'relative' }}>
                <button
                  className="action-header-btn"
                  onClick={() => setShowActionsMenu(v => !v)}
                  aria-expanded={showActionsMenu}
                  aria-haspopup="menu"
                >
                  <MoreVertical size={16} /> İşlemler
                  <ChevronDown size={14} style={{ marginLeft: '2px', transition: 'transform 0.15s', transform: showActionsMenu ? 'rotate(180deg)' : 'none' }} />
                  {totalBadge > 0 && (
                    <span className="pending-badge">{totalBadge}</span>
                  )}
                </button>
                {showActionsMenu && (
                  <div
                    className="actions-menu"
                    role="menu"
                    style={{
                      position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 50,
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '220px', overflow: 'hidden'
                    }}
                  >
                    <button
                      className="actions-menu-item"
                      role="menuitem"
                      onClick={() => { setShowActionsMenu(false); setShowLogModal(true); }}
                      style={menuItemStyle}
                    >
                      <ScrollText size={16} /> <span style={{ flex: 1, textAlign: 'left' }}>İşlem Geçmişi</span>
                    </button>
                    <button
                      className="actions-menu-item"
                      role="menuitem"
                      onClick={() => { setShowActionsMenu(false); setShowDeliverModal(true); setSelectedForDeliver([]); }}
                      style={menuItemStyle}
                    >
                      <Gift size={16} /> <span style={{ flex: 1, textAlign: 'left' }}>Ürün Teslim Al</span>
                      {readyForDeliverRepairs.length > 0 && (
                        <span style={menuBadgeStyle}>{readyForDeliverRepairs.length}</span>
                      )}
                    </button>
                    {workshopVisible && (
                      <button
                        className="actions-menu-item"
                        role="menuitem"
                        onClick={() => { setShowActionsMenu(false); setShowWorkshopModal(true); }}
                        style={menuItemStyle}
                      >
                        <Package size={16} /> <span style={{ flex: 1, textAlign: 'left' }}>Atölye Çıkış</span>
                        {completedWorkshopRepairs.length > 0 && (
                          <span style={menuBadgeStyle}>{completedWorkshopRepairs.length}</span>
                        )}
                      </button>
                    )}
                    <button
                      className="actions-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setShowActionsMenu(false);
                        setBulkDispatchSelected([]);
                        setBulkDispatchBarcodeInput('');
                        setBulkDispatchPointId('');
                        setShowBulkDispatchModal(true);
                      }}
                      style={menuItemStyle}
                    >
                      <Truck size={16} style={{ color: '#0f766e' }} />
                      <span style={{ flex: 1, textAlign: 'left' }}>Toplu Tamirat Çıkışı</span>
                      {receivedRepairs.length > 0 && (
                        <span style={menuBadgeStyle}>{receivedRepairs.length}</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
          <button className="action-header-btn" onClick={openNewModal}>
            <Plus size={16} /> Yeni Kayıt
          </button>
        </div>
      </header>

      <div className="repairs-content">
        {/* Yardım ve Tamirat Noktaları Butonları */}
        <button className="help-btn-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>
        {/* Bluetooth yazıcı butonu geçici olarak gizlendi (ileride lazım olursa false→true yap) */}
        {false && (
        <button
          className="help-btn-corner"
          title={btPrinterInfo.name ? `Bluetooth Yazıcı: ${btPrinterInfo.name}${btPrinterInfo.enabled ? ' (Aktif)' : ' (Pasif)'}` : 'Bluetooth Yazıcı Ayarları'}
          onClick={() => { setBtPrinterInfo(getBluetoothPrinterInfo()); setShowBTPrinterModal(true); }}
          style={{
            right: '44px',
            color: btPrinterInfo.enabled ? '#0ea5e9' : '#64748b',
            borderColor: btPrinterInfo.enabled ? '#0ea5e9' : '#cbd5e1',
          }}
        >
          <Bluetooth size={14} />
        </button>
        )}

        {/* Search + Sütunlar */}
        <div className="repairs-toolbar-row">
          <div className="search-box" style={{ flex: 1 }}>
            <span className="search-icon"><Search size={18} /></span>
            <input
              type="text"
              placeholder="Müşteri adı, telefon veya ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="column-settings-wrapper">
            <button className="column-settings-btn" onClick={() => setShowColumnSettings(!showColumnSettings)}>
              <Settings size={16} /> Sütunlar
            </button>
            {showColumnSettings && (
              <div className="column-settings-dropdown">
                <div className="column-settings-header">
                  <span>Görünür Sütunlar</span>
                  <button className="reset-columns-btn" onClick={resetColumns}>Sıfırla</button>
                </div>
                <div className="column-settings-list">
                  {allRepairColumns.map(col => (
                    <label key={col.key} className="column-toggle">
                      <input type="checkbox" checked={visibleColumns[col.key]} onChange={() => toggleColumn(col.key)} />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mağaza (sol) ve Tamirat Noktası (sağ) Filtreleri - aynı hizada */}
        <div className="repairs-filter-groups-row">
            {/* Mağaza Filtresi - Sadece admin */}
            {user?.role === 'admin' && (
              <div className="repairs-store-filter-group">
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '4px' }}><Store size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />Mağaza:</span>
                {[
                  { key: 'all', label: 'Tümü', icon: null },
                  { key: 'gold', label: 'Akik Gold', color: '#f59e0b' },
                  { key: 'osmangazi', label: 'Osmangazi', color: '#6366f1' },
                  { key: 'yesilsu', label: 'Yeşil Su', color: '#10b981' },
                ].map(s => (
                  <button
                    key={s.key}
                    className="repairs-chip-btn"
                    onClick={() => setFilterStore(s.key)}
                    style={{
                      border: filterStore === s.key ? '2px solid ' + (s.color || '#3b82f6') : '1px solid #e2e8f0',
                      background: filterStore === s.key ? (s.color || '#3b82f6') + '15' : '#f8fafc',
                      color: filterStore === s.key ? (s.color || '#3b82f6') : '#64748b',
                      fontWeight: filterStore === s.key ? 700 : 500,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Tamirat Noktası Filtresi - en sağ (sondaki "Düzenle" ile noktalar yönetilir) */}
              <div className="repairs-point-filter-group">
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '4px' }}><MapPin size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />Tamirat Noktası:</span>
                {[{ id: 'all', name: 'Tümü' }, ...repairPoints].map(point => (
                  <button
                    key={point.id}
                    className="repairs-chip-btn"
                    onClick={() => setFilterRepairPoint(point.id)}
                    style={{
                      border: filterRepairPoint === point.id ? '2px solid #ea580c' : '1px solid #e2e8f0',
                      background: filterRepairPoint === point.id ? '#ea580c15' : '#f8fafc',
                      color: filterRepairPoint === point.id ? '#ea580c' : '#64748b',
                      fontWeight: filterRepairPoint === point.id ? 700 : 500,
                    }}
                  >
                    {point.name}
                  </button>
                ))}
                <button
                  className="repairs-chip-btn"
                  onClick={() => setShowRepairPointManageModal(true)}
                  title="Tamirat noktalarını düzenle"
                  style={{ border: '1px dashed #ea580c', background: '#fff7ed', color: '#ea580c', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  <Pencil size={13} /> Düzenle
                </button>
              </div>
          </div>

        {/* Durum Filtreler */}
        <div className="repairs-status-filter-row">
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'received', label: 'Teslim Alındı' },
            { key: 'sent_to_workshop', label: 'Tamirata Yolda' },
            { key: 'at_workshop', label: 'Atölyede' },
            { key: 'in_progress', label: 'Tamiratta' },
            { key: 'completed', label: 'Tamamlandı' },
            { key: 'returning_to_store', label: 'Mağazaya Yolda' },
            { key: 'ready_for_pickup', label: 'Tamirattan Geldi' },
            { key: 'checked', label: 'Kontrol Edildi' },
            { key: 'delivered', label: 'Teslim Edilenler' }
          ].map(f => (
            <button key={f.key} className={`filter-btn ${filterStatus === f.key ? 'active' : ''}`} onClick={() => setFilterStatus(f.key)}>
              {f.label} ({statusCounts[f.key] || 0})
            </button>
          ))}
          <button
            className={`filter-btn ${filterStatus === 'overdue' ? 'active' : ''}`}
            onClick={() => setFilterStatus('overdue')}
            style={filterStatus !== 'overdue' && statusCounts.overdue > 0 ? { borderColor: '#ef4444', color: '#dc2626' } : undefined}
          >
            ⚠ Geç Teslim Tarihi Gelenler{statusCounts.overdue > 0 ? ` (${statusCounts.overdue})` : ''}
          </button>
        </div>

        {/* Repairs Table */}
        {filterStatus !== 'delivered' && (
        <div className="table-container">
          {loading ? (
            <div className="loading">Yükleniyor...</div>
          ) : filteredRepairs.length === 0 ? (
            <div className="empty-state">
              <span><Wrench size={48} /></span>
              <p>Tamirat kaydı bulunamadı</p>
            </div>
          ) : (
            <table className="repairs-table">
              <thead>
                <tr>
                  {visibleColumns.no && <th>No</th>}
                  {visibleColumns.source && <th>Kaynak</th>}
                  {visibleColumns.customer && <th>Müşteri</th>}
                  {visibleColumns.store && <th>Mağaza</th>}
                  {visibleColumns.items && <th>Ürünler</th>}
                  {visibleColumns.price && <th className="sortable-th" onClick={() => handleSort('price')}>Toplam Ücret{sortIndicator('price')}</th>}
                  {visibleColumns.deposit && <th className="sortable-th" onClick={() => handleSort('deposit')}>Alınan Ücret{sortIndicator('deposit')}</th>}
                  {visibleColumns.balance && <th className="sortable-th" onClick={() => handleSort('balance')}>Kalan Bakiye{sortIndicator('balance')}</th>}
                  {visibleColumns.estimatedDelivery && <th className="sortable-th" onClick={() => handleSort('estimatedDelivery')}>Tahmini Teslim{sortIndicator('estimatedDelivery')}</th>}
                  {visibleColumns.deadline && <th className="sortable-th" onClick={() => handleSort('deadline')}>En Geç Teslim{sortIndicator('deadline')}</th>}
                  {visibleColumns.staff && <th>Kasiyer</th>}
                  {visibleColumns.customerCalled && <th>Arandı</th>}
                  {visibleColumns.messageLeft && <th>Mesaj</th>}
                  {visibleColumns.createdAt && <th className="sortable-th" onClick={() => handleSort('createdAt')}>Kayıt Tarihi{sortIndicator('createdAt')}</th>}
                  {visibleColumns.status && <th className="sortable-th" onClick={() => handleSort('status')}>Durum{sortIndicator('status')}</th>}
                  {visibleColumns.actions && <th>İşlemler</th>}
                </tr>
              </thead>
              <tbody>
                {sortRepairs(filteredRepairs).map(repair => {
                  const isStoreRepair = repair.store_id && (!repair.customer_phone || repair.customer_phone.trim() === '');

                  let items = [];
                  if (repair.items) {
                    try {
                      items = typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items;
                    } catch (e) {
                      items = [];
                    }
                  }
                  if (!items || items.length === 0) {
                    items = [{
                      item_type: repair.item_type,
                      item_description: repair.item_description,
                      problem_description: repair.problem_description,
                      quantity: 1
                    }];
                  }

                  const reminderHours = parseInt(localStorage.getItem('repairReminderHours') || '24');
                  const deadlineDate = repair.deadline ? new Date(repair.deadline) : null;
                  const hoursUntilDeadline = deadlineDate ? (deadlineDate - new Date()) / (1000 * 60 * 60) : null;
                  const isOverdue = deadlineDate && repair.status !== 'delivered' && hoursUntilDeadline < 0;
                  const isUrgent = deadlineDate && !repair.reminder_dismissed && hoursUntilDeadline <= reminderHours;

                  const repairItemsForDisplay = items.filter(i => i.item_description?.trim());
                  const repairItemsSummary = repairItemsForDisplay
                    .map(item => `${item.item_description}${(item.quantity || 1) > 1 ? ` x${item.quantity}` : ''}`)
                    .join(', ');
                  const isItemsExpanded = expandedItemsRepairId === repair.id;
                  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

                  return (
                    <Fragment key={repair.id}>
                    <tr
                      className={`clickable-row ${isStoreRepair ? 'store-repair-row' : ''} ${isUrgent ? 'urgent-repair' : ''}`}
                      onClick={() => fetchRepairDetails(repair.id)}
                    >
                      {visibleColumns.no && <td className="repair-id-cell">#{repair.id}</td>}
                      {visibleColumns.source && <td>
                        <span className={`source-badge ${isStoreRepair ? 'store' : 'customer'}`}>
                          {isStoreRepair ? '🏪 Mağaza' : '👤 Müşteri'}
                        </span>
                      </td>}
                      {visibleColumns.customer && <td className="customer-cell">
                        <div className="customer-info-table">
                          <span className="customer-name-table">
                            {repair.customer_name}
                            {!isStoreRepair && customerDebtMap[repair.customer_id] > 0 && (
                              <span className="customer-debt-alert-icon" title={`Borcu var: ₺${customerDebtMap[repair.customer_id].toLocaleString('tr-TR')} — tahsilat gerekiyor`}>
                                <AlertTriangle size={13} />
                              </span>
                            )}
                          </span>
                          {!isStoreRepair && <span className="customer-phone-table">{repair.customer_phone || '-'}</span>}
                        </div>
                      </td>}
                      {visibleColumns.store && <td style={{ fontSize: '0.82rem', color: '#475569' }}>{getStoreName(repair.store_id)}</td>}
                      {visibleColumns.items && <td
                        className="items-cell"
                        onClick={(e) => { e.stopPropagation(); setExpandedItemsRepairId(isItemsExpanded ? null : repair.id); }}
                      >
                        <div className="items-summary">
                          <span className="items-summary-text">{repairItemsSummary}</span>
                          {repairItemsForDisplay.length > 0 && (
                            <ChevronDown size={14} className={`items-expand-icon ${isItemsExpanded ? 'open' : ''}`} />
                          )}
                        </div>
                      </td>}
                      {visibleColumns.price && <td className="price-cell">
                        {repair.price_type === 'free' ? <span style={{ color: '#10b981', fontWeight: 600 }}>Ücretsiz</span> : repair.price_type === 'pending' ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>Hesaplanacak</span> : Number(repair.estimated_price || 0) > 0 ? `₺${Number(repair.estimated_price).toLocaleString()}` : '-'}
                      </td>}
                      {visibleColumns.deposit && <td className="deposit-cell">
                        {(() => { const collected = getRepairCollectedAmount(repair); return collected > 0 ? `₺${collected.toLocaleString('tr-TR')}` : '-'; })()}
                      </td>}
                      {visibleColumns.balance && <td className="balance-cell">
                        {repair.price_type === 'free' ? (
                          <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>Ücretsiz</span>
                        ) : repair.price_type === 'pending' ? (
                          <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.85rem' }}>Hesaplanacak</span>
                        ) : Number(repair.estimated_price || 0) > 0 ? (() => {
                          const total = Number(repair.estimated_price);
                          const deposit = Number(repair.deposit_amount || 0);
                          const paid = getRepairPaidAmount(repair);
                          const remaining = total - deposit - paid;
                          return remaining <= 0 ? (
                            <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>Ödendi</span>
                          ) : (
                            <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.85rem' }}>₺{remaining.toLocaleString('tr-TR')}</span>
                          );
                        })() : '-'}
                      </td>}
                      {visibleColumns.estimatedDelivery && <td>
                        {repair.estimated_delivery ? new Date(repair.estimated_delivery).toLocaleDateString('tr-TR') : '-'}
                      </td>}
                      {visibleColumns.deadline && <td>
                        {repair.deadline ? (
                          isOverdue ? (
                            <span className="deadline-overdue-badge">
                              ⚠ {new Date(repair.deadline).toLocaleDateString('tr-TR')}
                            </span>
                          ) : isUrgent ? (
                            <span className="deadline-urgent-badge">
                              ⏰ {new Date(repair.deadline).toLocaleDateString('tr-TR')}
                            </span>
                          ) : (
                            <span style={{ color: '#64748b' }}>{new Date(repair.deadline).toLocaleDateString('tr-TR')}</span>
                          )
                        ) : '-'}
                      </td>}
                      {visibleColumns.staff && <td>{repair.staff_name || '-'}</td>}
                      {visibleColumns.customerCalled && <td style={{ textAlign: 'center' }}>{repair.customer_called ? <span style={{ color: '#10b981', fontWeight: 600 }}>Evet</span> : <span style={{ color: '#94a3b8' }}>-</span>}</td>}
                      {visibleColumns.messageLeft && <td style={{ textAlign: 'center' }}>{repair.message_left ? <span style={{ color: '#10b981', fontWeight: 600 }}>Evet</span> : <span style={{ color: '#94a3b8' }}>-</span>}</td>}
                      {visibleColumns.createdAt && <td>
                        {new Date(repair.created_at).toLocaleDateString('tr-TR')}
                      </td>}
                      {visibleColumns.status && <td style={{ minWidth: '150px' }}>
                        <div style={{
                          display: 'inline-block',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          background: repair.status === 'received' ? '#fef3c7' :
                                     repair.status === 'sent_to_workshop' ? '#ffedd5' :
                                     repair.status === 'at_workshop' ? '#ffedd5' :
                                     repair.status === 'in_progress' ? '#ccfbf1' :
                                     repair.status === 'completed' ? '#d1fae5' :
                                     repair.status === 'checked' ? '#dbeafe' :
                                     repair.status === 'delivered' ? '#ede9fe' : '#f1f5f9',
                          color: repair.status === 'received' ? '#b45309' :
                                repair.status === 'sent_to_workshop' ? '#c2410c' :
                                repair.status === 'at_workshop' ? '#c2410c' :
                                repair.status === 'in_progress' ? '#0d9488' :
                                repair.status === 'completed' ? '#047857' :
                                repair.status === 'checked' ? '#1d4ed8' :
                                repair.status === 'delivered' ? '#6d28d9' : '#64748b',
                          border: `1px solid ${
                            repair.status === 'received' ? '#f59e0b' :
                            repair.status === 'sent_to_workshop' ? '#f97316' :
                            repair.status === 'at_workshop' ? '#f97316' :
                            repair.status === 'in_progress' ? '#14b8a6' :
                            repair.status === 'completed' ? '#10b981' :
                            repair.status === 'checked' ? '#3b82f6' :
                            repair.status === 'delivered' ? '#8b5cf6' : '#cbd5e1'
                          }`
                        }}>
                          {getStatusLabel(repair.status)}
                        </div>
                        {repair.repair_not_done === 1 && (
                          <div style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '700',
                            background: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #f87171',
                            marginLeft: '6px'
                          }}>
                            YAPILMADI
                          </div>
                        )}
                        {repair.repair_point_name && (
                          <div style={{ fontSize: '0.75rem', color: '#ea580c', marginTop: '4px' }}>
                            <MapPin size={12} style={{ verticalAlign: 'middle', marginRight: '3px' }} />
                            {repair.repair_point_name}
                          </div>
                        )}
                      </td>}
                      {visibleColumns.actions && <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        {(repair.status !== 'delivered' || user?.role === 'admin') && (
                          <button className="btn-icon edit" onClick={() => openEditModal(repair)} title="Düzenle"><Pencil size={16} /></button>
                        )}
                        {user?.role === 'admin' && (
                          <button className="btn-icon delete" onClick={() => handleDelete(repair)} title="Sil"><Trash2 size={16} /></button>
                        )}
                      </td>}
                    </tr>
                    {isItemsExpanded && (
                      <tr className="items-dropdown-row">
                        <td className="items-dropdown-cell" colSpan={visibleColumnCount}>
                          <div className="items-dropdown-list">
                            {repairItemsForDisplay.map((item, idx) => (
                              <div key={idx} className="items-dropdown-item">
                                <span className="item-desc">
                                  {item.item_description}
                                  {(item.quantity || 1) > 1 && <span className="qty-badge"> x{item.quantity}</span>}
                                </span>
                                {parseRepairPricing(repair)?.mode === 'item' && itemPriceLabel(item) && (
                                  <span className={`item-price-tag ${item.price_type || 'normal'}`}>
                                    {item.price_type === 'normal' && Number(item.final_price) < Number(item.price) && <s>{formatTL(item.price)}</s>}
                                    {itemPriceLabel(item)}
                                  </span>
                                )}
                                {item.problem_description && <span className="item-problem">{item.problem_description}</span>}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        )}

        {/* Teslim Edilenler Tablosu — "Teslim Edilenler" filtresi seçilince */}
        {filterStatus === 'delivered' && (
          <div className="table-container">
            {deliveredRepairs.length === 0 ? (
              <div className="empty-state">
                <span><CheckCircle size={48} /></span>
                <p>Teslim edilmiş ürün bulunamadı</p>
              </div>
            ) : (
              <table className="repairs-table delivered-repairs-table">
                <thead>
                  <tr>
                    {visibleColumns.no && <th>No</th>}
                    {visibleColumns.source && <th>Kaynak</th>}
                    {visibleColumns.customer && <th>Müşteri</th>}
                    {visibleColumns.store && <th>Mağaza</th>}
                    {visibleColumns.items && <th>Ürünler</th>}
                    {visibleColumns.price && <th className="sortable-th" onClick={() => handleSort('price')}>Toplam Ücret{sortIndicator('price')}</th>}
                    {visibleColumns.deposit && <th className="sortable-th" onClick={() => handleSort('deposit')}>Alınan Ücret{sortIndicator('deposit')}</th>}
                    {visibleColumns.balance && <th className="sortable-th" onClick={() => handleSort('balance')}>Kalan Bakiye{sortIndicator('balance')}</th>}
                    {visibleColumns.estimatedDelivery && <th className="sortable-th" onClick={() => handleSort('estimatedDelivery')}>Teslim Edildiği Tarih{sortIndicator('estimatedDelivery')}</th>}
                    {visibleColumns.deadline && <th className="sortable-th" onClick={() => handleSort('deadline')}>En Geç Teslim{sortIndicator('deadline')}</th>}
                    {visibleColumns.staff && <th>Kasiyer</th>}
                    {visibleColumns.customerCalled && <th>Arandı</th>}
                    {visibleColumns.messageLeft && <th>Mesaj</th>}
                    {visibleColumns.createdAt && <th className="sortable-th" onClick={() => handleSort('createdAt')}>Kayıt Tarihi{sortIndicator('createdAt')}</th>}
                    {visibleColumns.status && <th>Durum</th>}
                    {visibleColumns.actions && <th>İşlemler</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortRepairs(deliveredRepairs, 'updated_at').map(repair => {
                    const isStoreRepair = repair.store_id && (!repair.customer_phone || repair.customer_phone.trim() === '');

                    let items = [];
                    if (repair.items) {
                      try {
                        items = typeof repair.items === 'string' ? JSON.parse(repair.items) : repair.items;
                      } catch (e) {
                        items = [];
                      }
                    }
                    if (!items || items.length === 0) {
                      items = [{
                        item_type: repair.item_type,
                        item_description: repair.item_description,
                        problem_description: repair.problem_description,
                        quantity: 1
                      }];
                    }

                    const repairItemsForDisplay = items.filter(i => i.item_description?.trim());
                    const repairItemsSummary = repairItemsForDisplay
                      .map(item => `${item.item_description}${(item.quantity || 1) > 1 ? ` x${item.quantity}` : ''}`)
                      .join(', ');
                    const isItemsExpanded = expandedItemsRepairId === repair.id;
                    const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

                    return (
                      <Fragment key={repair.id}>
                      <tr
                        className={`clickable-row ${isStoreRepair ? 'store-repair-row' : ''}`}
                        onClick={() => fetchRepairDetails(repair.id)}
                      >
                        {visibleColumns.no && <td className="repair-id-cell">#{repair.id}</td>}
                        {visibleColumns.source && <td>
                          <span className={`source-badge ${isStoreRepair ? 'store' : 'customer'}`}>
                            {isStoreRepair ? '🏪 Mağaza' : '👤 Müşteri'}
                          </span>
                        </td>}
                        {visibleColumns.customer && <td className="customer-cell">
                          <div className="customer-info-table">
                            <span className="customer-name-table">
                              {repair.customer_name}
                              {!isStoreRepair && customerDebtMap[repair.customer_id] > 0 && (
                                <span className="customer-debt-alert-icon" title={`Borcu var: ₺${customerDebtMap[repair.customer_id].toLocaleString('tr-TR')} — tahsilat gerekiyor`}>
                                  <AlertTriangle size={13} />
                                </span>
                              )}
                            </span>
                            {!isStoreRepair && <span className="customer-phone-table">{repair.customer_phone || '-'}</span>}
                          </div>
                        </td>}
                        {visibleColumns.store && <td style={{ fontSize: '0.82rem', color: '#475569' }}>{getStoreName(repair.store_id)}</td>}
                        {visibleColumns.items && <td
                          className="items-cell"
                          onClick={(e) => { e.stopPropagation(); setExpandedItemsRepairId(isItemsExpanded ? null : repair.id); }}
                        >
                          <div className="items-summary">
                            <span className="items-summary-text">{repairItemsSummary}</span>
                            {repairItemsForDisplay.length > 0 && (
                              <ChevronDown size={14} className={`items-expand-icon ${isItemsExpanded ? 'open' : ''}`} />
                            )}
                          </div>
                        </td>}
                        {visibleColumns.price && <td className="price-cell">
                          {repair.price_type === 'free' ? <span style={{ color: '#10b981', fontWeight: 600 }}>Ücretsiz</span> : repair.price_type === 'pending' ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>Hesaplanacak</span> : Number(repair.estimated_price || 0) > 0 ? `₺${Number(repair.estimated_price).toLocaleString()}` : '-'}
                        </td>}
                        {visibleColumns.deposit && <td className="deposit-cell">
                          {(() => { const collected = getRepairCollectedAmount(repair); return collected > 0 ? `₺${collected.toLocaleString('tr-TR')}` : '-'; })()}
                        </td>}
                        {visibleColumns.balance && <td className="balance-cell">
                          {repair.price_type === 'free' ? (
                            <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>Ücretsiz</span>
                          ) : repair.price_type === 'pending' ? (
                            <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.85rem' }}>Hesaplanacak</span>
                          ) : Number(repair.estimated_price || 0) > 0 ? (() => {
                            const total = Number(repair.estimated_price);
                            const deposit = Number(repair.deposit_amount || 0);
                            const paid = getRepairPaidAmount(repair);
                            const remaining = total - deposit - paid;
                            return remaining <= 0 ? (
                              <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>Ödendi</span>
                            ) : (
                              <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.85rem' }}>₺{remaining.toLocaleString('tr-TR')}</span>
                            );
                          })() : '-'}
                        </td>}
                        {visibleColumns.estimatedDelivery && <td>
                          {repair.updated_at ? new Date(repair.updated_at).toLocaleDateString('tr-TR') : '-'}
                        </td>}
                        {visibleColumns.deadline && <td>
                          {repair.deadline ? new Date(repair.deadline).toLocaleDateString('tr-TR') : '-'}
                        </td>}
                        {visibleColumns.staff && <td>{repair.staff_name || '-'}</td>}
                        {visibleColumns.customerCalled && <td style={{ textAlign: 'center' }}>{repair.customer_called ? <span style={{ color: '#10b981', fontWeight: 600 }}>Evet</span> : <span style={{ color: '#94a3b8' }}>-</span>}</td>}
                        {visibleColumns.messageLeft && <td style={{ textAlign: 'center' }}>{repair.message_left ? <span style={{ color: '#10b981', fontWeight: 600 }}>Evet</span> : <span style={{ color: '#94a3b8' }}>-</span>}</td>}
                        {visibleColumns.createdAt && <td>
                          {new Date(repair.created_at).toLocaleDateString('tr-TR')}
                        </td>}
                        {visibleColumns.status && <td style={{ minWidth: '150px' }}>
                          <div style={{
                            display: 'inline-block',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            background: '#ccfbf1',
                            color: '#0f766e',
                            border: '1px solid #14b8a6'
                          }}>
                            {getStatusLabel(repair.status)}
                          </div>
                          {repair.repair_not_done === 1 && (
                            <div style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              background: '#fef2f2',
                              color: '#dc2626',
                              border: '1px solid #f87171',
                              marginLeft: '6px'
                            }}>
                              YAPILMADI
                            </div>
                          )}
                        </td>}
                        {visibleColumns.actions && <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                          {(repair.status !== 'delivered' || user?.role === 'admin') && (
                          <button className="btn-icon edit" onClick={() => openEditModal(repair)} title="Düzenle"><Pencil size={16} /></button>
                        )}
                          {user?.role === 'admin' && (
                            <button className="btn-icon delete" onClick={() => handleDelete(repair)} title="Sil"><Trash2 size={16} /></button>
                          )}
                        </td>}
                      </tr>
                      {isItemsExpanded && (
                        <tr className="items-dropdown-row">
                          <td className="items-dropdown-cell" colSpan={visibleColumnCount}>
                            <div className="items-dropdown-list">
                              {repairItemsForDisplay.map((item, idx) => (
                                <div key={idx} className="items-dropdown-item">
                                  <span className="item-desc">
                                    {item.item_description}
                                    {(item.quantity || 1) > 1 && <span className="qty-badge"> x{item.quantity}</span>}
                                  </span>
                                  {parseRepairPricing(repair)?.mode === 'item' && itemPriceLabel(item) && (
                                    <span className={`item-price-tag ${item.price_type || 'normal'}`}>
                                      {item.price_type === 'normal' && Number(item.final_price) < Number(item.price) && <s>{formatTL(item.price)}</s>}
                                      {itemPriceLabel(item)}
                                    </span>
                                  )}
                                  {item.problem_description && <span className="item-problem">{item.problem_description}</span>}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      </>)}

      {/* Modal */}
      {showModal && (
        <div className={`modal-overlay ${closingModal === 'main' ? 'closing' : ''}`}>
          <div className={`modal large ${formData.pricing_mode === 'item' ? 'repair-item-pricing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRepair ? 'Tamirat Kaydını Düzenle' : 'Yeni Tamirat Kaydı'}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('main', setShowModal)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '16px 20px' }}>
              {/* Kasiyer düzenlemede müşteri kilitli */}
              {editingRepair && user?.role !== 'admin' && formData.customer_name && (
                <div style={{ marginBottom: '14px', padding: '10px 14px', background: '#f1f5f9', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>Müşteri</div>
                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{formData.customer_name} {formData.customer_phone && <span style={{ fontWeight: 400, color: '#64748b' }}>({formData.customer_phone})</span>}</div>
                </div>
              )}
              {/* Müşteri Bölümü — Sekmeli (Müşteri Seç / Yeni Kayıt) */}
              {(user?.role === 'admin' || !editingRepair) && (
              <div style={{ marginBottom: '14px' }}>
                {/* Sekme Başlıkları */}
                <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '10px' }}>
                  {['search', 'new'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => { setRepairCustomerTab(tab); if (tab === 'search') setCustomerSearch(''); }}
                      style={{
                        padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600,
                        border: 'none', background: 'none', cursor: 'pointer', marginBottom: '-2px',
                        color: repairCustomerTab === tab ? '#3b82f6' : '#64748b',
                        borderBottom: repairCustomerTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                      }}
                    >
                      {tab === 'search' ? 'Müşteri Seç' : 'Yeni Kayıt'}
                    </button>
                  ))}
                </div>

                {/* Seçili müşteri göstergesi (her iki sekmede de) */}
                {formData.customer_id && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                    <span style={{ flex: 1, fontWeight: 600, color: '#166534', fontSize: '0.85rem' }}>
                      ✓ {customerSelectedFromMatch ? 'Müşteri seçildi' : 'Müşteri kaydedildi'}: {formData.customer_name}
                      {formData.customer_phone && <span style={{ fontWeight: 400, color: '#15803d', marginLeft: '6px' }}>({formData.customer_phone})</span>}
                    </span>
                    <button
                      className="clear-customer-btn"
                      onClick={() => {
                        setFormData({ ...formData, customer_id: '', customer_name: '', customer_phone: '' });
                        setPhoneMatchCustomers([]);
                        setCustomerSelectedFromMatch(false);
                      }}
                      title="Müşteriyi temizle"
                    >✕</button>
                  </div>
                )}

                {/* Müşteri Seç Sekmesi */}
                {!formData.customer_id && repairCustomerTab === 'search' && (
                  <div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <input
                        type="text"
                        placeholder="Ad soyad veya telefon numarası ile ara..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    {customerSearch.trim() && filteredCustomers.length > 0 && (
                      <div style={{ marginTop: '4px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                        {filteredCustomers.slice(0, 10).map(c => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setFormData({ ...formData, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || '' });
                              setCustomerSearch('');
                              setCustomerSelectedFromMatch(true);
                            }}
                            style={{ padding: '9px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
                            <span style={{ color: '#64748b', fontSize: '0.8rem' }}>{c.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {customerSearch.trim() && filteredCustomers.length === 0 && (
                      <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#94a3b8', padding: '8px 0' }}>
                        Kayıtlı müşteri bulunamadı.
                      </div>
                    )}
                  </div>
                )}

                {/* Yeni Kayıt Sekmesi */}
                {!formData.customer_id && repairCustomerTab === 'new' && (
                  <div className="new-customer-form">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label style={{ fontSize: '0.8rem', marginBottom: '4px', display: 'block' }}>Ad Soyad <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          type="text"
                          placeholder="Ad Soyad"
                          value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value.toLocaleUpperCase('tr-TR') })}
                        />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label style={{ fontSize: '0.8rem', marginBottom: '4px', display: 'block' }}>Telefon <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          type="tel"
                          placeholder="0 5XX XXX XX XX"
                          value={formatPhoneDisplay(formData.customer_phone)}
                          onChange={(e) => {
                            const digits = normalizePhoneDigits(e.target.value);
                            setFormData({ ...formData, customer_phone: digits });
                            searchCustomerByPhone(digits);
                          }}
                        />
                      </div>
                      {formData.customer_name && (
                        <button className="save-new-customer-btn" onClick={createNewCustomer}>
                          ✓ Kaydet
                        </button>
                      )}
                    </div>
                    {formData.customer_name && (
                      <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        Müşteri kaydederek KVKK onayı alınmış sayılır.
                      </div>
                    )}
                    {phoneMatchCustomers.length > 0 && (
                      <div style={{ marginTop: '6px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#92400e', background: '#fef3c7' }}>
                          Bu numaraya kayıtlı müşteri bulundu:
                        </div>
                        {phoneMatchCustomers.map(c => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setFormData({ ...formData, customer_id: c.id, customer_name: c.name, customer_phone: c.phone || '' });
                              setPhoneMatchCustomers([]);
                              setCustomerSelectedFromMatch(true);
                            }}
                            style={{ padding: '8px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #fde68a', fontSize: '0.8rem' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fef9c3'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
                            <span style={{ color: '#64748b' }}>{c.phone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Ürünler - Kompakt Liste */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Ürünler <span style={{ color: '#ef4444' }}>*</span> ({formData.items.filter(i => i.item_description?.trim()).length || formData.items.length})</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Yeni ürün eklemek için Enter'a basın</span>
                    <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: '7px', overflow: 'hidden' }} title="Fiyatlandırma şekli">
                      {[['receipt', 'Fiş Bazlı Ücret'], ['item', 'Ürün Bazlı Ücret']].map(([mode, label]) => {
                        const active = (formData.pricing_mode || 'receipt') === mode;
                        return (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, pricing_mode: mode }))}
                            style={{ padding: '4px 10px', border: 'none', borderLeft: mode === 'item' ? '1px solid #cbd5e1' : 'none', background: active ? '#1e40af' : '#fff', color: active ? '#fff' : '#475569', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                          >{label}</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {formData.items.map((item, itemIndex) => {
                    const pt = item.price_type || 'normal';
                    const calc = formData.pricing_mode === 'item' ? formPricing?.items?.[itemIndex] : null;
                    const photos = item.photos || [];
                    const showAllPhotos = expandedPhotoItem === itemIndex;
                    const inlinePhotos = showAllPhotos ? [] : photos.slice(0, 3);
                    const iconBtn = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '5px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' };
                    const renderThumb = (photo, photoIndex, size) => (
                      <div key={photoIndex} style={{ position: 'relative', width: size, height: size, borderRadius: '5px', overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                        <img src={`${IMAGE_HOST}${photo}`} alt={`Fotoğraf ${photoIndex + 1}`} onClick={() => setViewingPhoto(`${IMAGE_HOST}${photo}`)} title="Büyüt" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
                        <button type="button" onClick={() => removePhoto(itemIndex, photoIndex)} title="Fotoğrafı sil" style={{ position: 'absolute', top: 0, right: 0, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '0 4px 0 4px', width: '13px', height: '13px', fontSize: '9px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 }}>✕</button>
                      </div>
                    );
                    return (
                    <div key={itemIndex} style={{ background: '#f8fafc', borderRadius: '8px', padding: '6px 8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                        <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem', minWidth: '14px', lineHeight: '32px' }}>{itemIndex + 1}</span>
                        {/* Ürün adı: uzun yazılırsa alt satıra kayar, kutu yazıyla birlikte uzar */}
                        <textarea
                          rows={1}
                          placeholder="Örn: 22 ayar bilezik - kaynak yapılacak"
                          value={item.item_description}
                          onChange={(e) => updateItem(itemIndex, 'item_description', e.target.value.replace(/\n/g, ' '))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (item.item_description?.trim() && itemIndex === formData.items.length - 1) {
                                addItem();
                                setTimeout(() => {
                                  const inputs = document.querySelectorAll('.repair-item-input');
                                  if (inputs.length > 0) inputs[inputs.length - 1].focus();
                                }, 50);
                              }
                            }
                          }}
                          ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }}
                          className="repair-item-input"
                          style={{ flex: 1, minWidth: '120px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 9px', fontSize: '0.9rem', outline: 'none', resize: 'none', overflow: 'hidden', lineHeight: 1.35, fontFamily: 'inherit', minHeight: '32px', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, minHeight: '32px' }}>
                          {formData.pricing_mode === 'item' && (
                            <>
                              <select
                                value={pt}
                                onChange={(e) => updateItem(itemIndex, 'price_type', e.target.value)}
                                title="Ürün ücreti"
                                style={{ width: '112px', height: '30px', padding: '0 6px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600, background: pt === 'free' ? '#ecfdf5' : pt === 'pending' ? '#fffbeb' : '#fff', color: pt === 'free' ? '#059669' : pt === 'pending' ? '#d97706' : '#1e293b', cursor: 'pointer' }}
                              >
                                <option value="normal">Ücretli</option>
                                <option value="free">Ücretsiz</option>
                                <option value="pending">Hesaplanacak</option>
                              </select>
                              {pt === 'normal' && (
                                <>
                                  <div style={{ width: '82px' }} title="Ürün ücreti (₺)">
                                    <PriceInput hideText placeholder="Ücret ₺" value={item.price} onChange={(val) => updateItem(itemIndex, 'price', val)} style={{ height: '30px', padding: '0 7px', fontSize: '0.85rem', border: !(Number(item.price) > 0) && item.item_description?.trim() ? '1px solid #fca5a5' : '1px solid #e2e8f0', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }} />
                                  </div>
                                  <div title="Ürün indirimi (₺ / % değiştirmek için soldaki simgeye tıklayın)">
                                    {renderDiscountInput(
                                      item.discount_type || 'amount',
                                      item.discount_value,
                                      (t) => updateItem(itemIndex, 'discount_type', t),
                                      (v) => updateItem(itemIndex, 'discount_value', v),
                                      '82px'
                                    )}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                          {inlinePhotos.map((photo, photoIndex) => renderThumb(photo, photoIndex, '30px'))}
                          {photos.length > 3 && (
                            <button type="button" onClick={() => setExpandedPhotoItem(showAllPhotos ? null : itemIndex)} title={showAllPhotos ? 'Fotoğrafları gizle' : 'Tüm fotoğrafları göster'} style={{ ...iconBtn, padding: '0 6px', height: '30px', fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                              {showAllPhotos ? `${photos.length} ▲` : `+${photos.length - 3}`}
                            </button>
                          )}
                          <input type="file" id={`file-input-${itemIndex}`} accept="image/*" multiple onChange={(e) => handlePhotoUpload(e, itemIndex)} style={{ display: 'none' }} />
                          <input type="file" id={`camera-input-${itemIndex}`} accept="image/*" capture="environment" onChange={(e) => handlePhotoUpload(e, itemIndex)} style={{ display: 'none' }} />
                          <button type="button" onClick={() => document.getElementById(`camera-input-${itemIndex}`)?.click()} disabled={uploading} style={iconBtn} title="Kamera">
                            <Camera size={16} />
                          </button>
                          <button type="button" onClick={() => document.getElementById(`file-input-${itemIndex}`)?.click()} disabled={uploading} style={iconBtn} title="Galeri">
                            <Image size={16} />
                          </button>
                          <button type="button" onClick={() => openTerminalModal(itemIndex)} disabled={uploading || waitingForPhoto} style={iconBtn} title="Terminal">
                            <Smartphone size={16} />
                          </button>
                          {formData.items.length > 1 && (
                            <button type="button" onClick={() => removeItem(itemIndex)} style={{ ...iconBtn, borderColor: '#fecaca', color: '#ef4444' }} title="Sil">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                      {calc && pt === 'normal' && Number(item.price) > 0 && (calc.discount_amount > 0 || calc.basket_discount > 0) && (
                        <div style={{ margin: '3px 0 0 20px', fontSize: '0.75rem', color: '#475569', textAlign: 'right' }}>
                          <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: '4px' }}>{formatTL(calc.price)}</span>
                          → <strong style={{ color: '#0f172a' }}>{formatTL(calc.final_price)}</strong>
                          {calc.basket_discount > 0 && <span style={{ color: '#94a3b8', marginLeft: '4px' }}>(sepet ind. payı {formatTL(calc.basket_discount)})</span>}
                        </div>
                      )}
                      {showAllPhotos && photos.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', marginLeft: '20px', flexWrap: 'wrap' }}>
                          {photos.map((photo, photoIndex) => renderThumb(photo, photoIndex, '48px'))}
                        </div>
                      )}
                      {uploading && <p className="uploading-text" style={{ margin: '4px 0 0 24px', fontSize: '0.75rem' }}>Yükleniyor...</p>}
                      {waitingForPhoto && (
                        <div className="waiting-photo-indicator" style={{ margin: '4px 0 0 24px' }}>
                          <span className="spinner"></span>
                          <span>Terminal'den fotoğraf bekleniyor... ({selectedTerminal?.name})</span>
                          <button type="button" className="cancel-wait-btn" onClick={cancelPhotoWait}>İptal</button>
                        </div>
                      )}
                      {item.item_description?.trim() && photos.length === 0 && (
                        <p style={{ margin: '4px 0 0 20px', fontSize: '0.75rem', color: '#ef4444' }}>⚠ En az 1 fotoğraf eklemelisiniz</p>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Ücret, Tarihler, Kasiyer - Hepsi Tek Blok */}
              {(editingRepair || !(editingRepair?.store_id && (!editingRepair?.customer_phone || editingRepair?.customer_phone?.trim() === ''))) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: '14px' }}>
                  {/* Ücret Tipi Checkboxlar (fiş bazlı) */}
                  {formData.pricing_mode !== 'item' && (
                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', border: formData.price_type === 'free' ? '2px solid #10b981' : '1px solid #e2e8f0', background: formData.price_type === 'free' ? '#ecfdf5' : '#fff', fontSize: '0.85rem' }}>
                        <input type="checkbox" checked={formData.price_type === 'free'} onChange={(e) => setFormData({...formData, price_type: e.target.checked ? 'free' : 'normal', estimated_price: e.target.checked ? '' : formData.estimated_price})} style={{ width: '16px', height: '16px', accentColor: '#10b981' }} />
                        <span style={{ fontWeight: 600, color: formData.price_type === 'free' ? '#059669' : '#64748b' }}>Ücretsiz</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', border: formData.price_type === 'pending' ? '2px solid #f59e0b' : '1px solid #e2e8f0', background: formData.price_type === 'pending' ? '#fffbeb' : '#fff', fontSize: '0.85rem' }}>
                        <input type="checkbox" checked={formData.price_type === 'pending'} onChange={(e) => setFormData({...formData, price_type: e.target.checked ? 'pending' : 'normal', estimated_price: e.target.checked ? '' : formData.estimated_price})} style={{ width: '16px', height: '16px', accentColor: '#f59e0b' }} />
                        <span style={{ fontWeight: 600, color: formData.price_type === 'pending' ? '#d97706' : '#64748b' }}>Hesaplanacak</span>
                      </label>
                    </div>
                  )}
                  {/* Fiş bazlı ücret + fiş indirimi */}
                  {formData.pricing_mode !== 'item' && formData.price_type === 'normal' && (
                    <>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Ücret (₺) <span style={{ color: '#ef4444' }}>*</span></label>
                        <PriceInput placeholder="Ücret girin" value={formData.estimated_price} onChange={(val) => setFormData({...formData, estimated_price: val})} />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Fiş İndirimi</label>
                        {renderDiscountInput(
                          formData.discount_type || 'amount',
                          formData.discount_value,
                          (t) => setFormData(prev => ({ ...prev, discount_type: t })),
                          (v) => setFormData(prev => ({ ...prev, discount_value: v })),
                          '100%'
                        )}
                      </div>
                      {formPricing && formPricing.discount_amount > 0 && (
                        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '14px', alignItems: 'baseline', padding: '6px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.82rem', color: '#475569' }}>
                          <span>Ücret: {formatTL(formPricing.subtotal)}</span>
                          <span style={{ color: '#dc2626' }}>İndirim ({discountLabel(formData.discount_type, formData.discount_value)}): −{formatTL(formPricing.discount_amount)}</span>
                          <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>Toplam: {formatTL(formPricing.total)}</span>
                        </div>
                      )}
                    </>
                  )}
                  {/* Ürün bazlı: toplam özeti + sepet indirimi */}
                  {formData.pricing_mode === 'item' && formPricing && (
                    <div style={{ gridColumn: '1 / -1', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.85rem', color: '#475569' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Ara Toplam</span>
                        <span>{formatTL(formPricing.subtotal)}</span>
                      </div>
                      {formPricing.item_discount_total > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                          <span>Ürün İndirimleri</span>
                          <span>−{formatTL(formPricing.item_discount_total)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                        <span>Sepet İndirimi <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>(ücretli ürünlere paylaştırılır)</span></span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {formPricing.discount_amount > 0 && <span style={{ color: '#dc2626' }}>−{formatTL(formPricing.discount_amount)}</span>}
                          {renderDiscountInput(
                            formData.discount_type || 'amount',
                            formData.discount_value,
                            (t) => setFormData(prev => ({ ...prev, discount_type: t })),
                            (v) => setFormData(prev => ({ ...prev, discount_value: v })),
                            '120px'
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: '6px', fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                        <span>Toplam</span>
                        {formPricing.price_type === 'pending' ? (
                          <span style={{ color: '#d97706' }}>Hesaplanacak{formPricing.total > 0 ? <span style={{ fontWeight: 500, fontSize: '0.8rem', color: '#64748b' }}> (belli olan: {formatTL(formPricing.total)})</span> : null}</span>
                        ) : formPricing.price_type === 'free' ? (
                          <span style={{ color: '#059669' }}>Ücretsiz</span>
                        ) : (
                          <span>{formatTL(formPricing.total)}</span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Alınan ücret — net toplam belli ve sıfırdan büyükse */}
                  {/* (önceden ücret alınmışsa fiyat Ücretsiz'e çekilse bile görünür kalır ki düzeltilebilsin) */}
                  {formPricing && ((formPricing.price_type === 'normal' && formPricing.total > 0) ||
                    [...(formData.deposit_payment_details || []), ...(formData.delivery_payment_details || [])].some(p => Number(p.amount) > 0)) && (
                    <>
                      <div className="form-group" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Alınan Ücret (₺)</label>
                        {(() => {
                          const shownPayments = [...(formData.deposit_payment_details || []), ...(formData.delivery_payment_details || [])];
                          const shownTotal = shownPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
                          return shownTotal > 0 ? (
                          <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', minHeight: '38px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                              <span style={{ fontWeight: 700, color: '#16a34a', fontSize: '0.95rem' }}>
                                ₺{shownTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                              </span>
                              {shownPayments.map((p, idx) => (
                                <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 8px', background: '#fff', border: '1px solid #d1fae5', borderRadius: '12px', fontSize: '0.72rem', color: '#334155' }}>
                                  <strong>{p.method}</strong>
                                  {p.bank_account && <span style={{ color: '#6d28d9', fontSize: '0.68rem' }}>· {p.bank_account}</span>}
                                  <span style={{ color: '#16a34a', fontWeight: 600 }}>₺{Number(p.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                                </span>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                              <button type="button" onClick={openDepositPaymentModal} style={{ padding: '4px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#475569' }}>Düzenle</button>
                              <button type="button" onClick={clearDepositPayment} style={{ padding: '4px 8px', background: '#fff', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', color: '#dc2626' }} title="Alınan ücreti kaldır">✕</button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" onClick={openDepositPaymentModal} style={{ padding: '8px 12px', background: '#f0fdf4', color: '#16a34a', border: '1px dashed #86efac', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px' }}>
                            <Wallet size={16} /> Ücret Al
                          </button>
                        );
                        })()}
                      </div>
                    </>
                  )}
                  {/* Tarihler */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Tahmini Teslim</label>
                    <input type="date" value={formData.estimated_delivery} onChange={(e) => setFormData({...formData, estimated_delivery: e.target.value})} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>En Geç Teslim</label>
                    <input type="date" value={formData.deadline} onChange={(e) => setFormData({...formData, deadline: e.target.value})} />
                  </div>
                </div>
              )}

              {/* Kasiyer ve Notlar - Yan Yana */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Kasiyer <span style={{ color: '#ef4444' }}>*</span></label>
                  <select value={formData.staff_name} onChange={(e) => setFormData({...formData, staff_name: e.target.value})}>
                    <option value="">Kasiyer Seç</option>
                    {staffList.map(staff => (
                      <option key={staff.id} value={staff.name}>{staff.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.8rem', marginBottom: '4px' }}>Notlar</label>
                  <input type="text" placeholder="Ek notlar..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleSubmit}>
                {editingRepair ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fotoğraf Büyük Görüntüleme Modal */}
      {viewingPhoto && (
        <div className={`photo-modal-overlay ${closingModal === 'photo' ? 'closing' : ''}`} onClick={() => handleCloseModal('photo', () => setViewingPhoto(null))}>
          <button className="photo-modal-close" onClick={() => handleCloseModal('photo', () => setViewingPhoto(null))}>✕</button>
          <div className="photo-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 'none', maxHeight: 'none' }}>
            <ZoomablePhoto src={viewingPhoto} alt="Büyük görünüm" onBackgroundClick={() => handleCloseModal('photo', () => setViewingPhoto(null))} />
          </div>
        </div>
      )}

      {/* Tamirat Detay Modal */}
      {showDetailModal && selectedRepairDetails && (() => {
        // Mağazadan gönderilen mi kontrol et (store_id var ve müşteri telefonu yok/boş)
        const isStoreRepairDetail = selectedRepairDetails.repair?.store_id &&
          (!selectedRepairDetails.repair?.customer_phone || selectedRepairDetails.repair?.customer_phone.trim() === '');
        const reminderHoursDetail = parseInt(localStorage.getItem('repairReminderHours') || '24');
        const isUrgentDetail = selectedRepairDetails.repair?.deadline &&
          !selectedRepairDetails.repair?.reminder_dismissed &&
          ((new Date(selectedRepairDetails.repair.deadline) - new Date()) / (1000 * 60 * 60)) <= reminderHoursDetail;

        return (
        <div className={`modal-overlay ${closingModal === 'detail' ? 'closing' : ''}`}>
          <div className="modal detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Tamirat Detayları</h2>
              <button className="close-btn" onClick={() => handleCloseModal('detail', setShowDetailModal)}>✕</button>
            </div>
            <div className="modal-body detail-body" style={{ padding: '14px 18px' }}>
              {/* Fiş No + Durum - Kompakt Başlık */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: '10px', padding: '10px 14px', marginBottom: '12px' }}>
                <div>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Fiş No</span>
                  <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#00bfa6' }}>{selectedRepairDetails.repair?.repair_code || String(selectedRepairDetails.repair?.id || '').padStart(6, '0')}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {selectedRepairDetails.repair?.repair_not_done === 1 && (
                    <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, background: '#fef2f2', color: '#dc2626', border: '1px solid #f87171' }}>YAPILMADI</span>
                  )}
                  {user?.role === 'admin' ? (
                    <select
                      value={selectedRepairDetails.repair?.status || ''}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        if (newStatus && newStatus !== selectedRepairDetails.repair?.status) {
                          // Atölyeye/tamirata gönderiliyor ve henüz tamirat noktası atanmamışsa: önce nokta seçtir
                          if ((newStatus === 'sent_to_workshop' || newStatus === 'at_workshop' || newStatus === 'in_progress') && !selectedRepairDetails.repair?.repair_point_id) {
                            setStatusPointId('');
                            setStatusPointModal({ repair: selectedRepairDetails.repair, newStatus });
                            return;
                          }
                          await handleStatusChange(selectedRepairDetails.repair, newStatus, 'Admin tarafından değiştirildi');
                          setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, status: newStatus } }));
                          toast.success(`Durum güncellendi: ${getStatusLabel(newStatus)}`);
                        }
                      }}
                      style={{ padding: '6px 10px', borderRadius: '6px', border: '2px solid #6366f1', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', background: '#eef2ff', color: '#4338ca' }}
                    >
                      <option value="received">Teslim Alındı</option>
                      <option value="sent_to_workshop">Atölyeye Gönderildi</option>
                      <option value="at_workshop">Atölyede</option>
                      <option value="in_progress">Tamirata Başlandı</option>
                      <option value="completed">Tamirat Tamamlandı</option>
                      <option value="returning_to_store">Mağazaya Yolda</option>
                      <option value="ready_for_pickup">Tamirattan Geldi</option>
                      <option value="checked">Kontrol Edildi</option>
                      <option value="delivered">Teslim Edildi</option>
                    </select>
                  ) : (
                    <div className="repair-status-badge">{getStatusIcon(selectedRepairDetails.repair?.status)} {getStatusLabel(selectedRepairDetails.repair?.status)}</div>
                  )}
                </div>
              </div>

              {/* Müşteri + Ücret - Yan Yana */}
              <div style={{ display: 'grid', gridTemplateColumns: !isStoreRepairDetail ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '12px' }}>
                {/* Müşteri */}
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{isStoreRepairDetail ? 'Mağaza' : 'Müşteri'}</span>
                  <div style={{ fontWeight: 600, color: '#1e293b', marginTop: '2px' }}>{selectedRepairDetails.repair?.customer_name}</div>
                  {!isStoreRepairDetail && selectedRepairDetails.repair?.customer_phone && (
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{selectedRepairDetails.repair.customer_phone}</div>
                  )}
                  {isStoreRepairDetail && <div style={{ fontSize: '0.8rem', color: '#16a34a' }}>Mağaza stoğundan</div>}
                  {!isStoreRepairDetail && customerDebtBalance > 0 && (
                    <div
                      onClick={goToCollectionForRepairCustomer}
                      title="Tahsilat penceresini açmak için tıklayın"
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        color: '#dc2626',
                        fontSize: '0.76rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                      <span>Borcu var (₺{customerDebtBalance.toLocaleString('tr-TR')}) — Lütfen tahsilat yapınız</span>
                    </div>
                  )}
                </div>

                {/* Ücret */}
                {!isStoreRepairDetail && (
                  <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px 12px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Ücret</span>
                    {selectedRepairDetails.repair?.price_type === 'free' ? (
                      <div style={{ fontWeight: 700, color: '#10b981', marginTop: '2px' }}>Ücretsiz</div>
                    ) : selectedRepairDetails.repair?.price_type === 'pending' ? (
                      <div style={{ fontWeight: 700, color: '#f59e0b', marginTop: '2px' }}>Hesaplanacak</div>
                    ) : Number(selectedRepairDetails.repair?.estimated_price) > 0 ? (
                      <div style={{ marginTop: '2px' }}>
                        {(() => {
                          const pr = parseRepairPricing(selectedRepairDetails.repair);
                          const totalDiscount = (Number(pr?.item_discount_total) || 0) + (Number(pr?.discount_amount) || 0);
                          if (!(totalDiscount > 0)) return null;
                          return (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Ara Toplam:</span>
                                <span style={{ fontWeight: 500 }}>{formatTL(pr.subtotal)}</span>
                              </div>
                              {Number(pr.item_discount_total) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Ürün İndirimleri:</span>
                                  <span style={{ fontWeight: 500, color: '#dc2626' }}>−{formatTL(pr.item_discount_total)}</span>
                                </div>
                              )}
                              {Number(pr.discount_amount) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{pr.mode === 'item' ? 'Sepet' : 'Fiş'} İndirimi{pr.discount_type === 'percent' ? ` (${discountLabel(pr.discount_type, pr.discount_value)})` : ''}:</span>
                                  <span style={{ fontWeight: 500, color: '#dc2626' }}>−{formatTL(pr.discount_amount)}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Ücret:</span>
                          <span style={{ fontWeight: 600 }}>₺{Number(selectedRepairDetails.repair.estimated_price).toLocaleString()}</span>
                        </div>
                        {Number(selectedRepairDetails.repair?.deposit_amount) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px' }}>
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Alınan Ücret:</span>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontWeight: 600, color: '#16a34a' }}>₺{Number(selectedRepairDetails.repair.deposit_amount).toLocaleString()}</span>
                              {(() => {
                                let details = selectedRepairDetails.repair?.deposit_payment_details;
                                if (typeof details === 'string') {
                                  try { details = JSON.parse(details); } catch (e) { details = null; }
                                }
                                if (Array.isArray(details) && details.length > 0) {
                                  return (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px', justifyContent: 'flex-end' }}>
                                      {details.map((p, i) => (
                                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 6px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', fontSize: '0.68rem', color: '#334155' }}>
                                          <strong>{p.method}</strong>
                                          {p.bank_account && <span style={{ color: '#6d28d9' }}>· {p.bank_account}</span>}
                                          <span style={{ color: '#16a34a' }}>₺{Number(p.amount).toLocaleString('tr-TR')}</span>
                                        </span>
                                      ))}
                                    </div>
                                  );
                                }
                                if (selectedRepairDetails.repair?.deposit_payment_method) {
                                  return (
                                    <div style={{ marginTop: '2px', fontSize: '0.68rem', color: '#64748b' }}>
                                      {selectedRepairDetails.repair.deposit_payment_method}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        )}
                        {Number(selectedRepairDetails.repair?.estimated_price) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #e2e8f0', paddingTop: '3px', marginTop: '3px' }}>
                          <span style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 600 }}>Kalan:</span>
                          <span style={{ fontWeight: 700, color: '#dc2626' }}>₺{Number((selectedRepairDetails.repair.estimated_price || 0) - (selectedRepairDetails.repair.deposit_amount || 0)).toLocaleString()}</span>
                        </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontWeight: 500, color: '#94a3b8', marginTop: '2px' }}>Fiyat belirtilmemiş</div>
                    )}
                  </div>
                )}
              </div>

              {/* Ürünler - Kompakt Liste */}
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>Ürünler</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                {(() => {
                  let items = [];
                  if (selectedRepairDetails.repair?.items) {
                    try { items = typeof selectedRepairDetails.repair.items === 'string' ? JSON.parse(selectedRepairDetails.repair.items) : selectedRepairDetails.repair.items; } catch (e) { items = []; }
                  }
                  if (!items || items.length === 0) {
                    items = [{ item_type: selectedRepairDetails.repair?.item_type, item_description: selectedRepairDetails.repair?.item_description, problem_description: selectedRepairDetails.repair?.problem_description, photos: parsePhotos(selectedRepairDetails.repair?.photos), quantity: 1 }];
                  }
                  // repair.photos'tan gelen fotoğrafları items'a ekle (items'da yoksa)
                  let repairPhotos = parsePhotos(selectedRepairDetails.repair?.photos);
                  if (repairPhotos.length > 0 && items.length > 0) {
                    const itemPhotos = items.flatMap(i => i.photos || []);
                    const missingPhotos = repairPhotos.filter(p => !itemPhotos.includes(p));
                    if (missingPhotos.length > 0) {
                      items[0].photos = [...(items[0].photos || []), ...missingPhotos];
                    }
                  }
                  return items.filter(i => i.item_description?.trim()).map((item, idx) => (
                    <div key={idx} style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 12px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem' }}>{idx + 1}</span>
                        <span style={{ flex: 1, fontWeight: 500, color: '#1e293b' }}>{item.item_description}{item.problem_description ? ` - ${item.problem_description}` : ''}</span>
                        {(item.quantity || 1) > 1 && <span style={{ fontSize: '0.75rem', background: '#dbeafe', color: '#1d4ed8', padding: '2px 6px', borderRadius: '10px', fontWeight: 600 }}>x{item.quantity}</span>}
                        <span style={{ fontSize: '0.75rem', color: item.item_type === 'gold' ? '#f59e0b' : '#9ca3af', fontWeight: 500 }}>{item.item_type === 'gold' ? 'Altın' : 'Gümüş'}</span>
                        {!isStoreRepairDetail && parseRepairPricing(selectedRepairDetails.repair)?.mode === 'item' && itemPriceLabel(item) && (
                          <span style={{ textAlign: 'right', fontSize: '0.82rem', fontWeight: 700, color: item.price_type === 'free' ? '#059669' : item.price_type === 'pending' ? '#d97706' : '#0f172a', whiteSpace: 'nowrap' }}>
                            {item.price_type === 'normal' && Number(item.final_price) < Number(item.price) && (
                              <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontWeight: 500, marginRight: '4px', fontSize: '0.75rem' }}>{formatTL(item.price)}</span>
                            )}
                            {itemPriceLabel(item)}
                          </span>
                        )}
                      </div>
                      {item.photos && item.photos.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', marginLeft: '20px', flexWrap: 'wrap' }}>
                          {item.photos.map((photo, pIdx) => (
                            <img key={pIdx} src={`${IMAGE_HOST}${photo}`} alt={`Fotoğraf ${pIdx + 1}`} onClick={() => setViewingPhoto(`${IMAGE_HOST}${photo}`)} style={{ width: '48px', height: '48px', borderRadius: '6px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #e2e8f0' }} />
                          ))}
                        </div>
                      )}
                    </div>
                  ));
                })()}
                </div>
              </div>

              {/* Tarihler + Tamirat Noktası + Notlar - Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>Kayıt Tarihi</span>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, marginTop: '2px' }}>{new Date(selectedRepairDetails.repair?.created_at).toLocaleDateString('tr-TR')}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '8px 10px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>Tahmini Teslim</span>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, marginTop: '2px' }}>{selectedRepairDetails.repair?.estimated_delivery ? new Date(selectedRepairDetails.repair.estimated_delivery).toLocaleDateString('tr-TR') : '-'}</div>
                </div>
                {(() => {
                  const dl = selectedRepairDetails.repair?.deadline;
                  const dlDate = dl ? new Date(dl) : null;
                  const dlHours = dlDate ? (dlDate - new Date()) / (1000 * 60 * 60) : null;
                  const dlOverdue = dlDate && selectedRepairDetails.repair?.status !== 'delivered' && dlHours < 0;
                  const dlUrgent = dlDate && selectedRepairDetails.repair?.status !== 'delivered' && dlHours >= 0 && dlHours <= 24;
                  const bgColor = dlOverdue ? '#fef2f2' : dlUrgent ? '#fffbeb' : '#f8fafc';
                  const borderColor = dlOverdue ? '#fca5a5' : dlUrgent ? '#fde68a' : '#e2e8f0';
                  return (
                    <div style={{ background: bgColor, borderRadius: '8px', padding: '8px 10px', border: `1px solid ${borderColor}` }}>
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>En Geç Teslim</span>
                      {dlDate ? (
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '2px' }}>
                          {dlOverdue ? (
                            <span style={{ color: '#dc2626' }}>⚠ {dlDate.toLocaleDateString('tr-TR')}</span>
                          ) : dlUrgent ? (
                            <span style={{ color: '#d97706' }}>⏰ {dlDate.toLocaleDateString('tr-TR')}</span>
                          ) : (
                            <span style={{ color: '#1e293b' }}>{dlDate.toLocaleDateString('tr-TR')}</span>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.85rem', marginTop: '2px', color: '#94a3b8' }}>-</div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Tamirat Noktası + Kasiyer + Notlar - tek satır */}
              {(selectedRepairDetails.repair?.repair_point_name || selectedRepairDetails.repair?.staff_name || selectedRepairDetails.repair?.notes || selectedRepairDetails.repair?.transferred_to_store_id) && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {selectedRepairDetails.repair?.transferred_to_store_id && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: '#faf5ff', borderRadius: '6px', border: '1px solid #e9d5ff', fontSize: '0.8rem' }}>
                      <ArrowLeftRight size={14} style={{ color: '#7e22ce' }} />
                      <span style={{ fontWeight: 600, color: '#7e22ce' }}>
                        Transfer edildi: {selectedRepairDetails.repair.transferred_store_name || getStoreName(selectedRepairDetails.repair.transferred_to_store_id)}
                      </span>
                    </div>
                  )}
                  {selectedRepairDetails.repair?.repair_point_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa', fontSize: '0.8rem' }}>
                      <MapPin size={14} style={{ color: '#ea580c' }} />
                      <span style={{ fontWeight: 600 }}>{selectedRepairDetails.repair.repair_point_name}</span>
                    </div>
                  )}
                  {selectedRepairDetails.repair?.staff_name && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0', fontSize: '0.8rem' }}>
                      <Users size={14} style={{ color: '#16a34a' }} />
                      <span style={{ fontWeight: 600 }}>{selectedRepairDetails.repair.staff_name}</span>
                    </div>
                  )}
                  {selectedRepairDetails.repair?.notes && (
                    <div style={{ flex: 1, minWidth: '150px', padding: '6px 10px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fde68a', fontSize: '0.8rem', color: '#92400e' }}>
                      {selectedRepairDetails.repair.notes}
                    </div>
                  )}
                </div>
              )}

              {/* Durum Geçmişi */}
              <div className="detail-section">
                <h3>Durum Geçmişi</h3>
                <div className="status-timeline">
                  {selectedRepairDetails.statusHistory?.length > 0 ? (
                    selectedRepairDetails.statusHistory.map((history, index) => (
                      <div key={history.id} className={`timeline-item ${index === selectedRepairDetails.statusHistory.length - 1 ? 'current' : ''}`}>
                        <div className="timeline-marker">
                          <span className="timeline-icon">{getStatusIcon(history.new_status)}</span>
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="timeline-status">
                              {getStatusLabel(history.new_status)}
                              {history.new_status === 'received' && selectedRepairDetails.repair?.staff_name && ` (${selectedRepairDetails.repair.staff_name})`}
                              {history.new_status === 'ready_for_pickup' && history.notes && (() => {
                                const match = (history.notes || '').match(/Teslim alan:\s*(.+)/);
                                return match ? ` (${match[1]})` : '';
                              })()}
                            </span>
                            <span className="timeline-date">
                              {new Date(history.created_at).toLocaleString('tr-TR')}
                            </span>
                          </div>
                          {history.notes && (
                            <p className="timeline-notes">{history.notes}</p>
                          )}
                          {!history.notes && history.new_status === 'received' && selectedRepairDetails.repair?.staff_name && (
                            <p className="timeline-notes">Kasiyer: {selectedRepairDetails.repair.staff_name}</p>
                          )}
                          {history.old_status && (
                            <span className="timeline-change">
                              {getStatusLabel(history.old_status)} → {getStatusLabel(history.new_status)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-history">
                      <span><ClipboardList size={32} /></span>
                      <p>Henüz durum geçmişi bulunmuyor</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Müşteri İletişim Checkboxları */}
            {(selectedRepairDetails.repair?.status === 'checked' || selectedRepairDetails.repair?.status === 'ready_for_pickup' || isUrgentDetail) && (
              <div style={{ display: 'flex', gap: '12px', padding: '8px 18px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={!!selectedRepairDetails.repair?.customer_called}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      await fetch(`${API_URL}/repairs/${selectedRepairDetails.repair.id}/contact`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customer_called: val, message_left: !!selectedRepairDetails.repair?.message_left })
                      });
                      setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, customer_called: val ? 1 : 0 } }));
                      if (val && isUrgentDetail) {
                        await fetch(`${API_URL}/repairs/${selectedRepairDetails.repair.id}/dismiss-reminder`, { method: 'PUT' });
                        setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, reminder_dismissed: 1 } }));
                        fetchRepairs();
                        toast.success('Bildirim kapatıldı');
                      }
                    }}
                    style={{ width: '16px', height: '16px', accentColor: '#10b981' }}
                  />
                  <span style={{ fontWeight: selectedRepairDetails.repair?.customer_called ? 600 : 400 }}>Müşteri telefonla arandı</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.85rem', color: '#475569' }}>
                  <input
                    type="checkbox"
                    checked={!!selectedRepairDetails.repair?.message_left}
                    onChange={async (e) => {
                      const val = e.target.checked;
                      await fetch(`${API_URL}/repairs/${selectedRepairDetails.repair.id}/contact`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customer_called: !!selectedRepairDetails.repair?.customer_called, message_left: val })
                      });
                      setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, message_left: val ? 1 : 0 } }));
                      if (val && isUrgentDetail) {
                        await fetch(`${API_URL}/repairs/${selectedRepairDetails.repair.id}/dismiss-reminder`, { method: 'PUT' });
                        setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, reminder_dismissed: 1 } }));
                        fetchRepairs();
                        toast.success('Bildirim kapatıldı');
                      }
                    }}
                    style={{ width: '16px', height: '16px', accentColor: '#10b981' }}
                  />
                  <span style={{ fontWeight: selectedRepairDetails.repair?.message_left ? 600 : 400 }}>Mesaj bırakıldı</span>
                </label>
                {(selectedRepairDetails.repair?.status === 'checked' || selectedRepairDetails.repair?.status === 'ready_for_pickup') && (
                  <button
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #a855f7', background: '#faf5ff', color: '#7e22ce', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}
                    onClick={() => { setTransferringRepair(selectedRepairDetails.repair); setShowTransferModal(true); }}
                  >
                    <ArrowLeftRight size={14} /> Şube Transferi
                  </button>
                )}
              </div>
            )}
            <div className="modal-footer" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', padding: '12px 18px' }}>
              {(() => {
                const btnStyle = { display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '8px', border: 'none', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
                const sendStorePrint = async (kind) => {
                  const repair = selectedRepairDetails?.repair;
                  if (!repair) return;
                  const storeId = repair.transferred_to_store_id || repair.store_id || currentStoreId;
                  let html = null;
                  try {
                    html = kind === 'customer'
                      ? await printReceipt(repair, true)
                      : kind === 'payment'
                        ? printRepairPaymentReceipt(repair, true)
                        : printRepairReceipt(repair, true);
                  } catch (e) {
                    toast.error('Fiş hazırlanamadı: ' + (e?.message || ''));
                    return;
                  }
                  if (!html) { toast.error('Fiş HTML üretilemedi'); return; }
                  const label = kind === 'customer' ? 'Müşteri Fişi' : kind === 'payment' ? 'Tamirat Tahsilat Fişi' : 'Tamirat Fişi';
                  try {
                    const result = await printSystemReceipt(html, storeId, 'repair');
                    if (result.mode === 'local') toast.success(`${label} yazıcıya gönderildi`);
                    else if (result.pending) toast.info(`${label} kuyruğa alındı (${result.targetName})`);
                    else toast.success(`${label} ${result.targetName} cihazından yazdırıldı`);
                  } catch (e) {
                    toast.error(`${label} yazdırma hatası: ` + (e?.message || ''));
                  }
                };
                return (
                  <>
                    <button style={{ ...btnStyle, background: '#334155', color: '#fff' }} onClick={() => sendStorePrint('customer')}>
                      <Printer size={16} /> Müşteri Fişi
                    </button>
                    <button style={{ ...btnStyle, background: '#6366f1', color: '#fff' }} onClick={() => sendStorePrint('internal')}>
                      <Printer size={16} /> Tamirat Fişi
                    </button>
                    {(Number(selectedRepairDetails.repair?.estimated_price) > 0 || Number(selectedRepairDetails.repair?.deposit_amount) > 0) && (
                      <button style={{ ...btnStyle, background: '#16a34a', color: '#fff' }} onClick={() => sendStorePrint('payment')}>
                        <Printer size={16} /> Tamirat Tahsilat Fişi
                      </button>
                    )}
                    {/* Ödeme Al: tamirat ücretsiz değilse ve teslim edilmemişse, sürecin HERHANGİ
                        bir aşamasında (sadece kayıt anında veya teslimde değil) tekrar tekrar
                        ödeme alınabilir. "Kapora" değil — doğrudan tamirat ödemesi/ücreti. */}
                    {selectedRepairDetails.repair?.status !== 'delivered' &&
                      selectedRepairDetails.repair?.price_type === 'normal' &&
                      Number(selectedRepairDetails.repair?.estimated_price) > 0 &&
                      (Number(selectedRepairDetails.repair.estimated_price) - Number(selectedRepairDetails.repair.deposit_amount || 0)) > 0.01 && (
                      <button style={{ ...btnStyle, background: '#059669', color: '#fff' }} onClick={() => openAnytimeRepairPaymentModal(selectedRepairDetails.repair)}>
                        <Wallet size={16} /> Ödeme Al
                      </button>
                    )}
                    {selectedRepairDetails.repair?.status === 'received' && (
                      <>
                        <button style={{ ...btnStyle, background: '#f97316', color: '#fff' }} onClick={() => { setSendingToRepairPoint(selectedRepairDetails.repair); setShowRepairPointModal(true); }}>
                          <Truck size={16} /> Tamirata Gönder
                        </button>
                        <button style={{ ...btnStyle, background: '#10b981', color: '#fff' }} onClick={async () => {
                          await handleStatusChange(selectedRepairDetails.repair, 'ready_for_pickup', 'Mağazada yerinde tamamlandı');
                          setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, status: 'ready_for_pickup' } }));
                          toast.success('Yerinde tamirat tamamlandı, teslime hazır!');
                        }}>
                          <CheckCircle size={16} /> Yerinde Tamirat
                        </button>
                      </>
                    )}
                    {(() => {
                      const status = selectedRepairDetails.repair?.status;
                      const hasAppAccess = selectedRepairDetails.repair?.repair_point_has_app_access;
                      const workshopStatuses = ['sent_to_workshop', 'at_workshop', 'in_progress', 'completed', 'returning_to_store'];
                      const showBtn = hasAppAccess ? status === 'returning_to_store' : workshopStatuses.includes(status);
                      return showBtn ? (
                        <button style={{ ...btnStyle, background: '#10b981', color: '#fff' }} onClick={() => { setReceiveStaffName(''); setReceiveStaffAction({ repair: selectedRepairDetails.repair, hasAppAccess }); setShowReceiveStaffModal(true); }}>
                          <Package size={16} /> Tamirattan Geldi
                        </button>
                      ) : null;
                    })()}
                    {selectedRepairDetails.repair?.status === 'ready_for_pickup' && (
                      <button style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }} onClick={() => {
                        setCheckRepair(selectedRepairDetails.repair);
                        setCheckStaffName('');
                        setShowCheckStaffModal(true);
                      }}>
                        <CheckCircle size={16} /> Kontrol Edildi
                      </button>
                    )}
                    {(selectedRepairDetails.repair?.status === 'ready_for_pickup' || selectedRepairDetails.repair?.status === 'checked') && (
                      <button style={{ ...btnStyle, background: '#dc2626', color: '#fff' }} onClick={() => openReworkModal(selectedRepairDetails.repair)}>
                        <RotateCcw size={16} /> Tekrar Atölyeye Gönder
                      </button>
                    )}
                    {selectedRepairDetails.repair?.status === 'checked' && (
                      <button style={{ ...btnStyle, background: '#0d9488', color: '#fff' }} onClick={handleDeliverToCustomer}>
                        <Gift size={16} /> Müşteriye Teslim
                      </button>
                    )}
                    {isUrgentDetail && (
                      <button style={{ ...btnStyle, background: '#ef4444', color: '#fff' }} onClick={async () => {
                        const r = selectedRepairDetails.repair;
                        await fetch(`${API_URL}/repairs/${r.id}/dismiss-reminder`, { method: 'PUT' });
                        setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, reminder_dismissed: 1 } }));
                        fetchRepairs();
                        toast.success('Bu tamirat için bildirim kapatıldı');
                      }}>
                        <AlertTriangle size={16} /> Bildirimi Kapat
                      </button>
                    )}
                    {user?.role === 'admin' && (
                      <button style={{ ...btnStyle, background: '#00bfa6', color: '#fff' }} onClick={() => { setShowDetailModal(false); openEditModal(selectedRepairDetails.repair); }}>
                        <Pencil size={16} /> Düzenle
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Tamirattan Teslim Alma - Kasiyer Seçimi Modal */}
      {showReceiveStaffModal && (
        <div className="modal-overlay">
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Teslim Alan Kasiyer</h2>
              <button className="close-btn" onClick={() => setShowReceiveStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {staffList.map(staff => {
                  const isSelected = receiveStaffName === staff.name;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => setReceiveStaffName(staff.name)}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!receiveStaffName} onClick={async () => {
                  const { repair, hasAppAccess } = receiveStaffAction;
                  const notes = hasAppAccess
                    ? `Atölye çıkışı onaylandı - Teslim alan: ${receiveStaffName}`
                    : `Tamirattan geldi (manuel) - Teslim alan: ${receiveStaffName}`;
                  await handleStatusChange(repair, 'ready_for_pickup', notes);
                  setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, status: 'ready_for_pickup' } }));
                  toast.success(`Ürün tamirattan geldi! Teslim alan: ${receiveStaffName}`);
                  setShowReceiveStaffModal(false);
                }}>Teslim Al</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kontrol Edildi - Kasiyer Seçimi Modal */}
      {showCheckStaffModal && (
        <div className="modal-overlay">
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kontrol Eden Kasiyer</h2>
              <button className="close-btn" onClick={() => setShowCheckStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {staffList.map(staff => {
                  const isSelected = checkStaffName === staff.name;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => setCheckStaffName(staff.name)}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!checkStaffName} onClick={async () => {
                  await handleStatusChange(checkRepair, 'checked', `Kontrol eden: ${checkStaffName}`);
                  setSelectedRepairDetails(prev => ({ ...prev, repair: { ...prev.repair, status: 'checked' } }));
                  toast.success(`Kontrol edildi! Kontrol eden: ${checkStaffName}`);
                  setShowCheckStaffModal(false);
                }}>Kontrol Edildi</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bluetooth Yazıcı Ayarları Modal — geçici olarak gizlendi (ileride lazım olursa false→showBTPrinterModal) */}
      {false && showBTPrinterModal && (
        <div className="modal-overlay" onClick={() => setShowBTPrinterModal(false)}>
          <div className="modal" style={{ maxWidth: '500px', width: '92vw' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bluetooth size={20} color="#0ea5e9" /> Bluetooth Fiş Yazıcısı
              </h2>
              <button className="close-btn" onClick={() => setShowBTPrinterModal(false)}><X size={18} /></button>
            </div>
            <div style={{ padding: '20px' }}>
              {!isBluetoothSupported() ? (
                <div style={{ padding: '14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#991b1b', fontSize: '13px' }}>
                  Bu tarayıcı Web Bluetooth desteklemiyor. Mac üzerinde Chrome veya Edge kullanın (https:// veya localhost zorunlu).
                </div>
              ) : (
                <>
                  <p style={{ fontSize: '13px', color: '#64748b', marginTop: 0, marginBottom: '16px', lineHeight: 1.5 }}>
                    HPRT HM-A300E gibi taşınabilir Bluetooth fiş yazıcısını eşleştirin. Bu yazıcı sadece Tamirat fişlerinde "BT Müşteri / BT Tamirat" butonlarıyla kullanılır; sistem yazıcılarınız etkilenmez.
                  </p>

                  <div style={{ padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>EŞLEŞTİRİLMİŞ CİHAZ</div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <div style={{ fontSize: '14px', color: btPrinterInfo.name ? '#0f172a' : '#94a3b8', fontWeight: 600 }}>
                        {btPrinterInfo.name || 'Henüz eşleştirilmedi'}
                      </div>
                      {btPrinterInfo.name && (
                        <span style={{ fontSize: '11px', fontWeight: 600, color: btPrinterInfo.enabled ? '#059669' : '#94a3b8', background: btPrinterInfo.enabled ? '#ecfdf5' : '#f1f5f9', padding: '3px 10px', borderRadius: '6px' }}>
                          {btPrinterInfo.enabled ? 'AKTİF' : 'PASİF'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <button
                      onClick={async () => {
                        setBtPairing(true);
                        try {
                          const info = await pairBluetoothPrinter();
                          setBtPrinterInfo(getBluetoothPrinterInfo());
                          toast.success(`Eşleştirildi: ${info.name}`);
                        } catch (e) {
                          if (e?.name !== 'NotFoundError') toast.error(e?.message || 'Eşleştirme başarısız');
                        } finally {
                          setBtPairing(false);
                        }
                      }}
                      disabled={btPairing}
                      style={{ flex: 1, minWidth: '140px', padding: '10px 14px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: btPairing ? 'not-allowed' : 'pointer', opacity: btPairing ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <Bluetooth size={14} /> {btPairing ? 'Eşleştiriliyor...' : (btPrinterInfo.name ? 'Yeniden Eşleştir' : 'Eşleştir')}
                    </button>
                    {btPrinterInfo.name && (
                      <button
                        onClick={() => {
                          clearBluetoothPrinter();
                          setBtPrinterInfo(getBluetoothPrinterInfo());
                          toast.info('Bluetooth yazıcı kaldırıldı');
                        }}
                        style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ef4444', background: '#fff', color: '#ef4444', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Kaldır
                      </button>
                    )}
                  </div>

                  {btPrinterInfo.name && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '10px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>Yazıcıyı Aktif Et</div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Açıkken modal altında BT butonları görünür</div>
                        </div>
                        <div
                          onClick={() => {
                            const newVal = !btPrinterInfo.enabled;
                            setBluetoothPrinterEnabled(newVal);
                            setBtPrinterInfo(getBluetoothPrinterInfo());
                            toast.success(newVal ? 'Bluetooth yazıcı aktif' : 'Bluetooth yazıcı pasif');
                          }}
                          style={{
                            width: '46px', height: '26px', borderRadius: '26px', cursor: 'pointer',
                            background: btPrinterInfo.enabled ? 'linear-gradient(135deg, #10b981, #059669)' : '#94a3b8',
                            position: 'relative', transition: 'background 0.3s', flexShrink: 0
                          }}
                        >
                          <div style={{
                            width: '20px', height: '20px', borderRadius: '50%', background: '#fff',
                            position: 'absolute', top: '3px', left: btPrinterInfo.enabled ? '23px' : '3px',
                            transition: 'left 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }} />
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', marginBottom: '10px' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>Karakter Genişliği</div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Çizgiler/sütunlar bu değere göre çizilir</div>
                        </div>
                        <select
                          value={btCols}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setBluetoothCols(v);
                            setBtCols(v);
                            toast.success(`Karakter genişliği: ${v}`);
                          }}
                          style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff', cursor: 'pointer' }}
                        >
                          <option value={32}>32 (HPRT 58mm Font A)</option>
                          <option value={42}>42 (HPRT 58mm Font B)</option>
                          <option value={48}>48 (HPRT 80mm Font A)</option>
                        </select>
                      </div>

                      <button
                        disabled={btTesting}
                        onClick={async () => {
                          setBtTesting(true);
                          try {
                            await printRawToBluetooth(buildTestReceiptBytes());
                            toast.success('Test fişi gönderildi');
                          } catch (e) {
                            toast.error(e?.message || 'Bluetooth yazdırma hatası');
                          } finally {
                            setBtTesting(false);
                          }
                        }}
                        style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontSize: '13px', fontWeight: 600, cursor: btTesting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: btTesting ? 0.6 : 1 }}
                      >
                        <Printer size={14} /> {btTesting ? 'Gönderiliyor...' : 'Test Fişi Yazdır'}
                      </button>

                      <div style={{ marginTop: '14px', padding: '10px 12px', background: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', fontSize: '11px', color: '#854d0e', lineHeight: 1.5 }}>
                        <b>İpucu:</b> Tarayıcı yenilendiğinde bağlantı kopabilir. "Bluetooth yazıcı bağlantısı kayboldu" hatası alırsanız tekrar Eşleştir'e basın.
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className={`modal-overlay ${closingModal === 'help' ? 'closing' : ''}`}>
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Tamirat Ürünleri Yardım</h2>
              <button className="close-btn" onClick={() => handleCloseModal('help', setShowHelpModal)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><Wrench size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden müşterilerden teslim alınan tamirat ürünlerini takip edebilirsiniz. Altın ve gümüş ürünlerin tamir, onarım ve tamirat süreçlerini yönetebilirsiniz.</p>
              </div>

              {/* Yeni Kayıt */}
              <div className="help-section">
                <h3><Plus size={18} style={{ marginRight: '6px' }} /> Yeni Tamirat Kaydı Oluşturma</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>+ Yeni Kayıt</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Müşteri seçin veya yeni müşteri oluşturun</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Ürün tipini seçin (Altın/Gümüş)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Ürün açıklaması ve yapılacak işlemi yazın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">5</span>
                    <span>Fotoğraf ekleyin (opsiyonel ama önerilir)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">6</span>
                    <span>Ücret ve alınan ücret bilgilerini girin</span>
                  </div>
                </div>
              </div>

              {/* Durum Takibi */}
              <div className="help-section">
                <h3><BarChart3 size={18} style={{ marginRight: '6px' }} /> Durum Takibi</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num"><Inbox size={16} /></span>
                    <span><strong>Teslim Alındı:</strong> Ürün müşteriden alındı</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Truck size={16} /></span>
                    <span><strong>Atölyeye Gönderildi:</strong> Ürün ustaya gönderildi</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Wrench size={16} /></span>
                    <span><strong>Tamirata Başlandı:</strong> Ürün üzerinde çalışılıyor</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><CheckCircle size={16} /></span>
                    <span><strong>Tamirat Tamamlandı:</strong> İşlem bitti, müşteri bekliyor</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Gift size={16} /></span>
                    <span><strong>Teslim Edildi:</strong> Ürün müşteriye verildi</span>
                  </div>
                </div>
              </div>

              {/* Fotoğraf Ekleme */}
              <div className="help-section">
                <h3><Camera size={18} style={{ marginRight: '6px' }} /> Fotoğraf Ekleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Kayıt formunda <strong><Camera size={14} /> Kamera</strong> ile anlık çekim yapın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Veya <strong><Image size={14} /> Galeri</strong> ile mevcut fotoğrafları yükleyin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span><strong><Smartphone size={14} /> Terminal</strong> ile mobil cihazdan fotoğraf çektirin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Birden fazla fotoğraf ekleyebilirsiniz</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Terminal ile fotoğraf çekimi için mağazaya bağlı bir terminal kullanıcısının sisteme giriş yapmış olması gerekir.</div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Ürün teslim alınırken fotoğraf çekmek, olası anlaşmazlıkları önler.</div>
              </div>

              {/* Butonlar */}
              <div className="help-section">
                <h3><CircleDot size={18} style={{ marginRight: '6px' }} /> Buton Açıklamaları</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Plus size={18} /></span>
                    <div>
                      <strong>Yeni Kayıt</strong>
                      <p>Yeni tamirat kaydı oluşturur</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ScrollText size={18} /></span>
                    <div>
                      <strong>İşlem Geçmişi</strong>
                      <p>Tüm işlem loglarını gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pencil size={18} /></span>
                    <div>
                      <strong>Düzenle</strong>
                      <p>Kayıt bilgilerini günceller</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Trash2 size={18} /></span>
                    <div>
                      <strong>Sil</strong>
                      <p>Kaydı sistemden kaldırır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Printer size={18} /></span>
                    <div>
                      <strong>Müşteri Teslim Fişi Yazdır</strong>
                      <p>Müşteriye verilecek fiş çıktısı alır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ScrollText size={18} /></span>
                    <div>
                      <strong>Tamirat Fişi Yazdır</strong>
                      <p>Barkodlu iç kullanım fişi alır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ClipboardList size={18} /></span>
                    <div>
                      <strong>Durum Seçici</strong>
                      <p>Tamiratın hangi aşamada olduğunu günceller</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Arama ve Filtreleme */}
              <div className="help-section">
                <h3><Search size={18} style={{ marginRight: '6px' }} /> Arama ve Filtreleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>Arama:</strong> Fiş numarası, müşteri adı veya telefon ile arayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span><strong>Durum Filtreleri:</strong> Tümü, Bekleyen, Atölyede, Tamamlanan, Teslim Edildi</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span><strong>İstatistikler:</strong> Sayfa üstündeki kartlardan anlık durumu görüntüleyin</span>
                  </div>
                </div>
              </div>

              {/* Çoklu Ürün */}
              <div className="help-section">
                <h3><Plus size={18} style={{ marginRight: '6px' }} /> Çoklu Ürün Ekleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Tek bir tamirat kaydına birden fazla ürün ekleyebilirsiniz</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Ürün bilgisini yazıp <strong>Enter</strong>'a basarak yeni ürün satırı ekleyin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Her ürün için ayrı ayrı fotoğraf ekleyebilirsiniz</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Aynı müşterinin birden fazla ürünü varsa tek fiş altında toplayın.</div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={18} style={{ marginRight: '6px', color: '#f59e0b' }} /> İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Fiş numarasını müşteriye mutlaka verin</li>
                  <li>Teslim almadan önce ürünün fotoğrafını çekin</li>
                  <li>Alınan ücret ödeme garantisi sağlar</li>
                  <li>Tahmini teslim tarihi belirleyin</li>
                  <li>Durum değişikliklerini düzenli güncelleyin</li>
                  <li>Notlar kısmına önemli detayları yazın</li>
                  <li>Terminal fotoğrafı için mobil cihazın bağlı olduğundan emin olun</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terminal Seçim Modal */}
      {showTerminalModal && (
        <div className={`modal-overlay ${closingModal === 'terminal' ? 'closing' : ''}`}>
          <div className="modal terminal-select-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Smartphone size={20} style={{ marginRight: '8px' }} /> Terminal Seç</h2>
              <button className="close-btn" onClick={() => handleCloseModal('terminal', setShowTerminalModal)}>✕</button>
            </div>
            <div className="modal-body">
              <p className="terminal-modal-desc">
                Fotoğraf çekmek için bir terminal seçin. Seçilen terminale fotoğraf çekim isteği gönderilecek.
              </p>
              {loadingTerminals ? (
                <div className="loading-state">Terminaller yükleniyor...</div>
              ) : terminals.length === 0 ? (
                <div className="empty-state">
                  <span><Smartphone size={48} /></span>
                  <p>Bu mağazaya bağlı aktif terminal bulunamadı.</p>
                  <small>Terminal kullanıcısının sisteme giriş yapmış olması gerekir.</small>
                </div>
              ) : (
                <div className="terminal-list">
                  {terminals.map(terminal => (
                    <div
                      key={terminal.id}
                      className="terminal-item"
                      onClick={() => sendPhotoRequest(terminal)}
                    >
                      <div className="terminal-icon"><Smartphone size={24} /></div>
                      <div className="terminal-info">
                        <span className="terminal-name">{terminal.name}</span>
                        <span className="terminal-email">{terminal.email}</span>
                      </div>
                      <div className="terminal-arrow">→</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ürün Teslim Al Modal */}
      {showDeliverModal && (
        <div className={`modal-overlay ${closingModal === 'deliver' ? 'closing' : ''}`}>
          <div className="deliver-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Gift size={22} /> Tamirattan Ürün Teslim Al</h2>
              <button className="close-btn" onClick={() => handleCloseModal('deliver', setShowDeliverModal)}>×</button>
            </div>
            <div className="deliver-modal-body">
              {/* Sol Taraf - Barkod Okutma */}
              <div className="barcode-section">
                <h3><Search size={20} /> Barkod Okut</h3>
                <form onSubmit={handleDeliverBarcodeSubmit}>
                  <input
                    ref={deliverBarcodeInputRef}
                    type="text"
                    value={deliverBarcodeInput}
                    onChange={e => setDeliverBarcodeInput(e.target.value)}
                    placeholder="Barkodu okutun veya yazın..."
                    className="barcode-input"
                    autoFocus
                  />
                  <button type="submit" className="scan-btn">
                    <CheckCircle size={18} /> Ekle
                  </button>
                </form>
                <div className="barcode-info">
                  <p>Fiş üzerindeki barkodu okutarak ürünü otomatik seçin</p>
                </div>
                <div className="selected-count">
                  <strong>{selectedForDeliver.length}</strong> ürün seçildi
                </div>
              </div>

              {/* Sağ Taraf - Ürün Listesi */}
              <div className="products-section">
                <div className="products-header">
                  <h3><Inbox size={20} /> Teslim Bekleyen Ürünler ({readyForDeliverRepairs.length})</h3>
                  {readyForDeliverRepairs.length > 0 && (
                    <button className="select-all-btn" onClick={toggleDeliverSelectAll}>
                      {selectedForDeliver.length === readyForDeliverRepairs.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                    </button>
                  )}
                </div>
                <div className="products-list">
                  {readyForDeliverRepairs.length === 0 ? (
                    <div className="no-products">
                      <Inbox size={48} />
                      <p>Teslim bekleyen ürün yok</p>
                    </div>
                  ) : (
                    readyForDeliverRepairs.map(repair => {
                      const isSelected = selectedForDeliver.includes(repair.id);
                      return (
                        <div
                          key={repair.id}
                          className={`product-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleDeliverSelection(repair.id)}
                        >
                          <div className="product-checkbox">
                            {isSelected && <CheckCircle size={18} />}
                          </div>
                          <div className="product-info">
                            <div className="product-code">{repair.repair_code}</div>
                            <div className="product-customer">{repair.customer_name}</div>
                            <div className="product-desc">
                              {repair.item_description || repair.problem_description || '-'}
                            </div>
                          </div>
                          <div className="product-date">
                            {new Date(repair.created_at).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeliverModal(false)}>İptal</button>
              <button
                className="btn-deliver"
                onClick={() => { setDeliverStaffName(''); setShowBulkDeliverStaffModal(true); }}
                disabled={selectedForDeliver.length === 0}
              >
                <Gift size={18} /> {selectedForDeliver.length} Ürünü Teslim Al
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toplu Teslim Alma - Kasiyer Seçimi Modal */}
      {showBulkDeliverStaffModal && (
        <div className="modal-overlay">
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Teslim Alan Kasiyer</h2>
              <button className="close-btn" onClick={() => setShowBulkDeliverStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {staffList.map(staff => {
                  const isSelected = deliverStaffName === staff.name;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => setDeliverStaffName(staff.name)}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!deliverStaffName} onClick={async () => {
                  await deliverSelectedProducts();
                  setShowBulkDeliverStaffModal(false);
                }}>Teslim Al</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tamirat Noktası Seçim Modalı - admin manuel atölyeye/tamirata gönderirken */}
      {statusPointModal && (
        <div className="modal-overlay" onClick={() => { setStatusPointModal(null); setStatusPointId(''); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '420px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', padding: '14px 18px' }}>
              <h2 style={{ color: 'white', fontSize: '1.05rem', margin: 0 }}><MapPin size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Tamirat Noktası Seçin</h2>
              <button className="close-btn" onClick={() => { setStatusPointModal(null); setStatusPointId(''); }} style={{ color: '#fff' }}>×</button>
            </div>
            <div style={{ padding: '18px' }}>
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0, marginBottom: '12px' }}>
                <strong>{getStatusLabel(statusPointModal.newStatus)}</strong> durumuna geçmek için ürünün gönderildiği tamirat noktasını seçin.
              </p>
              <select
                value={statusPointId}
                onChange={e => setStatusPointId(e.target.value)}
                autoFocus
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#fff' }}
              >
                <option value="">Tamirat noktası seçin...</option>
                {repairPoints.map(point => (
                  <option key={point.id} value={point.id}>{point.name}{point.has_app_access ? ' (Uygulamalı)' : ''}</option>
                ))}
              </select>
              {repairPoints.length === 0 && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#dc2626' }}>
                  Henüz tamirat noktası yok. Önce eklemelisiniz.
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '18px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setStatusPointModal(null); setStatusPointId(''); }}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Vazgeç
                </button>
                <button
                  onClick={confirmStatusPoint}
                  disabled={!statusPointId}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: statusPointId ? '#f59e0b' : '#cbd5e1', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: statusPointId ? 'pointer' : 'not-allowed' }}
                >
                  Onayla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tekrar Atölyeye Gönder Modal */}
      {reworkModal && (
        <div className="modal-overlay" onClick={() => { if (!reworkSaving) setReworkModal(null); }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', width: '90%', maxWidth: '460px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: 'white', padding: '14px 18px' }}>
              <h2 style={{ color: 'white', fontSize: '1.05rem', margin: 0 }}><RotateCcw size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Tekrar Atölyeye Gönder</h2>
              <button className="close-btn" onClick={() => setReworkModal(null)} disabled={reworkSaving} style={{ color: '#fff' }}>×</button>
            </div>
            <div style={{ padding: '18px' }}>
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0, marginBottom: '12px' }}>
                Fiş <strong>{reworkModal.repair.repair_code}</strong> — {reworkModal.repair.customer_name}. Ürün istenildiği gibi olmadıysa sebebini yazıp tekrar atölyeye gönderin.
              </p>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Tamirat Noktası</label>
              <select
                value={reworkPointId}
                onChange={e => setReworkPointId(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#fff', marginBottom: '12px' }}
              >
                <option value="">Tamirat noktası seçin...</option>
                {repairPoints.map(point => (
                  <option key={point.id} value={point.id}>{point.name}{point.has_app_access ? ' (Uygulamalı)' : ''}</option>
                ))}
              </select>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Sebep / Atölyeye Not *</label>
              <textarea
                value={reworkReason}
                onChange={e => setReworkReason(e.target.value)}
                autoFocus
                rows={3}
                placeholder="Örn: Taş yerine tam oturmamış, kilit hâlâ açılıyor..."
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '18px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setReworkModal(null)}
                  disabled={reworkSaving}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  Vazgeç
                </button>
                <button
                  onClick={confirmRework}
                  disabled={reworkSaving || !reworkPointId || !reworkReason.trim()}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: (reworkPointId && reworkReason.trim() && !reworkSaving) ? '#dc2626' : '#cbd5e1', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: (reworkPointId && reworkReason.trim() && !reworkSaving) ? 'pointer' : 'not-allowed' }}
                >
                  {reworkSaving ? 'Gönderiliyor...' : 'Atölyeye Gönder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toplu Tamirat Çıkışı Modal */}
      {showBulkDispatchModal && (
        <div className={`modal-overlay ${closingModal === 'bulkDispatch' ? 'closing' : ''}`}>
          <div className="deliver-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)', color: 'white' }}>
              <h2 style={{ color: 'white' }}><Truck size={22} /> Toplu Tamirat Çıkışı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('bulkDispatch', setShowBulkDispatchModal)} style={{ color: '#000' }}>×</button>
            </div>
            <div className="deliver-modal-body">
              {/* Sol Taraf - Barkod + Tamirat Noktası */}
              <div className="barcode-section">
                <h3><Search size={20} /> Barkod Okut</h3>
                <form onSubmit={handleBulkDispatchBarcodeSubmit}>
                  <input
                    ref={bulkDispatchBarcodeInputRef}
                    type="text"
                    value={bulkDispatchBarcodeInput}
                    onChange={e => setBulkDispatchBarcodeInput(e.target.value)}
                    placeholder="Barkodu okutun veya yazın..."
                    className="barcode-input"
                    autoFocus
                  />
                  <button type="submit" className="scan-btn">
                    <CheckCircle size={18} /> Ekle
                  </button>
                </form>
                <div className="barcode-info">
                  <p>Fiş üzerindeki barkodu okutarak ürünü otomatik seçin</p>
                </div>

                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                    <MapPin size={14} style={{ verticalAlign: 'middle', marginRight: '4px', color: '#0f766e' }} />
                    Gönderilecek Tamirat Noktası *
                  </label>
                  <select
                    value={bulkDispatchPointId}
                    onChange={e => setBulkDispatchPointId(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#fff' }}
                  >
                    <option value="">Tamirat noktası seçin...</option>
                    {repairPoints.map(point => (
                      <option key={point.id} value={point.id}>{point.name}{point.has_app_access ? ' (Uygulamalı)' : ''}</option>
                    ))}
                  </select>
                  {repairPoints.length === 0 && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#dc2626' }}>
                      Henüz tamirat noktası yok. Önce eklemelisiniz.
                    </div>
                  )}
                </div>

                <div className="selected-count" style={{ marginTop: '16px' }}>
                  <strong>{bulkDispatchSelected.length}</strong> ürün seçildi
                </div>
              </div>

              {/* Sağ Taraf - Ürün Listesi */}
              <div className="products-section">
                <div className="products-header">
                  <h3><Inbox size={20} /> Gönderim Bekleyen Ürünler ({receivedRepairs.length})</h3>
                  {receivedRepairs.length > 0 && (
                    <button className="select-all-btn" onClick={toggleBulkDispatchSelectAll}>
                      {bulkDispatchSelected.length === receivedRepairs.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                    </button>
                  )}
                </div>
                <div className="products-list">
                  {receivedRepairs.length === 0 ? (
                    <div className="no-products">
                      <Inbox size={48} />
                      <p>Gönderim bekleyen ürün yok</p>
                    </div>
                  ) : (
                    receivedRepairs.map(repair => {
                      const isSelected = bulkDispatchSelected.includes(repair.id);
                      return (
                        <div
                          key={repair.id}
                          className={`product-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleBulkDispatchSelection(repair.id)}
                        >
                          <div className="product-checkbox">
                            {isSelected && <CheckCircle size={18} />}
                          </div>
                          <div className="product-info">
                            <div className="product-code">{repair.repair_code}</div>
                            <div className="product-customer">{repair.customer_name}</div>
                            <div className="product-desc">
                              {repair.item_description || repair.problem_description || '-'}
                            </div>
                          </div>
                          <div className="product-date">
                            {new Date(repair.created_at).toLocaleDateString('tr-TR')}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('bulkDispatch', setShowBulkDispatchModal)} disabled={bulkDispatching}>İptal</button>
              <button
                className="btn-deliver"
                onClick={dispatchSelectedProducts}
                disabled={bulkDispatchSelected.length === 0 || !bulkDispatchPointId || bulkDispatching}
                style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)' }}
              >
                <Truck size={18} /> {bulkDispatching ? 'Gönderiliyor...' : `${bulkDispatchSelected.length} Ürünü Tamirata Gönder`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tamirat Ödeme Modal */}
      {showRepairPaymentModal && (repairPaymentRepair || repairPaymentMode === 'deposit') && (() => {
        const isDeposit = repairPaymentMode === 'deposit';
        // Kapora modunda toplam = formdaki tamirat ücreti; alınan ücret bunu geçemez
        const totalPrice = isDeposit ? getFormNetPrice() : (Number(repairPaymentRepair.estimated_price) || 0);
        const depositAmount = isDeposit ? 0 : (Number(repairPaymentRepair.deposit_amount) || 0);
        const repairTotal = totalPrice - depositAmount;
        const totalPaid = getRepairPaymentsTotal();
        const remaining = Math.max(0, repairTotal - totalPaid);
        const showInputs = remaining > 0.01;

        return (
        <div className="modal-overlay">
          <div className="modal payment-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '10px 20px', flexShrink: 0 }}>
              <h2>{isDeposit ? 'Ücret Al' : 'Ödeme Al'}</h2>
              <button className="close-btn" onClick={() => setShowRepairPaymentModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '10px 20px', overflowY: 'auto', flex: 1 }}>
              {!isDeposit && (
                <div className="payment-info" style={{ padding: '8px 12px', marginBottom: '8px' }}>
                  <div className="payment-info-row" style={{ marginBottom: '2px' }}>
                    <span>Müşteri:</span>
                    <strong>{repairPaymentRepair.customer_name}</strong>
                  </div>
                  <div className="payment-info-row" style={{ marginBottom: 0 }}>
                    <span>Borç:</span>
                    <strong>₺{repairTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                  </div>
                </div>
              )}
              {isDeposit && formData.customer_name && (
                <div className="payment-info" style={{ padding: '8px 12px', marginBottom: '8px' }}>
                  <div className="payment-info-row" style={{ marginBottom: 0 }}>
                    <span>Müşteri:</span>
                    <strong>{formData.customer_name}</strong>
                  </div>
                </div>
              )}

              {/* Alınan Ödemeler - kompakt chip */}
              {repairPayments.length > 0 && (
                <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                  {repairPayments.map(payment => (
                    <div key={payment.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontSize: '0.78rem' }}>
                      <span style={{ fontWeight: 600, color: '#334155' }}>{payment.method}</span>
                      {payment.bankAccount && <span style={{ color: '#6d28d9', fontSize: '0.7rem' }}>{payment.bankAccount.account_name}</span>}
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>₺{payment.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                      <button onClick={() => removeRepairPayment(payment.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '13px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                    Toplam: <strong style={{ color: '#16a34a' }}>₺{totalPaid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                    {remaining > 0.01 && (
                      <> · Kalan: <strong style={{ color: '#f59e0b' }}>₺{remaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong></>
                    )}
                    {remaining <= 0.01 && <> · <strong style={{ color: '#16a34a' }}>Tamamlandı</strong></>}
                  </span>
                </div>
              )}

              {showInputs && (
                <div className="form-group">
                  <label>{isDeposit ? 'Alınan Ücret (₺)' : 'Ödeme Tutarı (₺)'}</label>
                  <PriceInput
                    value={repairPaymentAmount}
                    onChange={(val) => setRepairPaymentAmount(val)}
                    placeholder="Tutar girin..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addRepairPayment();
                      if (e.key === 'Tab' && !repairPaymentAmount) {
                        e.preventDefault();
                        if (remaining > 0 && remaining !== Infinity) setRepairPaymentAmount(remaining.toFixed(2));
                      }
                    }}
                    autoFocus
                  />
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>💡 Tab ile {isDeposit ? 'kalan' : 'tüm'} tutarı doldur</span>
                </div>
              )}

              {showInputs && <div className="form-group">
                <label>Ödeme Yöntemi</label>
                <div className="payment-methods">
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Nakit' ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(repairPaymentAmount);
                      if (amt > 0) { addRepairPayment('Nakit'); }
                      else { setRepairPaymentMethod('Nakit'); }
                    }}
                  >
                    <Banknote size={16} /> Nakit
                  </button>
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Kredi Kartı' ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(repairPaymentAmount);
                      if (amt > 0) { addRepairPayment('Kredi Kartı'); }
                      else { setRepairPaymentMethod('Kredi Kartı'); }
                    }}
                  >
                    <CreditCard size={16} /> Kredi Kartı
                  </button>
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Havale/EFT' ? 'active' : ''}`}
                    onClick={() => setRepairPaymentMethod('Havale/EFT')}
                  >
                    <Building2 size={16} /> Havale/EFT
                  </button>
                  <button
                    className={`payment-method-btn ${repairPaymentMethod === 'Açık Hesap' ? 'active' : ''}`}
                    onClick={() => {
                      const amt = parseFloat(repairPaymentAmount);
                      if (amt > 0) { addRepairPayment('Açık Hesap'); }
                      else { setRepairPaymentMethod('Açık Hesap'); }
                    }}
                  >
                    <Wallet size={16} /> Açık Hesap
                  </button>
                </div>
              </div>}

              {/* Havale/EFT Banka Hesapları */}
              {repairPaymentMethod === 'Havale/EFT' && (
                <div className="form-group">
                  <label>Banka Hesabı Seçin</label>
                  <div className="collection-bank-accounts">
                    {repairBankAccounts.length === 0 ? (
                      <p className="no-accounts-text">Kayıtlı banka hesabı bulunamadı.</p>
                    ) : (
                      repairBankAccounts.map(account => (
                        <div
                          key={account.id}
                          className={`bank-account-option ${selectedRepairBankAccount?.id === account.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedRepairBankAccount(account);
                            const amt = parseFloat(repairPaymentAmount);
                            if (amt > 0) addRepairPayment('Havale/EFT', account);
                          }}
                        >
                          <span className="account-icon">
                            {account.account_type === 'company' ? '🏢' : '👤'}
                          </span>
                          <span className="account-name">{account.account_name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowRepairPaymentModal(false)}>İptal</button>
              <button
                className="btn-save"
                onClick={
                  repairPaymentMode === 'deposit' ? completeDepositPayment :
                  repairPaymentMode === 'topup' ? completeAnytimeRepairPayment :
                  completeRepairPaymentAndDeliver
                }
                // 'delivery' modunda kalan tutarın TAMAMI ödenmeden teslim edilemez; 'deposit'
                // ve 'topup' (istediği an ödeme al) modlarında kısmi tutar da kaydedilebilir.
                disabled={repairPaymentMode === 'delivery' ? remaining > 0.01
                  : repairPaymentMode === 'deposit' ? (depositPaymentSaving || (totalPaid <= 0 && !(editingRepair && depositModalInitialIdsRef.current.length > 0)))
                  : totalPaid <= 0}
              >
                {isDeposit ? 'Ücreti Kaydet' : 'Ödemeyi Kaydet'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Müşteriye Teslim Onay Modal */}
      {showDeliverConfirmModal && (
        <div className={`modal-overlay ${closingModal === 'deliverConfirm' ? 'closing' : ''}`}>
          <div className="deliver-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Gift size={22} /> Müşteriye Teslim</h2>
              <button className="close-btn" onClick={() => handleCloseModal('deliverConfirm', setShowDeliverConfirmModal)}>×</button>
            </div>
            <div className="modal-body">
              <div className="confirm-info">
                <p><strong>Müşteri:</strong> {selectedRepairDetails?.repair?.customer_name}</p>
                <p><strong>Ürün:</strong> {selectedRepairDetails?.repair?.item_description}</p>
              </div>
              <div className="confirm-code-section">
                <label>{repairOverrideEnabled ? 'Fiş Kodu veya Yönetici Şifresi' : 'Fiş Kodunu Girin'}</label>
                <p className="code-hint">{repairOverrideEnabled ? 'Müşteriye verilen fiş kodunu veya yönetici şifresini girin' : 'Müşteriye verilen fiş üzerindeki kodu girin'}</p>
                <input
                  type="text"
                  value={deliverConfirmCode}
                  onChange={e => {
                    setDeliverConfirmCode(e.target.value);
                    setDeliverConfirmError('');
                  }}
                  placeholder="Örn: TAD-ABC123"
                  className="code-input"
                  autoFocus
                />
                {deliverConfirmError && (
                  <div className="code-error">{deliverConfirmError}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => handleCloseModal('deliverConfirm', setShowDeliverConfirmModal)}>İptal</button>
              <button className="btn-deliver" onClick={confirmDeliverToCustomer}>
                <CheckCircle size={18} /> Teslim Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Atölye Tamamlananlar Modal */}
      {showWorkshopModal && (
        <div className={`modal-overlay ${closingModal === 'workshop' ? 'closing' : ''}`}>
          <div className="workshop-completed-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><CheckCircle size={22} /> Tamamlananlar ({completedWorkshopRepairs.length})</h2>
              <button className="close-btn" onClick={() => handleCloseModal('workshop', setShowWorkshopModal)}>×</button>
            </div>
            <div className="workshop-completed-body">
              {completedWorkshopRepairs.length === 0 ? (
                <div className="empty-state">
                  <Inbox size={64} />
                  <p>Tamamlanmış ürün bulunamadı</p>
                </div>
              ) : (
                <div className="workshop-cards-grid">
                  {completedWorkshopRepairs.map(repair => (
                    <div key={repair.id} className="workshop-card">
                      <div className="card-header">
                        <span className="card-code">{repair.repair_code}</span>
                        <span className="card-status">
                          <CheckCircle size={14} /> Tamamlandı
                        </span>
                      </div>
                      <div className="card-body">
                        <h4 className="card-product">{repair.item_description || 'Ürün'}</h4>
                        <p><span>Müşteri:</span> {repair.customer_name}</p>
                        {repair.customer_phone && <p><span>Tel:</span> {repair.customer_phone}</p>}
                        <p><span>Sorun:</span> {repair.problem_description || '-'}</p>
                        <p><span>Mağaza:</span> {getStoreName(repair.store_id)}</p>
                        <p><span>Tarih:</span> {new Date(repair.created_at).toLocaleDateString('tr-TR')}</p>
                      </div>
                      <div className="card-actions">
                        <button
                          className="btn-dispatch-card"
                          onClick={() => {
                            setWorkshopDispatchRepair(repair);
                            setWorkshopDispatchNote('');
                            setShowWorkshopDispatchModal(true);
                          }}
                        >
                          <Send size={16} /> Çıkış Yap
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Atölye Çıkış Onay Modal */}
      {showWorkshopDispatchModal && workshopDispatchRepair && (
        <div className={`modal-overlay ${closingModal === 'workshopDispatch' ? 'closing' : ''}`}>
          <div className="dispatch-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Send size={22} /> Ürün Çıkışı</h2>
              <button className="close-btn" onClick={() => handleCloseModal('workshopDispatch', setShowWorkshopDispatchModal)}>×</button>
            </div>
            <div className="dispatch-modal-body">
              <div className="dispatch-info-card">
                <div className="dispatch-icon">
                  <Store size={48} />
                </div>
                <div className="dispatch-details">
                  <h3>Gönderilecek Mağaza</h3>
                  <p className="store-name">{getStoreName(workshopDispatchRepair.store_id)}</p>
                </div>
              </div>

              <div className="dispatch-product-info">
                <div className="info-row">
                  <span className="label">Fiş Kodu:</span>
                  <span className="value code">{workshopDispatchRepair.repair_code}</span>
                </div>
                <div className="info-row">
                  <span className="label">Müşteri:</span>
                  <span className="value">{workshopDispatchRepair.customer_name}</span>
                </div>
                <div className="info-row">
                  <span className="label">Ürün:</span>
                  <span className="value">{workshopDispatchRepair.item_description || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Sorun:</span>
                  <span className="value">{workshopDispatchRepair.problem_description || '-'}</span>
                </div>
              </div>

              <div className="dispatch-warning">
                <MapPin size={18} />
                <p>Bu ürün <strong>{getStoreName(workshopDispatchRepair.store_id)}</strong> mağazasına gönderilecek ve durum "Mağazaya Yolda" olarak güncellenecek.</p>
              </div>

              <label className="repair-not-done-checkbox">
                <input
                  type="checkbox"
                  checked={repairNotDone}
                  onChange={(e) => setRepairNotDone(e.target.checked)}
                />
                <span className="checkbox-label">Tamirat yapılmadı</span>
                <span className="checkbox-hint">Bu seçenek işaretlenirse ürün "tamirat yapılmadı" notu ile gönderilir</span>
              </label>

              <label className="workshop-dispatch-note-label">Çıkış Notu <span>*</span></label>
              <textarea
                className={`workshop-dispatch-note ${workshopDispatchNote.trim() ? '' : 'missing'}`}
                placeholder="Yapılan işlemi / ürünle ilgili notu yazın (zorunlu)"
                rows={3}
                value={workshopDispatchNote}
                onChange={e => setWorkshopDispatchNote(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => { setShowWorkshopDispatchModal(false); setRepairNotDone(false); }}>İptal</button>
              <button className="btn-dispatch" onClick={workshopDispatchToStore} disabled={!workshopDispatchNote.trim()}>
                <Send size={18} /> Çıkışını Yap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tamirat Noktası Seçim Modalı */}
      {showRepairPointModal && (
        <div className={`modal-overlay ${closingModal === 'repairPoint' ? 'closing' : ''}`}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={20} /> Tamirat Noktası Seç
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('repairPoint', setShowRepairPointModal)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {repairPoints.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#6b7280' }}>
                  <MapPin size={40} style={{ marginBottom: '12px', opacity: 0.4 }} />
                  <p style={{ margin: '0 0 16px 0' }}>Henüz tamirat noktası eklenmemiş.</p>
                  <button
                    onClick={() => {
                      setShowRepairPointManageModal(true);
                      setRepairPointForm({ name: '', contact_info: '', has_app_access: false, user_id: '' });
                      setEditingRepairPoint(null);
                    }}
                    style={{
                      padding: '10px 20px', borderRadius: '8px', border: 'none',
                      background: '#f97316', color: 'white', cursor: 'pointer', fontWeight: '500'
                    }}
                  >
                    <Plus size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Tamirat Noktası Ekle
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {repairPoints.map(point => (
                      <div
                        key={point.id}
                        onClick={() => sendToRepairPoint(point.id)}
                        style={{
                          padding: '14px 16px', borderRadius: '10px', border: '1px solid #e5e7eb',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          transition: 'all 0.2s', background: '#fff'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.background = '#fff7ed'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '15px', color: '#1f2937' }}>{point.name}</div>
                          {point.contact_info && (
                            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{point.contact_info}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {point.has_app_access ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                              <span style={{
                                fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                                background: '#dcfce7', color: '#166534', fontWeight: '500'
                              }}>Uygulama Erişimi</span>
                              {point.user_name && <span style={{ fontSize: '11px', color: '#6b7280' }}>{point.user_name}</span>}
                            </div>
                          ) : (
                            <span style={{
                              fontSize: '11px', padding: '3px 8px', borderRadius: '12px',
                              background: '#f3f4f6', color: '#6b7280', fontWeight: '500'
                            }}>Manuel</span>
                          )}
                          <Send size={16} style={{ color: '#f97316' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                      onClick={() => {
                        setShowRepairPointManageModal(true);
                        setRepairPointForm({ name: '', contact_info: '', has_app_access: false, user_id: '' });
                        setEditingRepairPoint(null);
                      }}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                        background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px',
                        display: 'flex', alignItems: 'center', gap: '6px'
                      }}
                    >
                      <Pencil size={14} /> Noktaları Yönet
                    </button>
                    <button
                      onClick={() => handleCloseModal('repairPoint', setShowRepairPointModal)}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                        background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px'
                      }}
                    >
                      İptal
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Şube Transferi Modalı - müşteri farklı şubeden teslim almak isterse */}
      {showTransferModal && (
        <div className={`modal-overlay ${closingModal === 'transfer' ? 'closing' : ''}`}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)', color: 'white' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeftRight size={20} /> Şube Transferi
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('transfer', setShowTransferModal)}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: '#64748b' }}>
                Müşteri farklı bir şubeden teslim almak isterse, tamiratı o şubeye transfer edin. Kayıt "yolda" durumuna geçer; hedef şube "Tamirattan Geldi" ile teslim alınca müşteriye teslime hazır olur.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {stores
                  // Şube transferi sadece Osmangazi (id 2) ve Yeşilsu (id 3) arasında yapılabiliyor
                  .filter(s => [2, 3].includes(s.id) && s.id !== (transferringRepair?.transferred_to_store_id || transferringRepair?.store_id))
                  .map(store => (
                    <div
                      key={store.id}
                      onClick={() => {
                        setTransferTargetStore(store);
                        setTransferStaffName('');
                        setShowTransferModal(false);
                        setShowTransferStaffModal(true);
                      }}
                      style={{
                        padding: '14px 16px', borderRadius: '10px', border: '1px solid #e5e7eb',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        transition: 'all 0.2s', background: '#fff'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#a855f7'; e.currentTarget.style.background = '#faf5ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Store size={16} style={{ color: '#7e22ce' }} />
                        <span style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937' }}>{store.name}</span>
                      </div>
                      <ArrowLeftRight size={16} style={{ color: '#a855f7' }} />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Şube Transferi - Kasiyer Seçimi Modalı */}
      {showTransferStaffModal && (
        <div className="modal-overlay">
          <div className="modal staff-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Transferi Yapan Kasiyer</h2>
              <button className="close-btn" onClick={() => setShowTransferStaffModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="staff-list">
                {staffList.map(staff => {
                  const isSelected = transferStaffName === staff.name;
                  return (
                    <div key={staff.id} className={`staff-item ${isSelected ? 'selected' : ''}`} onClick={() => setTransferStaffName(staff.name)}>
                      <div className={`staff-avatar ${staff.photo_url ? 'has-photo' : ''}`}>
                        {isSelected ? <Check size={24} strokeWidth={3} /> : staff.photo_url ? <img src={`${IMAGE_HOST}${staff.photo_url}`} alt={staff.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : staff.name.charAt(0)}
                      </div>
                      <div className="staff-info"><span className="staff-name">{staff.name}</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="staff-modal-actions">
                <button className="confirm-staff-btn" disabled={!transferStaffName} onClick={() => transferRepairToStore(transferTargetStore.id, transferStaffName)}>
                  Transfer Et
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tamirat Noktaları Yönetim Modalı */}
      {showRepairPointManageModal && (
        <div className="modal-overlay" style={{ zIndex: 10001 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white' }}>
              <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={20} /> Tamirat Noktaları Yönetimi
              </h2>
              <button className="close-btn" onClick={() => { setShowRepairPointManageModal(false); setEditingRepairPoint(null); setRepairPointForm({ name: '', contact_info: '', has_app_access: false, user_id: '' }); }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {/* Ekleme/Düzenleme Formu */}
              <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '16px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>
                  {editingRepairPoint ? 'Tamirat Noktası Düzenle' : 'Yeni Tamirat Noktası Ekle'}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <input
                    type="text"
                    placeholder="Tamirat noktası adı *"
                    value={repairPointForm.name}
                    onChange={e => setRepairPointForm(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
                      fontSize: '14px', outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="İletişim bilgisi (telefon, adres vb.)"
                    value={repairPointForm.contact_info}
                    onChange={e => setRepairPointForm(prev => ({ ...prev, contact_info: e.target.value }))}
                    style={{
                      padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
                      fontSize: '14px', outline: 'none'
                    }}
                  />
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
                    padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff'
                  }}>
                    <input
                      type="checkbox"
                      checked={repairPointForm.has_app_access}
                      onChange={e => {
                        setRepairPointForm(prev => ({ ...prev, has_app_access: e.target.checked, user_id: '' }));
                        if (e.target.checked && workshopUsers.length === 0) fetchWorkshopUsers();
                      }}
                      style={{ marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px', color: '#1f2937' }}>Uygulama Erisimi</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', lineHeight: '1.4' }}>
                        Bu secili ise karsi tarafin uygulamaya erisimi vardir. Urunu teslim alip tamirata baslayip tamamladiktan sonra size geri gonderebilir.
                        Karsi taraf gondermeden siz teslim alamaz ve musteriye teslim edemezsiniz.
                      </div>
                    </div>
                  </label>
                  {repairPointForm.has_app_access && (
                    <div style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff' }}>
                      <div style={{ fontWeight: '500', fontSize: '13px', color: '#374151', marginBottom: '6px' }}>Atölye Kullanıcısı Seç *</div>
                      {workshopUsers.length === 0 ? (
                        <div style={{ fontSize: '13px', color: '#ef4444' }}>
                          Henüz atölye rolünde kullanıcı yok. Kullanıcılar sayfasından "atolye" rolünde kullanıcı ekleyin.
                        </div>
                      ) : (
                        <select
                          value={repairPointForm.user_id}
                          onChange={e => setRepairPointForm(prev => ({ ...prev, user_id: e.target.value }))}
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
                            fontSize: '14px', outline: 'none', background: '#fff'
                          }}
                        >
                          <option value="">-- Kullanıcı seçin --</option>
                          {workshopUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={saveRepairPoint}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                        background: editingRepairPoint ? '#6366f1' : '#10b981', color: 'white',
                        cursor: 'pointer', fontWeight: '500', fontSize: '14px'
                      }}
                    >
                      {editingRepairPoint ? 'Güncelle' : 'Ekle'}
                    </button>
                    {editingRepairPoint && (
                      <button
                        onClick={() => { setEditingRepairPoint(null); setRepairPointForm({ name: '', contact_info: '', has_app_access: false, user_id: '' }); }}
                        style={{
                          padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db',
                          background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '14px'
                        }}
                      >
                        Vazgeç
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Mevcut Noktalar Listesi */}
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#374151' }}>Mevcut Tamirat Noktaları</h4>
              {repairPoints.length === 0 ? (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0' }}>Henüz tamirat noktası yok.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {repairPoints.map(point => (
                    <div key={point.id} style={{
                      padding: '12px 14px', borderRadius: '8px', border: '1px solid #e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>{point.name}</div>
                        {point.contact_info && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{point.contact_info}</div>
                        )}
                        {point.has_app_access ? (
                          <div style={{ marginTop: '4px' }}>
                            <span style={{
                              fontSize: '11px', padding: '2px 6px', borderRadius: '10px',
                              background: '#dcfce7', color: '#166534', fontWeight: '500', display: 'inline-block'
                            }}>Uygulama Erişimli</span>
                            {point.user_name && (
                              <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>({point.user_name})</span>
                            )}
                          </div>
                        ) : (
                          <span style={{
                            fontSize: '11px', padding: '2px 6px', borderRadius: '10px',
                            background: '#f3f4f6', color: '#6b7280', fontWeight: '500', marginTop: '4px', display: 'inline-block'
                          }}>Manuel</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => {
                            setEditingRepairPoint(point);
                            setRepairPointForm({ name: point.name, contact_info: point.contact_info || '', has_app_access: !!point.has_app_access, user_id: point.user_id || '' });
                            if (point.has_app_access && workshopUsers.length === 0) fetchWorkshopUsers();
                          }}
                          style={{
                            padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db',
                            background: '#fff', cursor: 'pointer', color: '#6366f1'
                          }}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => {
                            showConfirmation({
                              title: 'Tamirat Noktası Sil',
                              message: `"${point.name}" tamirat noktasını silmek istediğinize emin misiniz?`,
                              type: 'danger',
                              confirmText: 'Sil',
                              onConfirm: () => deleteRepairPoint(point.id)
                            });
                          }}
                          style={{
                            padding: '6px 10px', borderRadius: '6px', border: '1px solid #fecaca',
                            background: '#fff', cursor: 'pointer', color: '#ef4444'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => { setShowRepairPointManageModal(false); setEditingRepairPoint(null); setRepairPointForm({ name: '', contact_info: '', has_app_access: false, user_id: '' }); }}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: '500'
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="repairs"
        title="Tamirat Ürünleri"
      />

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmModalConfig.onCancel) {
                    confirmModalConfig.onCancel();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Repairs;
